import {
  HOSPITAL_BOOKING_RULES,
  getMinBookingDate,
  isBookingDateAllowed,
  getCrmDatePickerConstraints,
  isDateAllowedByCrmPickerConstraints,
} from "./bookingSlotRules";
import OtpVerifyFlow from "./_OtpVerify";

// Custom Event Listener
function pageListener() {
  this.events = {};
}

pageListener.prototype.on = function (eventType, listener) {
  // If the eventType Property not exist yet, create an empty aray of that property
  this.events[eventType] = this.events[eventType] || [];
  this.events[eventType].push(listener);
};

pageListener.prototype.emit = function (eventType, param) {
  if (this.events[eventType] && this.events[eventType].length > 0) {
    // Loop through the events[eventType] array of function and invoke each of them
    this.events[eventType].forEach(function (item) {
      item(param);
    });
  }
};

// Form validation regex patterns
const validationRegex = {
  // Name validation - Allow Vietnamese characters, spaces, no numbers or special chars
  nameRegex: /^[A-Za-zÀ-ỹ\s._-]+$/,

  // Email validation - Basic email format
  emailRegex: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/,

  // Phone validation - Allow 10-13 digits with optional + prefix
  phoneRegex: /^\d{10}$/,

  // Address validation - Vietnamese text, max 70 chars, max 5 special chars
  addressRegex: /^[A-Za-z0-9À-ỹ\s.,/_-]{0,200}$/,

  // CCCD validation - exactly 9 digits (CMND) or 12 digits (CCCD)
  cccdRegex: /^(\d{9}|\d{12})$/,
};

/** Digits only from a phone/hotline segment. */
function extractPhoneDigits(phone) {
  return String(phone || "").replace(/\D+/g, "");
}

/** >= 10 significant digits (leading country 84 ignored when counting). */
function shouldFormatPhoneIntl(digits) {
  let significant = String(digits || "");
  if (!significant) return false;
  if (significant.startsWith("84") && significant.length > 10) {
    significant = significant.slice(2);
  }
  return significant.length >= 10;
}

/** National digits without leading 0 / 84. */
function normalizePhoneNational(digits) {
  let value = String(digits || "");
  if (value.startsWith("84")) value = value.slice(2);
  if (value.startsWith("0")) value = value.slice(1);
  return value;
}

/** Space-group national number for +84 display. */
function groupPhoneNational(national) {
  const value = String(national || "");
  const len = value.length;
  if (!len) return "";

  let match = value.match(/^(2[48])(\d{4})(\d+)$/);
  if (len >= 9 && match) {
    return `${match[1]} ${match[2]} ${match[3]}`;
  }

  match = value.match(/^(2\d)(\d{4})(\d+)$/);
  if (len >= 9 && match) {
    return `${match[1]} ${match[2]} ${match[3]}`;
  }

  if (len === 9) {
    return `${value.slice(0, 3)} ${value.slice(3, 6)} ${value.slice(6)}`;
  }

  if (value.startsWith("1900") && len > 4) {
    return `1900 ${value.slice(4)}`;
  }

  if (len > 6) {
    return `${value.slice(0, 3)} ${value.slice(3, 6)} ${value.slice(6)}`;
  }

  return value;
}

function formatPhoneDisplaySegment(segment) {
  const original = String(segment || "").trim();
  if (!original) return "";

  if (/^\+\s*(?!84)\d/.test(original)) {
    return original;
  }

  const digits = extractPhoneDigits(original);
  if (!digits || !shouldFormatPhoneIntl(digits)) {
    return original;
  }

  const national = normalizePhoneNational(digits);
  if (!national) return original;

  return `+84 ${groupPhoneNational(national)}`;
}

function formatPhoneTelSegment(segment) {
  const original = String(segment || "").trim();
  if (!original) return "";

  if (/^\+\s*(?!84)\d/.test(original)) {
    return `+${extractPhoneDigits(original)}`;
  }

  const digits = extractPhoneDigits(original);
  if (!digits) return "";

  if (!shouldFormatPhoneIntl(digits)) {
    return digits;
  }

  const national = normalizePhoneNational(digits);
  return national ? `+84${national}` : digits;
}

/** Frontend hotline display (+84 when eligible). Supports "|" multi-numbers. */
function formatPhoneDisplay(phone) {
  const value = String(phone || "").trim();
  if (!value) return "";

  return value
    .split(/\s*\|\s*/)
    .map(formatPhoneDisplaySegment)
    .filter(Boolean)
    .join(" | ");
}

/** Frontend tel: href (first number if "|"). */
function formatPhoneTel(phone) {
  const value = String(phone || "").trim();
  if (!value) return "";
  const first = value.split(/\s*\|\s*/)[0] || "";
  return formatPhoneTelSegment(first);
}

const DAY_CODES = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const getAllSpecialtyOptionLabel = () => {
  if (typeof HM_Variables !== "undefined") {
    const lang = (HM_Variables.language || "").toLowerCase();
    if (lang === "en") {
      return "Optional";
    }
  }
  return "Tùy chọn";
};

const buildHisSpecialtyOptions = (specialties) => {
  const options = [
    `<li class="option" data-value="">${getAllSpecialtyOptionLabel()}</li>`,
  ];

  if (Array.isArray(specialties)) {
    const hisSpecialties = specialties.filter(
      (specialty) => specialty.is_his === 1 || specialty.is_his === true,
    );
    options.push(
      ...hisSpecialties.map(
        (specialty) =>
          `<li class="option" data-value="${specialty.id}">${specialty.title}</li>`,
      ),
    );
  }

  return options.join("");
};

const buildPackageOptions = (packages) => {
  const examineBySpecialtyLabel =
    HM_Variables?.frontend_lang?.select_placeholders?.examine_by_specialty ||
    "Khám theo chuyên khoa";
  const packageList = Array.isArray(packages) ? packages : [];

  if (!packageList.length) {
    return {
      html: '<li class="label">Gói khám đang trong quá trình cập nhật</li>',
      hasPackages: false,
    };
  }

  return {
    html: [
      `<li class="option" data-value="-2">${examineBySpecialtyLabel}</li>`,
      ...packageList.map(
        (packageItem) =>
          `<li class="option" data-value="${packageItem.id}">${packageItem.title}</li>`,
      ),
    ].join(""),
    hasPackages: true,
  };
};

const resolveDate = (input) => {
  if (!input) {
    return new Date();
  }

  if (input instanceof Date) {
    return new Date(input.getTime());
  }

  const parsed = new Date(input);
  if (Number.isNaN(parsed.getTime())) {
    return new Date();
  }

  return parsed;
};

const getCurrentTimeSlotDescriptor = (inputDate) => {
  const date = resolveDate(inputDate);

  const day = DAY_CODES[date.getDay()];
  const totalMinutes = date.getHours() * 60 + date.getMinutes();
  const flooredMinutes = Math.floor(totalMinutes / 15) * 15;
  const slotHour = Math.floor(flooredMinutes / 60);
  const slotMinute = flooredMinutes % 60;

  const timeDescriptor = `${String(slotHour).padStart(2, "0")}${String(
    slotMinute,
  ).padStart(2, "0")}`;

  return `${day}-${timeDescriptor}`;
};

const generateTimeSlots = (startHour, startMinute, endHour, endMinute) => {
  const slots = [];
  let cursor = startHour * 60 + startMinute;
  const end = endHour * 60 + endMinute;

  while (cursor <= end) {
    const hour = Math.floor(cursor / 60);
    const minute = cursor % 60;
    slots.push(
      `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
    );
    cursor += 15;
  }

  return slots;
};

const formatDateForDisplay = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const parseDateInputValue = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") {
    return null;
  }

  const trimmed = dateStr.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.indexOf("/") !== -1) {
    const parts = trimmed.split(" ")[0].split("/");
    if (parts.length >= 3) {
      const [d, m, y] = parts.map((part) => parseInt(part, 10));
      if (Number.isInteger(d) && Number.isInteger(m) && Number.isInteger(y)) {
        return new Date(y, m - 1, d);
      }
    }
  }

  if (trimmed.indexOf("-") !== -1) {
    const parts = trimmed.split(" ")[0].split("-");
    if (parts.length >= 3) {
      const [y, m, d] = parts.map((part) => parseInt(part, 10));
      if (Number.isInteger(d) && Number.isInteger(m) && Number.isInteger(y)) {
        return new Date(y, m - 1, d);
      }
    }
  }

  return null;
};

const isDateTimeInPast = (value) => {
  if (!value || typeof value !== "string") {
    return false;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }

  return parsed.getTime() < Date.now();
};

const applyUtmData = () => {
  try {
    const storedData = localStorage.getItem("hm_utm_data");

    if (storedData) {
      const utmData = JSON.parse(storedData);

      const now = Date.now();
      const validRange = 7 * 24 * 60 * 60 * 1000;
      const ageInMs = now - utmData.timestamp;

      if (ageInMs <= validRange) {
        const utmFields = {
          utm_source: utmData.utm_source || "",
          utm_campaign: utmData.utm_campaign || "",
          utm_medium: utmData.utm_medium || "",
          utm_term: utmData.utm_term || "",
          utm_content: utmData.utm_content || "",
          utm_adgroup: utmData.utm_adgroup || "",
          utm_ad: utmData.utm_ad || "",
        };

        // Fill các field UTM với data từ localStorage
        Object.keys(utmFields).forEach((fieldName) => {
          const field = document.getElementById(fieldName);
          if (field && utmFields[fieldName]) {
            field.value = utmFields[fieldName];
          }
        });
      } else {
        localStorage.removeItem("hm_utm_data");
      }
    }
  } catch (error) {
    console.warn("Error getting UTM data:", error);
  }
};

const createBookingDateTimeManager = () => {
  const $modal = $("#booking-datetime-modal");
  const modalBodyClass = "show-modal";
  const DEFAULT_DAY_SLOT_SESSIONS = {
    morning: generateTimeSlots(7, 0, 11, 30),
    afternoon: generateTimeSlots(12, 0, 16, 0),
  };
  const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  let activeInput = null;
  let currentHospitalKey = null;
  let onRelationLoadingChange = null;

  if (!$modal.length) {
    return {
      open: () => {},
      setHospital: () => {},
      setRelationLoadingListener: () => {},
    };
  }

  const $selectedHospitalNameWrapper = $modal
    .find(".booking-datetime-modal__header .desc")
    .first();
  const $selectedHospitalName = $selectedHospitalNameWrapper.find(
    ".selected-hospital-name",
  );
  const $slotSpecialtyList = $modal.find("#slot-specialty-select-list");
  const $slotSpecialtyInput = $modal.find(
    'input[name="slot-booking-specialty"]',
  );
  const $slotPackageList = $modal.find("#slot-packages-select-list");
  const $slotPackageInput = $modal.find('input[name="slot-booking-package"]');
  const $relationLoadingOverlay = $modal.find(".booking-appointment-loading-overlay");

  const timeStringToMinutes = (time) => {
    const [hourStr = "0", minuteStr = "0"] = String(time).split(":");
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return 0;
    }

    return hour * 60 + minute;
  };

  const cloneSlotSessions = (sessions) => ({
    morning: Array.isArray(sessions?.morning) ? [...sessions.morning] : [],
    afternoon: Array.isArray(sessions?.afternoon)
      ? [...sessions.afternoon]
      : [],
  });

  const partitionSlotsByNoon = (slots) => {
    const morning = [];
    const afternoon = [];
    const NOON_MINUTES = 12 * 60;

    slots.forEach((time) => {
      if (timeStringToMinutes(time) < NOON_MINUTES) {
        morning.push(time);
      } else {
        afternoon.push(time);
      }
    });

    return { morning, afternoon };
  };

  const normalizeDaySessions = (definition) => {
    if (
      definition &&
      typeof definition === "object" &&
      (Array.isArray(definition.morning) || Array.isArray(definition.afternoon))
    ) {
      return cloneSlotSessions(definition);
    }

    if (Array.isArray(definition)) {
      return partitionSlotsByNoon(definition);
    }

    return cloneSlotSessions(DEFAULT_DAY_SLOT_SESSIONS);
  };

  const flattenSessionSlots = (sessions) => [
    ...(Array.isArray(sessions?.morning) ? sessions.morning : []),
    ...(Array.isArray(sessions?.afternoon) ? sessions.afternoon : []),
  ];

  const normalizeTimeString = (value) => {
    if (typeof value !== "string") {
      return null;
    }
    const trimmed = value.trim();
    if (!trimmed.length) {
      return null;
    }
    const parts = trimmed.split(":");
    if (parts.length !== 2) {
      return null;
    }
    const hour = Number(parts[0]);
    const minute = Number(parts[1]);
    if (
      Number.isNaN(hour) ||
      Number.isNaN(minute) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      return null;
    }
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  };

  const getExcludedSlotSet = (hospitalKey) => {
    if (!hospitalKey || !HOSPITAL_BOOKING_RULES[hospitalKey]) {
      return new Set();
    }
    const config = HOSPITAL_BOOKING_RULES[hospitalKey];
    const excluded =
      config && config.settings && Array.isArray(config.settings.excludedSlots)
        ? config.settings.excludedSlots
        : [];

    return new Set(
      excluded
        .map((time) => normalizeTimeString(String(time)))
        .filter((time) => typeof time === "string" && time.length),
    );
  };

  const findHospitalByKey = (key) => {
    if (
      typeof HM_Variables === "undefined" ||
      !Array.isArray(HM_Variables.hospitals)
    ) {
      return null;
    }

    if (!key) {
      return null;
    }

    const normalized = String(key).trim().toLowerCase();
    if (!normalized.length) {
      return null;
    }

    return (
      HM_Variables.hospitals.find((hospital) => {
        const shortName = (hospital.hospital_shortname || "").toLowerCase();
        const hisId = String(hospital.hospital_id || "").toLowerCase();
        return (
          shortName === normalized ||
          hisId === normalized ||
          String(hospital.id) === normalized
        );
      }) || null
    );
  };

  const updateSelectedHospitalName = (hospital) => {
    if (!$selectedHospitalNameWrapper.length) {
      return;
    }

    const name = hospital && hospital.title ? String(hospital.title) : "";

    if (name.length) {
      $selectedHospitalName.text(name);
      $selectedHospitalNameWrapper.show();
    } else {
      $selectedHospitalName.text("");
      $selectedHospitalNameWrapper.hide();
    }
  };

  const setRelationLoadingState = (isLoading) => {
    if ($relationLoadingOverlay.length) {
      $relationLoadingOverlay
        .toggleClass("is-hidden", !isLoading)
        .attr("aria-hidden", isLoading ? "false" : "true");
    }
    if (typeof onRelationLoadingChange === "function") {
      onRelationLoadingChange(Boolean(isLoading));
    }
  };

  const getAllSpecialtyLabel = () => {
    return getAllSpecialtyOptionLabel();
  };

  const buildSlotSpecialtyOptions = (specialties) => {
    return buildHisSpecialtyOptions(specialties);
  };

  const buildSlotPackageOptions = (hospitalData) => {
    return buildPackageOptions(hospitalData?.packages);
  };

  const setSlotRelationSelectsEnabled = ({ specialtyEnabled, packageEnabled }) => {
    const $specialtySelect = $slotSpecialtyList.closest(".custom-select");
    const $packageSelect = $slotPackageList.closest(".custom-select");

    $specialtySelect.toggleClass("is-disabled", !specialtyEnabled);
    $packageSelect.toggleClass("is-disabled", !packageEnabled);
  };

  const setCustomSelectValue = ($list, $input, value, fallbackText = "") => {
    const $select = $list.closest(".custom-select");
    const $current = $select.find(".current-value");
    const normalized = value === undefined || value === null ? "" : String(value);
    const $target = $list.find(`.option[data-value="${normalized}"]`).first();

    $list.find(".option").removeClass("active");
    if ($target.length) {
      if (normalized === "" && fallbackText) {
        $input.val("");
        $current.text(fallbackText).addClass("default-value");
        return true;
      }

      $target.addClass("active");
      $input.val(normalized);
      $current.text($target.text().trim()).removeClass("default-value");
      return true;
    }

    $input.val("");
    $current.text(fallbackText).addClass("default-value");
    return false;
  };

  const getUrlBookingPreselection = ($form) => {
    let specialty = "";
    let pkg = "";

    if ($form && $form.length) {
      specialty = $form.find('input[name="booking-specialty"]').val() || "";
      pkg = $form.find('input[name="booking-package"]').val() || "";
    }

    if (!specialty && typeof packageBookingFormVars !== "undefined") {
      specialty = packageBookingFormVars.book_specialty || "";
    }
    if (!pkg && typeof packageBookingFormVars !== "undefined") {
      pkg = packageBookingFormVars.book_package || "";
    }
    if (!specialty && typeof bookingFormVars !== "undefined") {
      specialty = bookingFormVars.book_specialty || "";
    }
    if (!pkg && typeof bookingFormVars !== "undefined") {
      pkg = bookingFormVars.book_package || "";
    }

    return {
      specialty: String(specialty || ""),
      packageId: String(pkg || ""),
    };
  };

  const getPreselectedLegacyRelations = () => {
    const $form =
      activeInput && activeInput.length ? activeInput.closest("form") : null;
    return getUrlBookingPreselection($form);
  };

  const loadSlotRelationOptions = (hospitalData) => {
    const $specialtySelect = $slotSpecialtyList.closest(".custom-select");
    const $packageSelect = $slotPackageList.closest(".custom-select");
    const specialtyPlaceholder =
      $specialtySelect.find(".current-value").data("placeholder") ||
      (HM_Variables?.frontend_lang?.select_placeholders?.choose_specialty ??
        "Chọn chuyên khoa");
    const packagePlaceholder =
      $packageSelect.find(".current-value").data("placeholder") ||
      (HM_Variables?.frontend_lang?.select_placeholders?.choose_package ??
        "Chọn gói khám");
    const noPackagePlaceholder =
      HM_Variables?.frontend_lang?.select_placeholders?.no_packages_available ??
      "Không có gói khám khả dụng";

    return Promise.resolve().then(() => {
      $slotSpecialtyList.html(buildSlotSpecialtyOptions(hospitalData?.specialties));
      const packageData = buildSlotPackageOptions(hospitalData);
      $slotPackageList.html(packageData.html);

      const preselected = getPreselectedLegacyRelations();
      setCustomSelectValue(
        $slotSpecialtyList,
        $slotSpecialtyInput,
        preselected.specialty,
        specialtyPlaceholder,
      );

      const packageFallback = packageData.hasPackages
        ? packagePlaceholder
        : noPackagePlaceholder;
      setCustomSelectValue(
        $slotPackageList,
        $slotPackageInput,
        preselected.packageId,
        packageFallback,
      );

      setSlotRelationSelectsEnabled({
        specialtyEnabled: true,
        packageEnabled: packageData.hasPackages,
      });
    });
  };

  const syncSlotSelectionsFromForm = () => {
    if (!activeInput || !activeInput.length) {
      return;
    }
    const $form = activeInput.closest("form");
    if (!$form.length) {
      return;
    }

    const preselected = getUrlBookingPreselection($form);
    const $specialtySelect = $slotSpecialtyList.closest(".custom-select");
    const $packageSelect = $slotPackageList.closest(".custom-select");
    const specialtyPlaceholder =
      $specialtySelect.find(".current-value").data("placeholder") ||
      (HM_Variables?.frontend_lang?.select_placeholders?.choose_specialty ??
        "Chọn chuyên khoa");
    const packagePlaceholder =
      $packageSelect.find(".current-value").data("placeholder") ||
      (HM_Variables?.frontend_lang?.select_placeholders?.choose_package ??
        "Chọn gói khám");
    setCustomSelectValue(
      $slotSpecialtyList,
      $slotSpecialtyInput,
      preselected.specialty,
      specialtyPlaceholder,
    );
    setCustomSelectValue(
      $slotPackageList,
      $slotPackageInput,
      preselected.packageId,
      packagePlaceholder,
    );
  };

  const isPackageBookingForm = ($form) => {
    return $form && $form.length && $form.attr("id") === "the-package-booking-form";
  };

  const getRelationLabelFromForm = ($form, listSelector, inputName) => {
    const value = $form.find(`input[name="${inputName}"]`).val() || "";
    const $list = $form.find(listSelector);
    const $option = $list.find(`.option[data-value="${value}"]`).first();

    if ($option.length) {
      return $option.text().trim();
    }

    return "";
  };

  updateSelectedHospitalName(null);

  // Convert booking condition time (e.g. 1630) into minutes to simplify comparisons.
  const parseConditionTimeToMinutes = (value) => {
    const normalized = String(value || "0").padStart(4, "0");
    const hour = parseInt(normalized.slice(0, 2), 10);
    const minute = parseInt(normalized.slice(2), 10);

    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return 0;
    }

    return hour * 60 + minute;
  };

  // Reduce the incoming slot list using bookingCondition rules defined per hospital.
  // Note: caller ensures these rules only run against the next-day schedule.
  const applyBookingConditions = (
    slots,
    dayKey,
    currentMinutes,
    bookingConditions,
    daySessions,
    renderMeta = {},
    effectContext = {},
  ) => {
    if (!Array.isArray(bookingConditions) || !bookingConditions.length) {
      return slots;
    }

    const MINUTES_IN_DAY = 24 * 60;
    let currentSlots = slots;
    const { isSameDayFirstRender = false, shouldSuppressNextMorning = false } =
      renderMeta || {};
    const markSuppressNextMorning = () => {
      if (
        effectContext &&
        typeof effectContext === "object" &&
        !effectContext.suppressNextMorning &&
        shouldSuppressNextMorning
      ) {
        effectContext.suppressNextMorning = true;
      }
    };

    for (const condition of bookingConditions) {
      if (
        !condition ||
        !Array.isArray(condition.days) ||
        !condition.days.includes(dayKey)
      ) {
        continue;
      }

      const threshold = parseConditionTimeToMinutes(condition.time);
      const shouldApply = threshold === 0 || currentMinutes >= threshold;

      if (!shouldApply) {
        continue;
      }

      const distance = Number(condition.distance);

      if (distance === -2) {
        return [];
      }

      if (distance === -1) {
        if (isSameDayFirstRender) {
          markSuppressNextMorning();
          return [];
        }

        const afternoonSlots =
          daySessions &&
          Array.isArray(daySessions.afternoon) &&
          daySessions.afternoon.length
            ? daySessions.afternoon
            : null;

        const afternoonThreshold = afternoonSlots
          ? timeStringToMinutes(afternoonSlots[0])
          : 12 * 60;

        markSuppressNextMorning();

        return currentSlots.filter(
          (time) => timeStringToMinutes(time) >= afternoonThreshold,
        );
      }

      if (!Number.isNaN(distance) && distance > 0) {
        // If distance >= 24 hours and this is same day, block all slots for today
        if (distance >= 24 && isSameDayFirstRender) {
          markSuppressNextMorning();
          return [];
        }

        return currentSlots.filter((time) => {
          const slotMinutes = timeStringToMinutes(time);

          if (distance >= 24) {
            // For distance >= 24 hours, slots are for next day
            // Calculate the minimum time (current time + distance) in next day context
            // Example: current 10:53 (653 min) + 24h (1440 min) = 2093 min (10:53 next day)
            // Slot 11:00 (660 min) normalized = 660 + 1440 = 2100 min
            // 2100 >= 2093 → valid (at least 24 hours away)
            const minMinutesNextDay = currentMinutes + distance * 60;
            // Normalize slot to next day (add full day)
            const normalizedSlotMinutes = slotMinutes + MINUTES_IN_DAY;
            // Slot is valid if it's at least 24 hours from now
            // We use >= to include slots that are exactly 24 hours or more away
            // So if current is 10:53, we want slots >= 10:53 next day, which includes 11:00
            return normalizedSlotMinutes >= minMinutesNextDay;
          } else {
            // For distances < 24 hours, calculate normally
            const minMinutes = currentMinutes + distance * 60;
            // Only normalize if slot time is earlier than current time (wrapping to next day)
            const normalizedSlotMinutes =
              slotMinutes < currentMinutes
                ? slotMinutes + MINUTES_IN_DAY
                : slotMinutes;
            return normalizedSlotMinutes >= minMinutes;
          }
        });
      }

      return currentSlots;
    }

    return currentSlots;
  };

  // Generate date data for 30 days
  const generateDateData = () => {
    const locale =
      window.HM_Variables && window.HM_Variables.language === "en"
        ? "en-US"
        : "vi-VN";
    const dayFormatter = new Intl.DateTimeFormat(locale, {
      weekday: "short",
    });

    // Custom formatter to ensure DD/MM format
    const formatDateShort = (date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      return `${day}/${month}`;
    };

    // Format date as ISO string without timezone conversion
    const formatISODate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const getNextCalendarWeekday = (ref, weekdayKey) => {
      const targetIndex = DAY_KEYS.indexOf(weekdayKey);
      if (targetIndex < 0) {
        return null;
      }
      const d = new Date(ref);
      d.setHours(0, 0, 0, 0);
      const todayDow = d.getDay();
      let daysToAdd = (targetIndex - todayDow + 7) % 7;
      if (daysToAdd === 0) {
        daysToAdd = 7;
      }
      d.setDate(d.getDate() + daysToAdd);
      return d;
    };

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const todayDayKey = DAY_KEYS[now.getDay()];

    const startDate = new Date(now);
    startDate.setHours(0, 0, 0, 0);

    const hospitalConfig =
      currentHospitalKey && HOSPITAL_BOOKING_RULES[currentHospitalKey]
        ? HOSPITAL_BOOKING_RULES[currentHospitalKey]
        : null;
    const hospitalSettings = hospitalConfig?.settings || {};
    const allowSameDay =
      typeof hospitalSettings.allowSameDay === "boolean"
        ? hospitalSettings.allowSameDay
        : true;
    const bookingConditions = Array.isArray(hospitalSettings.bookingCondition)
      ? hospitalSettings.bookingCondition
      : [];

    const rawDayLimit = hospitalSettings.maxSelectableBookingDays;
    const bookingDayPickerLimit =
      typeof rawDayLimit === "number" &&
      Number.isFinite(rawDayLimit) &&
      rawDayLimit > 0
        ? Math.floor(rawDayLimit)
        : null;

    const startOffset = allowSameDay ? 0 : 1;
    const minBookingDate = getMinBookingDate(currentHospitalKey, now);
    startDate.setTime(minBookingDate.getTime());
    startDate.setHours(0, 0, 0, 0);

    const schedule =
      currentHospitalKey &&
      HOSPITAL_BOOKING_RULES[currentHospitalKey] &&
      HOSPITAL_BOOKING_RULES[currentHospitalKey].slots
        ? HOSPITAL_BOOKING_RULES[currentHospitalKey].slots
        : null;

    const weekdayMorningSuppress = hospitalSettings.suppressWeekdayMorningAfter;
    let suppressedWeekdayMorningIso = null;
    let suppressedWeekdayMorningDayKey = null;
    if (
      weekdayMorningSuppress &&
      typeof weekdayMorningSuppress.suppressDay === "string"
    ) {
      const suppressDayKey = weekdayMorningSuppress.suppressDay.toLowerCase();
      if (
        DAY_KEYS.includes(suppressDayKey) &&
        Array.isArray(weekdayMorningSuppress.days) &&
        weekdayMorningSuppress.days.includes(todayDayKey)
      ) {
        const suppressCutoff = parseConditionTimeToMinutes(
          weekdayMorningSuppress.time ?? 0,
        );
        if (suppressCutoff === 0 || currentMinutes >= suppressCutoff) {
          const targetDate = getNextCalendarWeekday(now, suppressDayKey);
          if (targetDate) {
            suppressedWeekdayMorningIso = formatISODate(targetDate);
            suppressedWeekdayMorningDayKey = suppressDayKey;
          }
        }
      }
    }

    const dateData = [];
    let suppressNextMorning = false;
    // If same-day is not al lowed, the first selectable day is "tomorrow" (i===0). We still need to
    // evaluate today's booking conditions for side-effects (e.g., suppress next morning) using the
    // same rules engine, even though today's slots won't be rendered.
    if (
      !allowSameDay &&
      Array.isArray(bookingConditions) &&
      bookingConditions.length
    ) {
      const todayDefinition =
        schedule && Object.prototype.hasOwnProperty.call(schedule, todayDayKey)
          ? schedule[todayDayKey]
          : null;
      const todaySessions = normalizeDaySessions(todayDefinition);
      const todaySlots = flattenSessionSlots(todaySessions);
      const conditionContext = { suppressNextMorning: false };

      // Run the rules engine in "same-day first render" mode so it can emit suppression flags.
      applyBookingConditions(
        todaySlots,
        todayDayKey,
        currentMinutes,
        bookingConditions,
        todaySessions,
        { isSameDayFirstRender: true, shouldSuppressNextMorning: true },
        conditionContext,
      );

      if (conditionContext.suppressNextMorning) {
        suppressNextMorning = true;
      }
    }

    for (let i = 0; i < 30; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      const isoDate = formatISODate(date);
      const weekday = dayFormatter.format(date);
      const dateDisplay = formatDateShort(date);
      const fullDateDisplay = formatDateForDisplay(date);
      const isFirstDay = i === 0;
      const dayKey = DAY_KEYS[date.getDay()];

      const baseDayDefinition =
        schedule && Object.prototype.hasOwnProperty.call(schedule, dayKey)
          ? schedule[dayKey]
          : null;

      const daySessions = normalizeDaySessions(baseDayDefinition);

      if (
        suppressedWeekdayMorningIso &&
        suppressedWeekdayMorningDayKey &&
        isoDate === suppressedWeekdayMorningIso &&
        dayKey === suppressedWeekdayMorningDayKey
      ) {
        daySessions.morning = [];
      }

      const suppressIndex = allowSameDay ? 1 : 0;
      if (suppressNextMorning && i === suppressIndex) {
        daySessions.morning = [];
        suppressNextMorning = false;
      }

      let slots = flattenSessionSlots(daySessions);

      const isSameDay = allowSameDay && isFirstDay && startOffset === 0;

      // Check if there's a condition with distance >= 24 hours that applies to this day
      const hasLongDistanceCondition = bookingConditions.some(
        (condition) =>
          condition &&
          Array.isArray(condition.days) &&
          condition.days.includes(dayKey) &&
          Number(condition.distance) >= 24,
      );

      // If there's a distance >= 24 condition and this is same day, block ALL slots for today
      // This must happen before any other filtering to ensure no slots are shown for today
      if (isSameDay) {
        // Check if any condition with distance >= 24 applies to today
        const hasLongDistanceForToday = bookingConditions.some(
          (condition) =>
            condition &&
            Array.isArray(condition.days) &&
            condition.days.includes(dayKey) &&
            Number(condition.distance) >= 24,
        );

        if (hasLongDistanceForToday) {
          // Block all slots for today when distance >= 24
          slots = [];
        } else if (slots.length) {
          // Only filter same-day slots if there's no distance >= 24 condition
          slots = slots.filter(
            (time) => timeStringToMinutes(time) >= currentMinutes,
          );
        }
      }

      // Apply booking condition to first day (today if allowSameDay, or tomorrow if not)
      // Also apply to second day (tomorrow) if there's a distance >= 24 condition
      // But skip if slots were already blocked due to distance >= 24 on same day
      const shouldApplyBookingCondition =
        (i === 0 || (hasLongDistanceCondition && i === 1 && allowSameDay)) &&
        !(
          isSameDay &&
          bookingConditions.some(
            (condition) =>
              condition &&
              Array.isArray(condition.days) &&
              condition.days.includes(dayKey) &&
              Number(condition.distance) >= 24,
          )
        );

      if (shouldApplyBookingCondition) {
        const conditionContext = { suppressNextMorning: false };
        const isNextDay = i === 1 && allowSameDay;
        slots = applyBookingConditions(
          slots,
          dayKey,
          currentMinutes,
          bookingConditions,
          daySessions,
          {
            isSameDayFirstRender: isSameDay && !isNextDay,
            shouldSuppressNextMorning: isSameDay && !isNextDay,
          },
          conditionContext,
        );
        if (conditionContext.suppressNextMorning) {
          suppressNextMorning = true;
        }
      }

      dateData.push({
        isoDate,
        weekday,
        dateDisplay,
        fullDateDisplay,
        dayKey,
        slots,
        hasSlots: slots.length > 0,
        outsideBookingDayWindow:
          bookingDayPickerLimit !== null && i >= bookingDayPickerLimit,
      });
    }

    return dateData;
  };

  // Update submit button state
  const updateSubmitButtonState = () => {
    const $submitBtn = $modal.find(".booking-datetime-submit");
    const $dateList = $modal.find(".date-list");
    const $timeList = $modal.find(".time-list");

    const hasActiveDate = $dateList.find(".date-item.active").length > 0;
    const hasActiveTime = $timeList.find(".time-option.active").length > 0;

    if (hasActiveDate && hasActiveTime) {
      $submitBtn.prop("disabled", false).removeClass("disabled");
    } else {
      $submitBtn.prop("disabled", true).addClass("disabled");
    }
  };

  // Render date picker (30 days)
  const renderDatePicker = (dateData) => {
    const $dateList = $modal.find(".date-list");
    if (!$dateList.length) {
      return;
    }

    const html = dateData
      .map((dateInfo) => {
        const pickerDisabled =
          !dateInfo.hasSlots || dateInfo.outsideBookingDayWindow;
        const disabledClass = pickerDisabled ? "disabled" : "";
        return `
          <div class="date-item ${disabledClass}" data-date="${
          dateInfo.isoDate
        }" data-has-slots="${dateInfo.hasSlots}" data-outside-booking-limit="${
          Boolean(dateInfo.outsideBookingDayWindow)
        }">
            <div class="date-item__weekday">${dateInfo.weekday}</div>
            <div class="date-item__date">${dateInfo.dateDisplay}</div>
          </div>
        `;
      })
      .join("");

    $dateList.html(html);

    // Add click handlers
    $dateList.find(".date-item").off("click.dateSelect");
    $dateList.find(".date-item").on("click.dateSelect", function () {
      const $dateItem = $(this);

      if ($dateItem.hasClass("disabled")) {
        return;
      }

      const selectedDate = $dateItem.data("date");

      // Update active state
      $dateList.find(".date-item.active").removeClass("active");
      $dateItem.addClass("active");

      // Render time slots for selected date
      renderTimeSlots(selectedDate, dateData);

      // Update button state
      updateSubmitButtonState();

      // Scroll to time picker on mobile (only on user click, not preselection)
      if (window.innerWidth <= 768) {
        setTimeout(() => {
          const $timePicker = $modal.find(".booking-time-picker");
          const $scrollContainer = $modal.find(".booking-date-time-wrapper");

          if ($timePicker.length && $scrollContainer.length) {
            const scrollTop =
              $timePicker.offset().top -
              $scrollContainer.offset().top +
              $scrollContainer.scrollTop() -
              20;
            $scrollContainer.animate(
              { scrollTop: Math.max(0, scrollTop) },
              300,
            );
          }
        }, 100);
      }
    });
  };

  // Render time slots for selected date
  const renderTimeSlots = (selectedDate, dateData) => {
    const $timeList = $modal.find(".time-list");
    const $noDateSelected = $modal.find(".no-date-selected");

    if (!$timeList.length) {
      return;
    }

    const dateInfo = dateData.find((d) => d.isoDate === selectedDate);

    if (!dateInfo) {
      return;
    }

    // Hide "no date selected" message
    $noDateSelected.hide();

    const locale =
      window.HM_Variables && window.HM_Variables.language === "en"
        ? "en-US"
        : "vi-VN";

    if (!dateInfo.hasSlots || dateInfo.slots.length === 0) {
      const emptyMessage =
        locale === "en-US"
          ? "No available slots for this date"
          : "Hiện tại đã hết khung giờ trống trong ngày. Quý khách vui lòng chọn một ngày khác để được phục vụ";

      $timeList.html(`<li class="time-option is-empty">${emptyMessage}</li>`);
      return;
    }

    const excludedSlotSet = getExcludedSlotSet(currentHospitalKey);
    const visibleSlots = dateInfo.slots.filter((time) => {
      const normalizedTime = normalizeTimeString(time);
      return !(normalizedTime && excludedSlotSet.has(normalizedTime));
    });

    if (!visibleSlots.length) {
      const emptyMessage =
        locale === "en-US"
          ? "No available slots for this date"
          : "Hiện tại đã hết khung giờ trống trong ngày. Quý khách vui lòng chọn một ngày khác để được phục vụ";

      $timeList.html(`<li class="time-option is-empty">${emptyMessage}</li>`);
      return;
    }

    const html = visibleSlots
      .map((time) => {
        const displayValue = `${dateInfo.fullDateDisplay} ${time}`;
        return `
          <li class="time-option" data-date="${dateInfo.isoDate}" data-time="${displayValue}" data-slot="${time}">
            <span class="time-option__label">${time}</span>
          </li>
        `;
      })
      .join("");

    $timeList.html(html);

    // Add click handlers for time slots
    $timeList.find(".time-option").off("click.timeSelect");
    $timeList.find(".time-option").on("click.timeSelect", function () {
      const $option = $(this);
      if ($option.hasClass("is-empty") || $option.hasClass("disabled")) {
        return;
      }
      $timeList.find(".time-option.active").removeClass("active");
      $option.addClass("active");

      // Update button state
      updateSubmitButtonState();
    });
  };

  // Parse existing input value to get date and time
  const parseExistingValue = (inputValue) => {
    if (!inputValue || typeof inputValue !== "string") {
      return null;
    }

    // Expected format: "DD/MM/YYYY HH:MM"
    const trimmed = inputValue.trim();
    const parts = trimmed.split(" ");

    if (parts.length < 2) {
      return null;
    }

    const datePart = parts[0]; // DD/MM/YYYY
    const timePart = parts[1]; // HH:MM

    // Parse date
    const dateParts = datePart.split("/");
    if (dateParts.length !== 3) {
      return null;
    }

    const day = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10);
    const year = parseInt(dateParts[2], 10);

    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      return null;
    }

    // Create ISO date string (YYYY-MM-DD)
    const isoDate = `${year}-${String(month).padStart(2, "0")}-${String(
      day,
    ).padStart(2, "0")}`;

    return {
      isoDate,
      time: timePart,
      displayValue: trimmed,
    };
  };

  const renderSlots = (preselectedValue = null) => {
    const $dateList = $modal.find(".date-list");
    const $timeList = $modal.find(".time-list");
    const $noDateSelected = $modal.find(".no-date-selected");

    if (!$dateList.length) {
      return;
    }

    // Generate 30 days of date data
    const dateData = generateDateData();

    // Render date picker
    renderDatePicker(dateData);

    // Clear time slots initially
    $timeList.html("");
    $noDateSelected.show();

    // Disable submit button initially
    updateSubmitButtonState();

    // If there's a preselected value, restore it
    if (preselectedValue) {
      const parsed = parseExistingValue(preselectedValue);

      if (parsed && parsed.isoDate) {
        // Find and activate the date
        const $dateItem = $dateList.find(
          `.date-item[data-date="${parsed.isoDate}"]`,
        );

        if ($dateItem.length && !$dateItem.hasClass("disabled")) {
          $dateItem.addClass("active");

          // Render time slots for this date
          renderTimeSlots(parsed.isoDate, dateData);

          // Activate the time slot
          if (parsed.time) {
            setTimeout(() => {
              const $timeOptions = $timeList.find(".time-option");
              $timeOptions.each(function () {
                const $option = $(this);
                const optionTime = $option.data("time");

                if (optionTime && optionTime.includes(parsed.time)) {
                  $option.addClass("active");
                  return false; // break
                }
              });

              // Update button state after preselection
              updateSubmitButtonState();
            }, 50);
          }
        }
      }
    }
  };

  const closeModal = () => {
    $modal.removeClass("show-modal").attr("aria-hidden", "true");
    $modal.removeClass("is-package-context");
    $modal.find(".booking-appointment-filters").show();
    $modal.find(".modal-dialog").removeClass("active");

    if ($(".modal-wrapper.show-modal").not($modal).length === 0) {
      $("body").removeClass(modalBodyClass);
    }

    if (activeInput && activeInput.length && activeInput.is(":visible")) {
      activeInput.trigger("focus");
    }

    activeInput = null;
  };

  const openModal = ($input) => {
    activeInput = $input;
    const $form = activeInput && activeInput.length ? activeInput.closest("form") : null;
    const packageContext = isPackageBookingForm($form);

    // Apply brand UI class if hospital is already set
    if (currentHospitalKey) {
      const hospital = findHospitalByKey(currentHospitalKey);
      if (hospital) {
        updateModalBrandUI(hospital);
      }
    }

    // Prefer hidden booking-time value for stable re-open restore.
    let existingValue = $input && $input.length ? $input.val() : null;
    if ($input && $input.length) {
      const $form = $input.closest("form");
      const hiddenBookingTime = $form.find('input[name="booking-time"]').val();
      if (hiddenBookingTime) {
        existingValue = hiddenBookingTime;
      }
    }
    $modal
      .toggleClass("is-package-context", packageContext)
      .find(".booking-appointment-filters")
      .toggle(!packageContext);
    syncSlotSelectionsFromForm();
    renderSlots(existingValue);

    $("body").addClass(modalBodyClass);
    $modal.addClass("show-modal").attr("aria-hidden", "false");
    $modal.find(".modal-dialog").addClass("active");

    const $focusTarget = $modal.find("[data-focus-default]").first();
    if ($focusTarget.length) {
      $focusTarget.trigger("focus");
    }
  };

  $(document)
    .off("click.bookingDateTimeClose")
    .on("click.bookingDateTimeClose", ".booking-datetime-close", function (e) {
      e.preventDefault();
      closeModal();
    });

  $(document)
    .off("keydown.bookingDateTimeModal")
    .on("keydown.bookingDateTimeModal", function (event) {
      if (event.key === "Escape") {
        closeModal();
      }
    });

  $(document)
    .off("click.bookingDateTimeSubmit")
    .on(
      "click.bookingDateTimeSubmit",
      ".booking-datetime-submit",
      function (event) {
        event.preventDefault();

        if (!activeInput) {
          return;
        }

        const $activeOption = $modal.find(".time-option.active");
        if (!$activeOption.length) {
          return;
        }

        const displayValue = $activeOption.data("time");
        if (!displayValue) {
          return;
        }

        let finalDisplayValue = String(displayValue);

        if (activeInput.attr("name") !== "booking-time") {
          const $form = activeInput.closest("form");
          if ($form.length) {
            const packageContext = isPackageBookingForm($form);
            const $hiddenBookingTime = $form
              .find('input[name="booking-time"]')
              .first();
            if (
              $hiddenBookingTime.length &&
              $hiddenBookingTime[0] !== activeInput[0]
            ) {
              $hiddenBookingTime.val(displayValue).trigger("change");
            }
            const specialtyLabel =
              packageContext
                ? getRelationLabelFromForm(
                    $form,
                    "#specialties-select-list",
                    "booking-specialty",
                  )
                : $slotSpecialtyList.find(".option.active").text().trim() || "";
            const packageLabel =
              packageContext
                ? getRelationLabelFromForm(
                    $form,
                    "#packages-select-list",
                    "booking-package",
                  )
                : $slotPackageList.find(".option.active").text().trim() || "";
            const lastSpaceIndex = String(displayValue).lastIndexOf(" ");
            const selectedDate =
              lastSpaceIndex > -1
                ? String(displayValue).slice(0, lastSpaceIndex).trim()
                : String(displayValue).trim();
            const selectedTime =
              lastSpaceIndex > -1
                ? String(displayValue).slice(lastSpaceIndex + 1).trim()
                : "";
            const renderedBookingTime = [
              specialtyLabel,
              selectedDate,
              selectedTime,
              packageLabel,
            ]
              .filter(Boolean)
              .join(" - ");

            finalDisplayValue = renderedBookingTime || finalDisplayValue;

            if (!packageContext) {
              $form
                .find('input[name="booking-specialty"]')
                .val($slotSpecialtyInput.val() || "")
                .trigger("change");
              $form
                .find('input[name="booking-package"]')
                .val($slotPackageInput.val() || "")
                .trigger("change");
            }
          }
        }
        activeInput.val(finalDisplayValue);
        activeInput.trigger("change");
        closeModal();
      },
    );

  // Update modal brand UI class based on hospital
  const updateModalBrandUI = (hospitalData) => {
    // Remove all brand UI classes
    $modal.removeClass("hmg-ui hm-ui tm-ui hp-ui");

    if (!hospitalData) {
      return;
    }

    // Check for brand shortname or hospital shortname
    const brandShortName =
      hospitalData.brand_shortname ||
      hospitalData.hospital_brand_shortname ||
      hospitalData.hospital_shortname ||
      "";

    // Apply brand UI class if it matches HMG
    if (brandShortName.toLowerCase() === "hmg") {
      $modal.addClass("hmg-ui");
    }
    // Add other brands as needed
    else if (brandShortName.toLowerCase() === "tm") {
      $modal.addClass("tm-ui");
    } else if (brandShortName.toLowerCase() === "hp") {
      $modal.addClass("hp-ui");
    }
    // Default to HM UI (no class needed, default styling)
  };

  const setHospital = (key, hospitalData = null) => {
    const normalized =
      typeof key === "string" && key.trim().length > 0
        ? key.trim().toLowerCase()
        : null;

    if (currentHospitalKey === normalized) {
      const displayHospital = hospitalData || findHospitalByKey(normalized);
      updateSelectedHospitalName(displayHospital);
      updateModalBrandUI(displayHospital);
      if (displayHospital) {
        setRelationLoadingState(true);
        loadSlotRelationOptions(displayHospital).finally(() =>
          setRelationLoadingState(false),
        );
      } else {
        setRelationLoadingState(false);
        setSlotRelationSelectsEnabled({
          specialtyEnabled: false,
          packageEnabled: false,
        });
      }
      return;
    }

    currentHospitalKey = normalized;

    const displayHospital = hospitalData || findHospitalByKey(normalized);
    updateSelectedHospitalName(displayHospital);

    // Update modal brand UI class
    updateModalBrandUI(displayHospital);

    $slotSpecialtyList.html(
      `<li class="label">${
        HM_Variables?.frontend_lang?.select_hospital_first || "Vui lòng chọn bệnh viện trước"
      }</li>`,
    );
    $slotPackageList.html(
      `<li class="label">${
        HM_Variables?.frontend_lang?.select_hospital_first || "Vui lòng chọn bệnh viện trước"
      }</li>`,
    );
    $slotSpecialtyInput.val("");
    $slotPackageInput.val("");
    const $specialtySelect = $slotSpecialtyList.closest(".custom-select");
    const $packageSelect = $slotPackageList.closest(".custom-select");
    const specialtyPlaceholder =
      $specialtySelect.find(".current-value").data("placeholder") ||
      (HM_Variables?.frontend_lang?.select_placeholders?.choose_specialty ??
        "Chọn chuyên khoa");
    const packagePlaceholder =
      $packageSelect.find(".current-value").data("placeholder") ||
      (HM_Variables?.frontend_lang?.select_placeholders?.choose_package ??
        "Chọn gói khám");
    $specialtySelect
      .find(".current-value")
      .text(specialtyPlaceholder)
      .addClass("default-value");
    $packageSelect
      .find(".current-value")
      .text(packagePlaceholder)
      .addClass("default-value");
    setSlotRelationSelectsEnabled({
      specialtyEnabled: false,
      packageEnabled: false,
    });

    if (displayHospital) {
      setRelationLoadingState(true);
      loadSlotRelationOptions(displayHospital).finally(() =>
        setRelationLoadingState(false),
      );
    } else {
      setRelationLoadingState(false);
    }

    if ($modal.hasClass("show-modal")) {
      renderSlots(null);
    }
  };

  return {
    open: openModal,
    setHospital,
    setRelationLoadingListener: (listener) => {
      onRelationLoadingChange =
        typeof listener === "function" ? listener : null;
    },
  };
};

const BOOKING_ACTIONS = {
  GET_SPECIALTIES: "get_specialties",
  GET_CALENDAR: "get_calendar",
  GET_APPOINTMENT_TYPES: "get_appointment_types",
};

const bookingApi = (action, payload = {}) => {
  const root =
    typeof HM_Variables !== "undefined" ? HM_Variables.root_url || "" : "";
  const nonce =
    typeof HM_Variables !== "undefined" ? HM_Variables.rest_nonce || "" : "";
  return fetch(`${root}/wp-json/hmc/v1/booking`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-WP-Nonce": nonce,
    },
    body: JSON.stringify({ action, payload }),
  }).then((r) => (r.ok ? r.json() : Promise.reject(r)));
};

const createBookingDateTimeManagerCRM = () => {
  const $modal = $("#booking-datetime-modal-crm");
  const modalBodyClass = "show-modal";
  const lang =
    typeof HM_Variables !== "undefined" ? HM_Variables.language : "vi";
  let activeInput = null;
  let currentHospitalKey = null;
  let onSpecialtiesLoadingChange = null;
  let followUpBridge = null;
  let followUpOpenContext = null;
  let datePickerLocked = false;
  let currentDatePickerConstraints = getCrmDatePickerConstraints(null);

  if (!$modal.length) {
    return {
      open: () => {},
      setHospital: () => {},
      setSpecialtiesLoadingListener: () => {},
      setFollowUpBridge: () => {},
    };
  }

  const $selectedHospitalNameWrapper = $modal
    .find(".booking-datetime-modal__header .desc")
    .first();
  const $selectedHospitalName = $selectedHospitalNameWrapper.find(
    ".selected-hospital-name",
  );

  const $specialtyList = $modal.find("#crm-specialty-select-list");
  const $specialtyInput = $modal.find('input[name="crm-booking-specialty"]');
  const $dateInput = $modal.find('input[name="booking-datetime"]');
  const $doctorList = $modal.find("#crm-doctor-select-list");
  const $doctorInput = $modal.find('input[name="crm-booking-doctor"]');
  const $slotsLoadingOverlay = $modal.find(
    ".booking-appointment-slots-wrapper .booking-appointment-loading-overlay",
  );

  const applyCrmDatePickerConstraints = (hospitalKey) => {
    currentDatePickerConstraints = getCrmDatePickerConstraints(
      hospitalKey,
      new Date(),
    );

    if (datePickerLocked) {
      return;
    }

    if (!$dateInput.length || typeof $dateInput.datetimepicker !== "function") {
      return;
    }

    const pickerOptions = {
      format: "d/m/Y",
      timepicker: false,
      scrollInput: false,
      minDate: currentDatePickerConstraints.minDate,
      maxDate: currentDatePickerConstraints.maxDate,
      // Vendor setOptions only replaces these when length > 0; destroy+reinit
      // so switching hospitals clears prior Sunday/weekday locks.
      disabledWeekDays: currentDatePickerConstraints.disabledWeekDays || [],
      disabledDates: currentDatePickerConstraints.disabledDates || [],
    };

    if ($dateInput.data("xdsoft_datetimepicker")) {
      $dateInput.datetimepicker("destroy");
    }
    $dateInput
      .prop("readonly", true)
      .attr("aria-readonly", "true")
      .datetimepicker(pickerOptions);
  };

  const VIEW_ALL_DOCTOR_ID = "__view_all__";
  const getViewAllLabel = () =>
    (typeof HM_Variables !== "undefined" &&
      HM_Variables.frontend_lang?.booking_messages?.view_all) ||
    (lang === "en" ? "View all" : "Xem tất cả");

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return "";
    const trimmed = dateStr.trim();
    if (!trimmed) return "";
    if (trimmed.indexOf("-") !== -1) {
      const parts = trimmed.split(" ")[0].split("-");
      if (parts.length >= 3) {
        const [y, m, d] = parts;
        return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
      }
    }
    if (trimmed.indexOf("/") !== -1) return trimmed.split(" ")[0];
    return trimmed;
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return "";
    const trimmed = dateStr.trim();
    if (!trimmed) return "";

    if (trimmed.indexOf("/") !== -1) {
      return trimmed.split(" ")[0];
    }

    const isoPart = trimmed.split("T")[0].split(" ")[0];
    if (isoPart.indexOf("-") !== -1) {
      const parts = isoPart.split("-");
      if (parts.length >= 3) {
        const [y, m, d] = parts;
        return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
      }
    }
    return trimmed;
  };

  const getPastDateErrorMessage = () =>
    HM_Variables?.frontend_lang?.validation?.past_date_not_allowed ||
    (lang === "en"
      ? "Please choose today or a future date."
      : "Vui lòng chọn ngày khám là ngày hôm nay hoặc ngày tương lai.");

  const getDateNotAllowedErrorMessage = () =>
    HM_Variables?.frontend_lang?.validation?.booking_date_not_allowed ||
    (lang === "en"
      ? "Please choose a valid appointment date for this hospital."
      : "Vui lòng chọn ngày khám hợp lệ cho bệnh viện này.");

  const clearDateInputError = () => {
    const $dateGroup = $dateInput.closest(".input-group");
    $dateGroup.removeClass("err");
    $dateGroup.find(".err-msg").text("");
  };

  const showDateInputError = (message) => {
    const $dateGroup = $dateInput.closest(".input-group");
    $dateGroup.addClass("err");
    $dateGroup.find(".err-msg").text(message || "");
  };

  const isCrmBookingDateAllowed = (dateStr) => {
    const parsed = parseDateInputValue(dateStr);
    return (
      isBookingDateAllowed(currentHospitalKey, parsed) &&
      isDateAllowedByCrmPickerConstraints(
        parsed,
        currentDatePickerConstraints,
      )
    );
  };

  const showSlotsLoading = () => {
    $slotsLoadingOverlay.removeClass("is-hidden").attr("aria-hidden", "false");
  };
  const hideSlotsLoading = () => {
    $slotsLoadingOverlay.addClass("is-hidden").attr("aria-hidden", "true");
  };

  const $specialtySelect = $specialtyList.closest(".custom-select");
  const $dateGroup = $dateInput.closest(".input-group");
  const $specialtyGroup = $specialtySelect.closest(".input-group");
  const $doctorGroup = $doctorList.closest(".input-group");

  const updateCrmStepHighlight = () => {
    const dateVal = ($dateInput.val() || "").trim();
    const hasDate = !!dateVal && isCrmBookingDateAllowed(dateVal);
    const hasSpecialty = !!($specialtyInput.val() || "").trim();
    const hasDoctor = !!($doctorInput.val() || "").trim();

    $dateGroup.toggleClass("is-step-highlight", !hasDate);
    $specialtyGroup.toggleClass("is-step-highlight", hasDate && !hasSpecialty);
    $doctorGroup.toggleClass(
      "is-step-highlight",
      hasDate && hasSpecialty && !hasDoctor,
    );
  };

  const getSelectDateFirstMessage = () =>
    HM_Variables?.frontend_lang?.please_select_date_first ||
    (lang === "en" ? "Please select a date first" : "Vui lòng chọn ngày trước");

  const getSelectSpecialtyFirstMessage = () =>
    HM_Variables?.frontend_lang?.please_select_specialty_first ||
    (lang === "en"
      ? "Please select a specialty to view available slots"
      : "Vui lòng chọn chuyên khoa để xem lịch hẹn");

  const getSpecialtyDisabledPlaceholder = () =>
    $specialtySelect.find(".current-value").data("placeholder") ||
    HM_Variables?.frontend_lang?.select_placeholders?.choose_specialty ||
    (lang === "en" ? "Choose Specialty" : "Chọn chuyên khoa");

  const getSpecialtyEnabledPlaceholder = () => {
    const fromData = $specialtySelect
      .find(".current-value")
      .data("placeholderEnabled");
    if (fromData) {
      return fromData;
    }
    return (
      HM_Variables?.frontend_lang?.select_placeholders?.choose_specialty ||
      (lang === "en" ? "Choose Specialty" : "Chọn chuyên khoa")
    );
  };

  const getNoSlotsMessage = () =>
    HM_Variables?.frontend_lang?.booking_messages?.no_slots_available ||
    (lang === "en" ? "No slots available" : "Hiện tại đã hết khung giờ trống trong ngày. Quý khách vui lòng chọn một ngày khác để được phục vụ");

  const hasSlotSearchCriteria = () =>
    !!$specialtyInput.val() && !!($dateInput.val() || "").trim();

  const getCalendarRequestKey = () => {
    const benhvienId = getBenhvienId();
    const ngaykham = ($dateInput.val() || "").trim();
    if (!benhvienId || !ngaykham) {
      return null;
    }
    return `${benhvienId}|${ngaykham}`;
  };

  const isCalendarEmptyForSelectedDate = () => {
    const requestKey = getCalendarRequestKey();
    return (
      !!requestKey &&
      calendarFetchedForKey === requestKey &&
      calendarCache.length === 0
    );
  };

  const getSlotsStatusMessage = () => {
    const hasDate = !!($dateInput.val() || "").trim();
    if (!hasDate) {
      return getSelectDateFirstMessage();
    }
    if (isCalendarEmptyForSelectedDate()) {
      return getNoSlotsMessage();
    }
    if (!$specialtyInput.val()) {
      return getSelectSpecialtyFirstMessage();
    }
    return getSelectSpecialtyFirstMessage();
  };

  const showEmptySlotsMessage = () => {
    $modal
      .find(".booking-appointment-slots")
      .html(`<p class="slots-status-message">${getNoSlotsMessage()}</p>`)
      .attr("data-status", "empty");
  };

  const showSlotsStatusMessage = () => {
    $modal
      .find(".booking-appointment-slots")
      .html(
        `<p class="slots-status-message">${getSlotsStatusMessage()}</p>`,
      )
      .attr(
        "data-status",
        isCalendarEmptyForSelectedDate() ? "empty" : "initial",
      );
  };

  const resetSlotsPanelForMissingCriteria = () => {
    slotsList = [];
    selectedSlot = null;
    showSlotsStatusMessage();
    hideSlotsLoading();
  };

  const findHospitalByKey = (key) => {
    if (
      typeof HM_Variables === "undefined" ||
      !Array.isArray(HM_Variables.hospitals)
    ) {
      return null;
    }

    if (!key) {
      return null;
    }

    const normalized = String(key).trim().toLowerCase();
    if (!normalized.length) {
      return null;
    }

    return (
      HM_Variables.hospitals.find((hospital) => {
        const shortName = (hospital.hospital_shortname || "").toLowerCase();
        const hisId = String(hospital.hospital_id || "").toLowerCase();
        return (
          shortName === normalized ||
          hisId === normalized ||
          String(hospital.id) === normalized
        );
      }) || null
    );
  };

  const updateSelectedHospitalName = (hospital) => {
    if (!$selectedHospitalNameWrapper.length) {
      return;
    }

    const name = hospital && hospital.title ? String(hospital.title) : "";

    if (name.length) {
      $selectedHospitalName.text(name);
      $selectedHospitalNameWrapper.show();
    } else {
      $selectedHospitalName.text("");
      $selectedHospitalNameWrapper.hide();
    }
  };

  updateSelectedHospitalName(null);

  const getBenhvienId = () => {
    const selectors = [
      "#integrated-booking-form",
      "#the-hospital-booking-form",
      "#the-general-booking-form",
      "#the-package-booking-form",
    ];

    for (const selector of selectors) {
      const $form = $(selector);
      if (!$form.length) {
        continue;
      }

      const id = $form.find('input[name="booking-hospital"]').val();
      if (id) {
        return String(id).trim();
      }
    }

    if (activeInput && activeInput.length) {
      const $activeForm = activeInput.closest("form");
      if ($activeForm.length) {
        const activeId = $activeForm.find('input[name="booking-hospital"]').val();
        if (activeId) {
          return String(activeId).trim();
        }
      }
    }

    return null;
  };

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const isFollowUpConstrainedOpen = () =>
    !!followUpOpenContext &&
    (followUpOpenContext.mode === "add" ||
      followUpOpenContext.mode === "edit" ||
      followUpOpenContext.mode === "recover");

  const getFollowUpAppointments = () => {
    if (!followUpBridge || typeof followUpBridge.getAppointments !== "function") {
      return [];
    }
    const list = followUpBridge.getAppointments();
    return Array.isArray(list) ? list : [];
  };

  const getFollowUpExceptIndex = () => {
    if (
      !isFollowUpConstrainedOpen() ||
      (followUpOpenContext.mode !== "edit" &&
        followUpOpenContext.mode !== "recover")
    ) {
      return -1;
    }
    const index = Number(followUpOpenContext.editIndex);
    return Number.isInteger(index) ? index : -1;
  };

  const slotClockTime = (iso) => {
    if (!iso) {
      return "";
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return date.toLocaleTimeString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTakenSpecialtyIds = () => {
    const ids = new Set();
    if (!isFollowUpConstrainedOpen()) {
      return ids;
    }
    const except = getFollowUpExceptIndex();
    getFollowUpAppointments().forEach((item, index) => {
      if (index === except || !item) {
        return;
      }
      if (
        item.specialtyId !== undefined &&
        item.specialtyId !== null &&
        String(item.specialtyId) !== ""
      ) {
        ids.add(String(item.specialtyId));
      }
    });
    return ids;
  };

  const getTakenSlotTimes = () => {
    const times = new Set();
    if (!isFollowUpConstrainedOpen()) {
      return times;
    }
    const except = getFollowUpExceptIndex();
    getFollowUpAppointments().forEach((item, index) => {
      if (index === except || !item) {
        return;
      }
      const time = slotClockTime(item.startTime);
      if (time) {
        times.add(time);
      }
    });
    return times;
  };

  const getFollowUpSelectedSuffix = () =>
    HM_Variables?.frontend_lang?.followup_specialty_selected ||
    (lang === "en" ? "(selected)" : "(đã chọn)");

  const getTakenSlotTooltip = () =>
    HM_Variables?.frontend_lang?.followup_slot_taken ||
    (lang === "en"
      ? "This time slot is already selected in another appointment"
      : "Khung giờ này đã được chọn ở một lịch hẹn khác");

  let $takenSlotTooltip = null;

  const ensureTakenSlotTooltip = () => {
    if ($takenSlotTooltip && $takenSlotTooltip.length) {
      return $takenSlotTooltip;
    }
    $takenSlotTooltip = $(
      '<div id="crm-taken-slot-tooltip" class="crm-slot-tooltip" role="tooltip"></div>',
    );
    $("body").append($takenSlotTooltip);
    return $takenSlotTooltip;
  };

  const hideTakenSlotTooltip = () => {
    if ($takenSlotTooltip) {
      $takenSlotTooltip.removeClass("is-visible");
    }
  };

  const showTakenSlotTooltip = ($slot) => {
    if (!$slot || !$slot.length) {
      return;
    }

    const $tip = ensureTakenSlotTooltip();
    const wasVisible = $tip.hasClass("is-visible");
    $tip.text($slot.attr("data-tooltip") || getTakenSlotTooltip());

    const rect = $slot[0].getBoundingClientRect();
    const tipRect = $tip[0].getBoundingClientRect();
    const gap = 10;
    const margin = 8;
    let left = rect.left + rect.width / 2 - tipRect.width / 2;
    left = Math.max(
      margin,
      Math.min(left, window.innerWidth - tipRect.width - margin),
    );

    let top = rect.top - tipRect.height - gap;
    let placement = "top";
    if (top < margin) {
      top = rect.bottom + gap;
      placement = "bottom";
    }

    const arrowX = Math.max(
      16,
      Math.min(tipRect.width - 16, rect.left + rect.width / 2 - left),
    );

    $tip
      .css({
        left: `${left}px`,
        top: `${top}px`,
        "--arrow-x": `${arrowX}px`,
      })
      .attr("data-placement", placement);

    if (!wasVisible) {
      requestAnimationFrame(() => {
        $tip.addClass("is-visible");
      });
    }
  };

  const getFollowUpIndexLabel = (index) => {
    const template =
      HM_Variables?.frontend_lang?.followup_appointment_label ||
      (lang === "en" ? "Appointment %d" : "Lịch hẹn %d");
    return String(template).replace("%d", String(index));
  };

  const updateFollowUpIndexLabel = () => {
    const $label = $modal.find(".followup-appointment-index");
    if (!$label.length) {
      return;
    }
    if (!isFollowUpConstrainedOpen()) {
      $label.attr("hidden", "hidden").text("");
      return;
    }
    const displayIndex =
      followUpOpenContext.mode === "edit" ||
      followUpOpenContext.mode === "recover"
        ? Number(followUpOpenContext.editIndex) + 1
        : getFollowUpAppointments().length + 1;
    $label.text(getFollowUpIndexLabel(displayIndex)).removeAttr("hidden");
  };

  const lockCrmDatePicker = () => {
    datePickerLocked = true;
    if ($dateInput.data("xdsoft_datetimepicker")) {
      $dateInput.datetimepicker("destroy");
    }
    $dateInput.closest(".input-group").addClass("is-date-locked");
  };

  const unlockCrmDatePicker = () => {
    datePickerLocked = false;
    $dateInput.closest(".input-group").removeClass("is-date-locked");
    if (typeof $dateInput.datetimepicker === "function") {
      applyCrmDatePickerConstraints(currentHospitalKey);
    }
  };

  const shouldCommitFollowUp = ($form) => {
    if (!followUpBridge || typeof followUpBridge.isFollowUpForm !== "function") {
      return false;
    }
    if (!$form || !$form.length) {
      return false;
    }
    return !!followUpBridge.isFollowUpForm($form);
  };

  const clearFollowUpSpecialtySelection = () => {
    $specialtyInput.val("");
    $specialtyList.find(".option").removeClass("active");
    $specialtySelect
      .find(".current-value")
      .text(getSpecialtyEnabledPlaceholder())
      .addClass("default-value");
    clearDoctorSelection();
    slotsList = [];
    selectedSlot = null;
    showSlotsStatusMessage();
    updateCrmSubmitButtonState();
  };

  const closeModal = () => {
    $modal.removeClass("show-modal").attr("aria-hidden", "true");
    $modal.find(".modal-dialog").removeClass("active");

    if ($(".modal-wrapper.show-modal").not($modal).length === 0) {
      $("body").removeClass(modalBodyClass);
    }

    if (activeInput && activeInput.length && activeInput.is(":visible")) {
      activeInput.trigger("focus");
    }

    followUpOpenContext = null;
    $modal.removeClass("is-above-recovery");
    updateFollowUpIndexLabel();
    hideTakenSlotTooltip();
    if (datePickerLocked) {
      unlockCrmDatePicker();
    }

    activeInput = null;
  };

  let slotsList = [];
  let selectedSlot = null;
  let specialtyMasterList = [];
  let specialtyMasterHospitalId = null;
  let specialtyRequestSeq = 0;
  let calendarCache = [];
  let availableSpecialties = new Set();
  let calendarFetchedForKey = null;
  let calendarRequestSeq = 0;
  let pendingRestore = null;

  const updateCrmSubmitButtonState = () => {
    const $submitBtn = $modal.find(".booking-datetime-submit");
    const hasSpecialty = !!$specialtyInput.val();
    const hasDate = !!$dateInput.val();
    if (hasSpecialty && hasDate && selectedSlot) {
      $submitBtn.prop("disabled", false).removeClass("disabled");
    } else {
      $submitBtn.prop("disabled", true).addClass("disabled");
    }
  };

  const setSpecialtiesLoadingState = (isLoading) => {
    if (typeof onSpecialtiesLoadingChange === "function") {
      onSpecialtiesLoadingChange(Boolean(isLoading));
    }
  };

  const checkSlotPassed = (slot) => {
    const currentTime = new Date().getTime();
    return currentTime > new Date(slot.thoigian_batdau).getTime();
  };

  const isBookableCalendarSlot = (item) => {
    if (!item || item.chuyenkhoa_id === null || item.chuyenkhoa_id === undefined) {
      return false;
    }
    if (String(item.chuyenkhoa_id).trim() === "") {
      return false;
    }
    return item.status === "Open" && !checkSlotPassed(item);
  };

  const deriveAvailableSpecialties = (results) => {
    const ids = new Set();
    if (!Array.isArray(results)) {
      return ids;
    }
    for (const item of results) {
      if (!isBookableCalendarSlot(item)) {
        continue;
      }
      ids.add(String(item.chuyenkhoa_id));
    }
    return ids;
  };

  const groupCalendarResultsByDoctor = (results) => {
    const groupedByDoctor = {};
    if (!Array.isArray(results)) {
      return [];
    }

    for (const item of results) {
      const {
        manhanvien,
        tennhanvien = "",
        thoigian_batdau = "",
        thoigian_ketthuc = "",
        status = "",
        id,
      } = item;

      if (!manhanvien) {
        continue;
      }

      if (!groupedByDoctor[manhanvien]) {
        groupedByDoctor[manhanvien] = {
          manhanvien: String(manhanvien),
          tennhanvien,
          slots: [],
        };
      }

      const numericId =
        typeof id === "number"
          ? id
          : parseInt(id, 10) || groupedByDoctor[manhanvien].slots.length + 1;

      groupedByDoctor[manhanvien].slots.push({
        id: numericId,
        thoigian_batdau,
        thoigian_ketthuc,
        status,
      });
    }

    return Object.values(groupedByDoctor);
  };

  const setSpecialtySelectEnabled = (enabled) => {
    const $currentValue = $specialtySelect.find(".current-value");
    if (enabled) {
      $specialtySelect.removeClass("is-disabled");
      if (!$specialtyInput.val()) {
        $currentValue
          .text(getSpecialtyEnabledPlaceholder())
          .addClass("default-value");
      }
      updateCrmStepHighlight();
      return;
    }

    $specialtySelect.addClass("is-disabled");
    $specialtyInput.val("");
    $specialtyList.find(".option").removeClass("active");
    $currentValue
      .text(getSpecialtyDisabledPlaceholder())
      .addClass("default-value");
    updateCrmStepHighlight();
  };

  const renderFilteredSpecialtyOptions = (availableSpecialtiesSet) => {
    const filtered = specialtyMasterList.filter((item) =>
      availableSpecialtiesSet.has(String(item.chuyenkhoa_id)),
    );

    const html = filtered
      .map((item) => {
        const label =
          lang === "en" ? item.tenchuyenkhoa_en : item.tenchuyenkhoa;
        const safeLabel = escapeHtml(label || "");
        const isTaken = getTakenSpecialtyIds().has(String(item.chuyenkhoa_id));
        const displayLabel = isTaken
          ? `${safeLabel} ${escapeHtml(getFollowUpSelectedSuffix())}`
          : safeLabel;
        return `<li class="option${
          isTaken ? " is-taken" : ""
        }" value="${escapeHtml(item.chuyenkhoa_id)}" data-value="${escapeHtml(
          item.chuyenkhoa_id,
        )}" data-label="${safeLabel}">${displayLabel}</li>`;
      })
      .join("");

    $specialtyList.html(html);

    const currentSpecialty = ($specialtyInput.val() || "").trim();
    const $currentOption = currentSpecialty
      ? $specialtyList.find(`.option[data-value="${currentSpecialty}"]`).first()
      : $();

    if ($currentOption.length && !$currentOption.hasClass("is-taken")) {
      $specialtyList.find(".option").removeClass("active");
      $currentOption.addClass("active");
      $specialtyInput.val(currentSpecialty);
      $specialtySelect
        .find(".current-value")
        .text($currentOption.attr("data-label") || $currentOption.text().trim())
        .removeClass("default-value");
      updateCrmStepHighlight();
      return;
    }

    $specialtyInput.val("");
    $specialtyList.find(".option").removeClass("active");
    $specialtySelect
      .find(".current-value")
      .text(getSpecialtyEnabledPlaceholder())
      .addClass("default-value");
    updateCrmStepHighlight();
  };

  const syncSpecialtySelectFromCaches = () => {
    const restore = pendingRestore || {};

    if (!calendarFetchedForKey) {
      return;
    }

    if (!calendarCache.length) {
      availableSpecialties = new Set();
      $specialtyList.html("");
      setSpecialtySelectEnabled(false);
      showEmptySlotsMessage();
      updateCrmSubmitButtonState();
      return;
    }

    availableSpecialties = deriveAvailableSpecialties(calendarCache);

    if (!specialtyMasterList.length) {
      // Calendar ready but specialty names not loaded yet.
      setSpecialtySelectEnabled(false);
      showSlotsStatusMessage();
      updateCrmSubmitButtonState();
      return;
    }

    const keepDoctorId = $doctorInput.val() || "";
    const keepSlotId = selectedSlot ? String(selectedSlot.id) : "";
    renderFilteredSpecialtyOptions(availableSpecialties);
    setSpecialtySelectEnabled(true);
    updateCrmSubmitButtonState();

    if (restore.restoreSpecialtyId) {
      if (tryRestoreSpecialtySelection(restore.restoreSpecialtyId)) {
        restoreDoctorAndSlotSelection({
          doctorId: restore.restoreDoctorId || "",
          slotId: restore.restoreSlotId || "",
        });
        pendingRestore = null;
        return;
      }
    }

    if ($specialtyInput.val()) {
      renderSlotsFromCalendarCache({
        restoreDoctorId: keepDoctorId,
        restoreSlotId: keepSlotId,
      });
      return;
    }
    showSlotsStatusMessage();
  };

  const fetchSpecialtyMasterList = ({ force = false } = {}) => {
    const benhvienId = getBenhvienId();
    if (!benhvienId) {
      return Promise.resolve([]);
    }

    const hospitalKey = String(benhvienId);
    if (
      !force &&
      specialtyMasterHospitalId === hospitalKey &&
      specialtyMasterList.length > 0
    ) {
      syncSpecialtySelectFromCaches();
      return Promise.resolve(specialtyMasterList);
    }

    const seq = ++specialtyRequestSeq;
    setSpecialtiesLoadingState(true);

    return bookingApi(BOOKING_ACTIONS.GET_SPECIALTIES, {
      benhvien_id: benhvienId,
    })
      .then((specialtyRes) => {
        if (seq !== specialtyRequestSeq) {
          return specialtyMasterList;
        }

        const results =
          (specialtyRes &&
            specialtyRes.data &&
            specialtyRes.data.results) ||
          [];
        specialtyMasterList = Array.isArray(results) ? results : [];
        specialtyMasterHospitalId = hospitalKey;
        syncSpecialtySelectFromCaches();
        return specialtyMasterList;
      })
      .catch(() => {
        if (seq !== specialtyRequestSeq) {
          return specialtyMasterList;
        }
        if (specialtyMasterHospitalId !== hospitalKey) {
          specialtyMasterList = [];
          specialtyMasterHospitalId = null;
        }
        syncSpecialtySelectFromCaches();
        return specialtyMasterList;
      })
      .finally(() => {
        if (seq === specialtyRequestSeq) {
          setSpecialtiesLoadingState(false);
        }
      });
  };

  const clearDoctorSelection = () => {
    const $doctorSelect = $doctorList.closest(".custom-select");
    const doctorPlaceholder = $doctorSelect
      .find(".current-value")
      .data("placeholder");

    $doctorList.html("");
    $doctorInput.val("");
    $doctorList.find(".option").removeClass("active");
    $doctorSelect
      .find(".current-value")
      .text(doctorPlaceholder || "")
      .addClass("default-value");
    updateCrmStepHighlight();
  };

  const populateDoctorDropdown = () => {
    const $doctorSelect = $doctorList.closest(".custom-select");
    const doctorPlaceholder = $doctorSelect
      .find(".current-value")
      .data("placeholder");

    let html = `<li class="option" value="${VIEW_ALL_DOCTOR_ID}" data-value="${VIEW_ALL_DOCTOR_ID}">${getViewAllLabel()}</li>`;
    for (const doctor of slotsList) {
      html += `<li class="option" value="${doctor.manhanvien}" data-value="${doctor.manhanvien}">${doctor.tennhanvien}</li>`;
    }
    $doctorList.html(html);
    $doctorInput.val("");
    $doctorList.find(".option").removeClass("active");
    $doctorSelect
      .find(".current-value")
      .text(doctorPlaceholder || "")
      .addClass("default-value");
    updateCrmStepHighlight();
  };

  const setDoctorSelection = (doctorId) => {
    const $doctorSelect = $doctorList.closest(".custom-select");
    const placeholderDoctor = $doctorSelect
      .find(".current-value")
      .data("placeholder");

    const normalized = doctorId ? String(doctorId) : "";
    const $activeDoctor = normalized
      ? $doctorList.find(`.option[data-value="${normalized}"]`).first()
      : $();

    $doctorList.find(".option").removeClass("active");
    if ($activeDoctor.length) {
      $activeDoctor.addClass("active");
      $doctorInput.val(normalized);
      $doctorSelect
        .find(".current-value")
        .text($activeDoctor.text().trim())
        .removeClass("default-value");
      updateCrmStepHighlight();
      return true;
    }

    $doctorInput.val("");
    $doctorSelect
      .find(".current-value")
      .text(placeholderDoctor || "")
      .addClass("default-value");
    updateCrmStepHighlight();
    return false;
  };

  const restoreDoctorAndSlotSelection = ({ doctorId = "", slotId = "" } = {}) => {
    if (!doctorId && !slotId) {
      return;
    }

    setDoctorSelection(doctorId);
    renderSlots();

    if (!slotId) {
      return;
    }

    const $slot = $modal
      .find(".js-crm-slot-item")
      .filter((_, el) => String($(el).attr("data-slot-id")) === String(slotId))
      .first();

    if (
      !$slot.length ||
      $slot.hasClass("disabled") ||
      $slot.hasClass("is-taken")
    ) {
      return;
    }

    $modal.find(".js-crm-slot-item").not(".is-taken").removeClass("selected");
    $slot.addClass("selected");
    selectedSlot = {
      id: $slot.attr("data-slot-id"),
      thoigian_batdau: $slot.attr("data-time"),
      tennhanvien: $slot.attr("data-tennhanvien") || "",
      manhanvien: $slot.attr("data-manhanvien") || "",
    };
    updateCrmSubmitButtonState();
  };

  const tryRestoreSpecialtySelection = (specialtyId) => {
    if (!specialtyId || !availableSpecialties.has(String(specialtyId))) {
      return false;
    }

    const $option = $specialtyList
      .find(`.option[data-value="${specialtyId}"]`)
      .first();
    if (!$option.length || $option.hasClass("is-taken")) {
      return false;
    }

    $specialtyList.find(".option").removeClass("active");
    $option.addClass("active");
    $specialtyInput.val(String(specialtyId));
    $specialtySelect
      .find(".current-value")
      .text($option.attr("data-label") || $option.text().trim())
      .removeClass("default-value");
    updateCrmStepHighlight();
    renderSlotsFromCalendarCache();
    return true;
  };

  const renderSlotsFromCalendarCache = ({
    restoreDoctorId = "",
    restoreSlotId = "",
  } = {}) => {
    const chuyenkhoaId = $specialtyInput.val();
    const ngaykham = ($dateInput.val() || "").trim();

    if (!chuyenkhoaId || !ngaykham || !calendarCache.length) {
      clearDoctorSelection();
      resetSlotsPanelForMissingCriteria();
      updateCrmSubmitButtonState();
      return;
    }

    let filtered = calendarCache.filter(
      (item) =>
        String(item.chuyenkhoa_id) === String(chuyenkhoaId) &&
        isBookableCalendarSlot(item),
    );

    // Recover mode: a slot the CRM reported full is usually gone from the
    // fresh calendar. Put it back so it still shows as overbooked (disabled).
    if (followUpOpenContext && followUpOpenContext.mode === "recover") {
      const shownIds = new Set(filtered.map((item) => String(item.id)));
      (followUpOpenContext.overbookedSlots || []).forEach((slot) => {
        if (
          !slot ||
          shownIds.has(String(slot.id)) ||
          String(slot.chuyenkhoa_id) !== String(chuyenkhoaId)
        ) {
          return;
        }
        const cached = calendarCache.find(
          (item) => String(item.id) === String(slot.id),
        );
        filtered.push(cached || { ...slot, status: "Full" });
        shownIds.add(String(slot.id));
      });
      filtered = filtered.sort(
        (a, b) =>
          new Date(a.thoigian_batdau).getTime() -
          new Date(b.thoigian_batdau).getTime(),
      );
    }

    slotsList = groupCalendarResultsByDoctor(filtered);
    selectedSlot = null;
    populateDoctorDropdown();
    updateCrmSubmitButtonState();
    renderSlots();
    if (restoreDoctorId || restoreSlotId) {
      restoreDoctorAndSlotSelection({
        doctorId: restoreDoctorId,
        slotId: restoreSlotId,
      });
    }
  };

  const fetchCalendarForDate = ({
    restoreSpecialtyId = null,
    restoreDoctorId = "",
    restoreSlotId = "",
    force = false,
  } = {}) => {
    const benhvienId = getBenhvienId();
    const ngaykham = ($dateInput.val() || "").trim();

    if (!benhvienId || !ngaykham) {
      calendarCache = [];
      availableSpecialties = new Set();
      calendarFetchedForKey = null;
      calendarRequestSeq += 1;
      pendingRestore = null;
      clearDoctorSelection();
      $specialtyList.html("");
      $specialtyInput.val("");
      setSpecialtySelectEnabled(false);
      resetSlotsPanelForMissingCriteria();
      updateCrmSubmitButtonState();
      return;
    }

    if (!isCrmBookingDateAllowed(ngaykham)) {
      showDateInputError(getPastDateErrorMessage());
      calendarCache = [];
      availableSpecialties = new Set();
      calendarFetchedForKey = null;
      calendarRequestSeq += 1;
      pendingRestore = null;
      clearDoctorSelection();
      $specialtyList.html("");
      $specialtyInput.val("");
      setSpecialtySelectEnabled(false);
      resetSlotsPanelForMissingCriteria();
      updateCrmSubmitButtonState();
      return;
    }

    const parsedNgaykham = parseDateInputValue(ngaykham);
    if (
      !parsedNgaykham ||
      !isDateAllowedByCrmPickerConstraints(
        parsedNgaykham,
        currentDatePickerConstraints,
      )
    ) {
      showDateInputError(getDateNotAllowedErrorMessage());
      calendarCache = [];
      availableSpecialties = new Set();
      calendarFetchedForKey = null;
      calendarRequestSeq += 1;
      pendingRestore = null;
      clearDoctorSelection();
      $specialtyList.html("");
      $specialtyInput.val("");
      setSpecialtySelectEnabled(false);
      resetSlotsPanelForMissingCriteria();
      updateCrmSubmitButtonState();
      return;
    }

    clearDateInputError();

    const requestKey = `${benhvienId}|${ngaykham}`;
    pendingRestore = {
      restoreSpecialtyId,
      restoreDoctorId,
      restoreSlotId,
    };

    // Use completed-fetch key (not in-flight) so first select is never skipped.
    if (!force && requestKey === calendarFetchedForKey) {
      const hasSpecialtyOptions = $specialtyList.find(".option").length > 0;
      if (isFollowUpConstrainedOpen()) {
        renderFilteredSpecialtyOptions(availableSpecialties);
        if (specialtyMasterList.length && availableSpecialties.size) {
          setSpecialtySelectEnabled(true);
        }
        if (
          restoreSpecialtyId &&
          tryRestoreSpecialtySelection(restoreSpecialtyId)
        ) {
          if (restoreDoctorId || restoreSlotId) {
            restoreDoctorAndSlotSelection({
              doctorId: restoreDoctorId,
              slotId: restoreSlotId,
            });
          }
        } else {
          clearFollowUpSpecialtySelection();
        }
        return;
      }
      if (hasSpecialtyOptions) {
        renderFilteredSpecialtyOptions(availableSpecialties);
        if (restoreSpecialtyId) {
          tryRestoreSpecialtySelection(restoreSpecialtyId);
          if (restoreDoctorId || restoreSlotId) {
            restoreDoctorAndSlotSelection({
              doctorId: restoreDoctorId,
              slotId: restoreSlotId,
            });
          }
        }
        return;
      }
      syncSpecialtySelectFromCaches();
      return;
    }

    const seq = ++calendarRequestSeq;
    showSlotsLoading();

    bookingApi(BOOKING_ACTIONS.GET_CALENDAR, {
      benhvien_id: benhvienId,
      ngaykham,
    })
      .then((res) => {
        if (seq !== calendarRequestSeq) {
          return;
        }

        calendarFetchedForKey = requestKey;
        calendarCache = (res && res.data && res.data.results) || [];
        clearDoctorSelection();
        slotsList = [];
        selectedSlot = null;
        syncSpecialtySelectFromCaches();

        // Ensure specialty master list is loading/loaded for this hospital.
        fetchSpecialtyMasterList();
      })
      .catch(() => {
        if (seq !== calendarRequestSeq) {
          return;
        }

        calendarCache = [];
        availableSpecialties = new Set();
        calendarFetchedForKey = null;
        clearDoctorSelection();
        slotsList = [];
        selectedSlot = null;
        $specialtyList.html("");
        $specialtyInput.val("");
        setSpecialtySelectEnabled(false);
        showSlotsStatusMessage();
        updateCrmSubmitButtonState();
      })
      .finally(() => {
        if (seq === calendarRequestSeq) {
          hideSlotsLoading();
        }
      });
  };

  const renderSlotsByShift = (
    slots,
    shift,
    tennhanvien = "",
    manhanvien = "",
  ) => {
    if (!Array.isArray(slots) || slots.length === 0) {
      return "";
    }

    if (shift !== "morning" && shift !== "afternoon") {
      return "";
    }

    const morningIcon = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.99984 0.666656V1.99999M7.99984 14V15.3333M2.81317 2.81332L3.75984 3.75999M12.2398 12.24L13.1865 13.1867M0.666504 7.99999H1.99984M13.9998 7.99999H15.3332M2.81317 13.1867L3.75984 12.24M12.2398 3.75999L13.1865 2.81332M11.3332 7.99999C11.3332 9.84094 9.84079 11.3333 7.99984 11.3333C6.15889 11.3333 4.6665 9.84094 4.6665 7.99999C4.6665 6.15904 6.15889 4.66666 7.99984 4.66666C9.84079 4.66666 11.3332 6.15904 11.3332 7.99999Z" stroke="#006991" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    const afternoonIcon = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.3332 12C11.3332 11.1159 10.982 10.2681 10.3569 9.64296C9.73174 9.01784 8.88389 8.66665 7.99984 8.66665C7.11578 8.66665 6.26794 9.01784 5.64281 9.64296C5.01769 10.2681 4.6665 11.1159 4.6665 12M7.99984 5.99998V1.33331M7.99984 5.99998L10.6665 3.33331M7.99984 5.99998L5.33317 3.33331M2.81317 6.81331L3.75984 7.75998M0.666504 12H1.99984M13.9998 12H15.3332M12.2398 7.75998L13.1865 6.81331M15.3332 14.6666H0.666504" stroke="#006991" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    const morningTitle =
      HM_Variables?.frontend_lang?.booking_messages?.morning ||
      (lang === "en" ? "Morning" : "Buổi sáng");
    const afternoonTitle =
      HM_Variables?.frontend_lang?.booking_messages?.afternoon ||
      (lang === "en" ? "Afternoon" : "Buổi chiều");

    return `<div class="slot-group__items-shift"><div class="slot-group__items-shift-title">${
      shift === "morning" ? morningIcon : afternoonIcon
    }${
      shift === "morning" ? morningTitle : afternoonTitle
    }</div><div class="slot-group__items-shift-group">${slots
      .map((slotItem) => {
        const time = new Date(slotItem.thoigian_batdau).toLocaleTimeString(
          "vi-VN",
          { hour: "2-digit", minute: "2-digit" },
        );
        const status = slotItem.status;
        // Recover mode: every slot the CRM already reported full for this card.
        const overbookedIds =
          followUpOpenContext && followUpOpenContext.mode === "recover"
            ? (followUpOpenContext.overbookedSlotIds || []).map(String)
            : [];
        const isOverbooked = overbookedIds.includes(String(slotItem.id));
        const isClosed = status !== "Open" || checkSlotPassed(slotItem);
        const isDisabled = isClosed || isOverbooked;
        const isTakenTime =
          !isDisabled && !isOverbooked && getTakenSlotTimes().has(time);
        const isSelected =
          !isDisabled &&
          !isTakenTime &&
          selectedSlot &&
          String(selectedSlot.id) === String(slotItem.id);
        const tennhanvienEsc = (tennhanvien || "").replace(/"/g, "&quot;");
        const manhanvienEsc = (manhanvien || "").replace(/"/g, "&quot;");
        const overbookedReason = isOverbooked
          ? escapeHtml(followUpOpenContext.overbookedReason || "")
          : "";
        const slotClass = isOverbooked
          ? "disabled is-overbooked"
          : isDisabled
            ? "disabled"
            : isTakenTime
              ? "selected is-taken"
              : isSelected
                ? "selected"
                : "";
        const slotTooltip = isOverbooked
          ? ` data-tooltip="${overbookedReason}"`
          : isTakenTime
            ? ` data-tooltip="${escapeHtml(getTakenSlotTooltip())}"`
            : "";
        return `<div class="slot-item js-crm-slot-item ${slotClass}" data-slot-id="${slotItem.id}" data-time="${
          slotItem.thoigian_batdau
        }" data-tennhanvien="${tennhanvienEsc}" data-manhanvien="${manhanvienEsc}"${slotTooltip}${
          isDisabled || isTakenTime ? "" : ' role="button" tabindex="0"'
        }><div class="slot-item__time">${time}</div></div>`;
      })
      .join("")}</div></div>`;
  };

  const filterSlotsByDoctor = (doctorId) => {
    return slotsList.filter((slot) => slot.manhanvien === doctorId);
  };

  const renderSlots = () => {
    hideTakenSlotTooltip();
    const $slots = $modal.find(".booking-appointment-slots");
    if (!$slots.length) {
      return;
    }

    const noSlotsMsg = getNoSlotsMessage();

    if (slotsList.length === 0) {
      if (!hasSlotSearchCriteria()) {
        showSlotsStatusMessage();
        return;
      }

      $slots.html(`<p class="slots-status-message">${noSlotsMsg}</p>`);
      $slots.attr("data-status", "empty");
      return;
    }

    const doctorId = $doctorInput.val();
    const listToRender =
      doctorId && doctorId !== VIEW_ALL_DOCTOR_ID
        ? filterSlotsByDoctor(doctorId)
        : slotsList;

    if (listToRender.length === 0) {
      $slots.html(`<p class="slots-status-message">${noSlotsMsg}</p>`);
      $slots.attr("data-status", "empty");
      return;
    }

    $slots.html(
      listToRender
        .map((slot) => {
          const morningSlots = slot.slots.filter((slotItem) => {
            const time = new Date(slotItem.thoigian_batdau);
            return time.getHours() <= 12;
          });
          const afternoonSlots = slot.slots.filter((slotItem) => {
            const time = new Date(slotItem.thoigian_batdau);
            return time.getHours() > 12;
          });

          return `<div class="slot-group"><div class="slot-group__title">${
            slot.tennhanvien
          }</div><div class="slot-group__items">${renderSlotsByShift(
            morningSlots,
            "morning",
            slot.tennhanvien,
            slot.manhanvien,
          )}${renderSlotsByShift(
            afternoonSlots,
            "afternoon",
            slot.tennhanvien,
            slot.manhanvien,
          )}</div></div>`;
        })
        .join(""),
    );

    $slots.attr("data-status", "filled");
    updateCrmSubmitButtonState();
  };

  const resetCrmModalState = ({ clearSpecialties = false } = {}) => {
    const $doctorSelect = $doctorList.closest(".custom-select");
    const doctorPlaceholder = $doctorSelect
      .find(".current-value")
      .data("placeholder");

    if (clearSpecialties) {
      specialtyRequestSeq += 1;
      specialtyMasterList = [];
      specialtyMasterHospitalId = null;
      $specialtyList.html("");
      setSpecialtiesLoadingState(false);
    }

    calendarRequestSeq += 1;
    calendarCache = [];
    availableSpecialties = new Set();
    calendarFetchedForKey = null;
    pendingRestore = null;

    $specialtyInput.val("");
    $specialtyList.find(".option").removeClass("active");
    setSpecialtySelectEnabled(false);

    $dateInput.val("");
    $doctorList.html("");
    $doctorInput.val("");
    $doctorList.find(".option").removeClass("active");
    $doctorSelect
      .find(".current-value")
      .text(doctorPlaceholder || "")
      .addClass("default-value");

    slotsList = [];
    selectedSlot = null;
    showSlotsStatusMessage();

    hideSlotsLoading();
    updateCrmSubmitButtonState();
    updateCrmStepHighlight();
  };

  const updateModalBrandUI = (hospitalData) => {
    $modal.removeClass("hmg-ui hm-ui tm-ui hp-ui");

    if (!hospitalData) {
      return;
    }

    const brandShortName =
      hospitalData.brand_shortname ||
      hospitalData.hospital_brand_shortname ||
      hospitalData.hospital_shortname ||
      "";

    if (brandShortName.toLowerCase() === "hmg") {
      $modal.addClass("hmg-ui");
    } else if (brandShortName.toLowerCase() === "tm") {
      $modal.addClass("tm-ui");
    } else if (brandShortName.toLowerCase() === "hp") {
      $modal.addClass("hp-ui");
    }
  };

  const openModal = ($input, followUpContext = null) => {
    activeInput = $input;
    followUpOpenContext =
      followUpContext &&
      (followUpContext.mode === "add" ||
        followUpContext.mode === "edit" ||
        followUpContext.mode === "recover")
        ? followUpContext
        : null;
    $modal.toggleClass(
      "is-above-recovery",
      !!(followUpOpenContext && followUpOpenContext.mode === "recover"),
    );
    datePickerLocked = false;
    $dateInput.closest(".input-group").removeClass("is-date-locked");

    if (currentHospitalKey) {
      const hospital = findHospitalByKey(currentHospitalKey);
      if (hospital) {
        updateModalBrandUI(hospital);
      }
    }

    // Refresh picker bounds when opening (e.g. Saturday crossed 16:30).
    applyCrmDatePickerConstraints(currentHospitalKey);

    if (isFollowUpConstrainedOpen()) {
      const appointments = getFollowUpAppointments();
      const source =
        followUpOpenContext.mode === "edit" ||
        followUpOpenContext.mode === "recover"
          ? appointments[Number(followUpOpenContext.editIndex)]
          : null;
      if (followUpOpenContext.mode === "add") {
        $specialtyInput.val("");
        $doctorInput.val("");
        selectedSlot = null;
      } else if (source) {
        $specialtyInput.val(
          source.specialtyId ? String(source.specialtyId) : "",
        );
        $doctorInput.val(source.doctorId ? String(source.doctorId) : "");
      }
    }

    const $doctorSelect = $doctorList.closest(".custom-select");
    const placeholderDoctor = $doctorSelect
      .find(".current-value")
      .data("placeholder");

    const specialtyValue = $specialtyInput.val();
    if (specialtyValue) {
      const $activeSpecialty = $specialtyList
        .find(`.option[data-value="${specialtyValue}"]`)
        .first();
      if ($activeSpecialty.length) {
        $specialtyList.find(".option").removeClass("active");
        $activeSpecialty.addClass("active");
        $specialtySelect
          .find(".current-value")
          .text($activeSpecialty.text().trim())
          .removeClass("default-value");
      } else {
        $specialtySelect
          .find(".current-value")
          .text(getSpecialtyDisabledPlaceholder())
          .addClass("default-value");
        $specialtyList.find(".option").removeClass("active");
      }
    } else {
      $specialtySelect
        .find(".current-value")
        .text(getSpecialtyDisabledPlaceholder())
        .addClass("default-value");
      $specialtyList.find(".option").removeClass("active");
    }

    const doctorValue = $doctorInput.val();
    if (doctorValue) {
      const $activeDoctor = $doctorList
        .find(`.option[data-value="${doctorValue}"]`)
        .first();
      if ($activeDoctor.length) {
        $doctorList.find(".option").removeClass("active");
        $activeDoctor.addClass("active");
        $doctorSelect
          .find(".current-value")
          .text($activeDoctor.text().trim())
          .removeClass("default-value");
      } else {
        $doctorSelect
          .find(".current-value")
          .text(placeholderDoctor)
          .addClass("default-value");
        $doctorList.find(".option").removeClass("active");
      }
    } else {
      $doctorSelect
        .find(".current-value")
        .text(placeholderDoctor)
        .addClass("default-value");
      $doctorList.find(".option").removeClass("active");
    }

    $("body").addClass(modalBodyClass);
    $modal.addClass("show-modal").attr("aria-hidden", "false");
    $modal.find(".modal-dialog").addClass("active");

    const $focusTarget = $modal.find("[data-focus-default]").first();
    if ($focusTarget.length) {
      $focusTarget.trigger("focus");
    }
    updateCrmSubmitButtonState();

    let dateVal = ($dateInput.val() || "").trim();
    let restoreSpecialtyId = specialtyValue || null;
    let restoreDoctorId = $doctorInput.val() || "";
    let restoreSlotId = "";

    const $form = $input.closest("form");
    if ($form.length) {
      const formDateTime = $form.find('input[name="booking-time"]').val() || "";
      const formSpecialtyId = $form.find('input[name="booking-specialty"]').val() || "";
      const formDoctorId = $form.find('input[name="booking-doctor-id"]').val() || "";
      const formSlotId = $form.find('input[name="booking-timeslot-id"]').val() || "";

      if (formDateTime) {
        dateVal = formatDateForInput(formDateTime);
        $dateInput.val(dateVal);
      }
      if (formSpecialtyId) {
        restoreSpecialtyId = String(formSpecialtyId);
      }
      if (formDoctorId) {
        restoreDoctorId = String(formDoctorId);
      }
      if (formSlotId) {
        restoreSlotId = String(formSlotId);
      }
    }

    if (isFollowUpConstrainedOpen()) {
      const appointments = getFollowUpAppointments();
      const source =
        followUpOpenContext.mode === "edit" ||
        followUpOpenContext.mode === "recover"
          ? appointments[Number(followUpOpenContext.editIndex)]
          : appointments[0];
      const lockedDate = source ? formatDateForInput(source.startTime) : "";
      if (lockedDate) {
        dateVal = lockedDate;
        $dateInput.val(lockedDate);
      }
      if (followUpOpenContext.mode === "add") {
        restoreSpecialtyId = null;
        restoreDoctorId = "";
        restoreSlotId = "";
      } else if (source && followUpOpenContext.mode === "recover") {
        restoreSpecialtyId = source.specialtyId
          ? String(source.specialtyId)
          : null;
        restoreDoctorId = source.doctorId ? String(source.doctorId) : "";
        restoreSlotId = "";
        selectedSlot = null;
      } else if (source) {
        restoreSpecialtyId = source.specialtyId
          ? String(source.specialtyId)
          : null;
        restoreDoctorId = source.doctorId ? String(source.doctorId) : "";
        restoreSlotId = source.timeslotid ? String(source.timeslotid) : "";
      }
      lockCrmDatePicker();
    }

    updateFollowUpIndexLabel();

    if (getBenhvienId()) {
      fetchSpecialtyMasterList();
    }

    if (dateVal && getBenhvienId()) {
      fetchCalendarForDate({
        restoreSpecialtyId,
        restoreDoctorId,
        restoreSlotId,
        // Recovery means slots just filled up: skip the cached day.
        force: !!(followUpOpenContext && followUpOpenContext.mode === "recover"),
      });
    } else {
      setSpecialtySelectEnabled(false);
      showSlotsStatusMessage();
    }
    updateCrmStepHighlight();
  };

  let fetchCalendarDebounceId = null;
  const scheduleFetchCalendarForDate = () => {
    const requestKey = getCalendarRequestKey();
    if (requestKey && requestKey === calendarFetchedForKey) {
      return;
    }
    if (fetchCalendarDebounceId) {
      clearTimeout(fetchCalendarDebounceId);
    }
    // A new date starts a fresh pick: drop specialty, doctor and slot.
    $specialtyInput.val("");
    $specialtyList.find(".option").removeClass("active");
    $specialtySelect
      .find(".current-value")
      .text(getSpecialtyEnabledPlaceholder())
      .addClass("default-value");
    clearDoctorSelection();
    resetSlotsPanelForMissingCriteria();
    updateCrmSubmitButtonState();
    fetchCalendarDebounceId = setTimeout(() => {
      fetchCalendarDebounceId = null;
      fetchCalendarForDate();
    }, 150);
  };

  $(document)
    .off("keydown.bookingDateTimeModalCRM")
    .on("keydown.bookingDateTimeModalCRM", function (event) {
      if (event.key === "Escape" && $modal.hasClass("show-modal")) {
        closeModal();
      }
    });

  $modal
    .off("change.crmDate")
    .on("change.crmDate", 'input[name="booking-datetime"]', () => {
      if (datePickerLocked) {
        return;
      }
      updateCrmStepHighlight();
      scheduleFetchCalendarForDate();
    });

  $modal
    .off("change.crmSpecialty")
    .on("change.crmSpecialty", 'input[name="crm-booking-specialty"]', () => {
      updateCrmStepHighlight();
      renderSlotsFromCalendarCache();
    });

  $modal
    .off("change.crmDoctor")
    .on("change.crmDoctor", 'input[name="crm-booking-doctor"]', () => {
      updateCrmStepHighlight();
      renderSlots();
    });

  $modal
    .off("mouseenter.crmTakenSlot mouseleave.crmTakenSlot")
    .on(
      "mouseenter.crmTakenSlot",
      ".js-crm-slot-item.is-taken, .js-crm-slot-item.is-overbooked",
      function () {
        showTakenSlotTooltip($(this));
      },
    )
    .on(
      "mouseleave.crmTakenSlot",
      ".js-crm-slot-item.is-taken, .js-crm-slot-item.is-overbooked",
      hideTakenSlotTooltip,
    );

  $modal
    .find(".booking-appointment-slots-wrapper, .booking-appointment-slots")
    .off("scroll.crmTakenSlot")
    .on("scroll.crmTakenSlot", hideTakenSlotTooltip);

  $(window).off("resize.crmTakenSlot").on("resize.crmTakenSlot", hideTakenSlotTooltip);

  $modal
    .off("click.crmSlotItem")
    .on("click.crmSlotItem", ".js-crm-slot-item:not(.disabled):not(.is-taken)", function (e) {
      e.preventDefault();
      const $el = $(this);
      $modal.find(".js-crm-slot-item").not(".is-taken").removeClass("selected");
      $el.addClass("selected");
      selectedSlot = {
        id: $el.attr("data-slot-id"),
        thoigian_batdau: $el.attr("data-time"),
        tennhanvien: $el.attr("data-tennhanvien") || "",
        manhanvien: $el.attr("data-manhanvien") || "",
      };
      updateCrmSubmitButtonState();
    });

  $(document)
    .off("click.bookingDateTimeSubmitCRM")
    .on(
      "click.bookingDateTimeSubmitCRM",
      "#booking-datetime-modal-crm .booking-datetime-submit",
      function (event) {
        event.preventDefault();

        if (!activeInput || !activeInput.length) {
          return;
        }

        const specialtyId = $specialtyInput.val();
        const dateVal = $dateInput.val();
        // Name of the CRM specialty whose id is submitted (tenchuyenkhoa / _en).
        const $selectedSpecialty = $specialtyList
          .find(".option")
          .filter((_, el) => String($(el).attr("data-value")) === String(specialtyId))
          .first();
        const $activeSpecialty = $selectedSpecialty.length
          ? $selectedSpecialty
          : $specialtyList.find(".option.active").first();
        const specialtyName =
          $activeSpecialty.attr("data-label") ||
          $activeSpecialty.text().trim() ||
          "";

        if (!specialtyId || !dateVal || !selectedSlot) {
          return;
        }

        if (!isCrmBookingDateAllowed(dateVal)) {
          showDateInputError(getPastDateErrorMessage());
          return;
        }

        const $form = activeInput.closest("form");
        if (shouldCommitFollowUp($form)) {
          const committed = followUpBridge.commit(
            {
              timeslotid: selectedSlot.id,
              specialtyId,
              specialtyName,
              doctorId: selectedSlot.manhanvien || "",
              doctorName: selectedSlot.tennhanvien || "",
              startTime: selectedSlot.thoigian_batdau || "",
            },
            followUpOpenContext || { mode: "create", editIndex: null },
          );
          if (committed === false) {
            return;
          }
          closeModal();
          return;
        }

        const dateFormatted = formatDateForDisplay(dateVal);
        const timeFormatted = new Date(
          selectedSlot.thoigian_batdau,
        ).toLocaleTimeString("vi-VN", {
          hour: "2-digit",
          minute: "2-digit",
        });
        const displayValue = [
          specialtyName,
          dateFormatted,
          timeFormatted,
          selectedSlot.tennhanvien,
        ]
          .filter(Boolean)
          .join(" - ");

        activeInput.val(displayValue);

        if ($form.length) {
          $form
            .find('input[name="booking-time"]')
            .val(selectedSlot.thoigian_batdau || "")
            .trigger("change");
          $form
            .find('input[name="booking-timeslot-id"]')
            .val(selectedSlot.id || "");
          $form
            .find('input[name="booking-doctor-name"]')
            .val(selectedSlot.tennhanvien || "");
          $form
            .find('input[name="booking-doctor-id"]')
            .val(selectedSlot.manhanvien || "");
          $form.find('input[name="booking-specialty"]').val(specialtyId || "");
          $form
            .find('input[name="booking-specialty-id"]')
            .val(specialtyId || "");
          $form
            .find('input[name="booking-specialty-name"]')
            .val(specialtyName);
        }

        activeInput.trigger("change");
        closeModal();
      },
    );

  const setHospital = (key, hospitalData = null) => {
    const normalized =
      typeof key === "string" && key.trim().length > 0
        ? key.trim().toLowerCase()
        : null;

    if (currentHospitalKey === normalized) {
      const displayHospital = hospitalData || findHospitalByKey(normalized);
      updateSelectedHospitalName(displayHospital);
      updateModalBrandUI(displayHospital);
      applyCrmDatePickerConstraints(normalized);
      return;
    }

    currentHospitalKey = normalized;

    const displayHospital = hospitalData || findHospitalByKey(normalized);
    updateSelectedHospitalName(displayHospital);
    updateModalBrandUI(displayHospital);

    // When hospital changes, modal filters/slots must be fully reset
    // so selections from previous hospital are never reused.
    resetCrmModalState({ clearSpecialties: true });
    applyCrmDatePickerConstraints(normalized);

    if (getBenhvienId()) {
      fetchSpecialtyMasterList();
    }
  };

  applyCrmDatePickerConstraints(null);
  setSpecialtySelectEnabled(false);
  showSlotsStatusMessage();
  updateCrmStepHighlight();

  return {
    open: openModal,
    setHospital,
    setSpecialtiesLoadingListener: (listener) => {
      onSpecialtiesLoadingChange =
        typeof listener === "function" ? listener : null;
    },
    setFollowUpBridge: (bridge) => {
      followUpBridge = bridge || null;
    },
    // Drop date / specialty / doctor / slot so the next open starts a fresh first slot.
    // The hospital's specialty list stays cached (fetched once per hospital).
    reset: () => {
      if (datePickerLocked) {
        unlockCrmDatePicker();
      }
      resetCrmModalState();
    },
  };
};

class BookingFormBase {
  constructor(
    formSelector,
    { formType = "general", dateTimeManager, managers } = {},
  ) {
    this.formSelector = formSelector;
    this.formType = formType;

    const slotMgr =
      (managers && managers.slot) || dateTimeManager || null;
    const crmMgr = (managers && managers.crm) || null;
    this.managers = { slot: slotMgr, crm: crmMgr };
    this.dateTimeManager = slotMgr;
    this.activeManager = crmMgr || slotMgr;

    this.$form = $(formSelector);
    this.isActive = this.$form.length > 0;
    this.currentFormSelector = formSelector;
    const lang =
      typeof HM_Variables !== "undefined" && HM_Variables.language
        ? HM_Variables.language
        : "vi";
    this.hospitalRequiredMessage =
      lang === "vi"
        ? "Vui lòng chọn bệnh viện trước khi chọn ngày/giờ."
        : "Please select a hospital before choosing date & time.";
    this.appointmentTypeRequiredMessage =
      lang === "vi"
        ? "Vui lòng chọn loại lịch hẹn trước khi chọn ngày/giờ."
        : "Please select an appointment type before choosing date & time.";
    this.hospitalAndTypeRequiredMessage =
      lang === "vi"
        ? "Vui lòng chọn bệnh viện và loại lịch hẹn trước khi chọn ngày/giờ."
        : "Please select a hospital and an appointment type before choosing date & time.";
    this.currentHospitalKey = null;
    this.bookingTimeSelector = 'input[name="booking-time"]';
    this.usesBookingTimeRelationLoading =
      formType === "integrated-crm" ||
      formType === "hospital" ||
      formType === "package";
    this.bookingTimeLoading = false;
    this.bookingTimeLoadingMessageCrm =
      lang === "vi"
        ? "Đang tải chuyên khoa, vui lòng chờ..."
        : "Loading specialties, please wait...";
    this.bookingTimeLoadingMessageLegacy =
      HM_Variables?.frontend_lang?.loading_specialty_package ||
      (lang === "vi"
        ? "Đang tải chuyên khoa và gói khám, vui lòng chờ..."
        : "Loading specialties and packages, please wait...");
    this.bookingTimeLoadingMessage = this.bookingTimeLoadingMessageCrm;
  }

  // Get "Examine by Specialty" label based on language
  getExamineBySpecialtyLabel() {
    if (
      typeof HM_Variables !== "undefined" &&
      HM_Variables.frontend_lang &&
      HM_Variables.frontend_lang.select_placeholders
    ) {
      return (
        HM_Variables.frontend_lang.select_placeholders.examine_by_specialty ||
        "Khám theo chuyên khoa"
      );
    }
    return "Khám theo chuyên khoa";
  }

  initBase() {
    if (!this.isActive) {
      return;
    }

    this.setupDateTimeField();
    this.initLocationSelectors();
    this.initAppointmentTypes();
    this.bindCommonEvents();
    this.clearOtpVerificationToken();
    if (this.onOtpTokenPageShow) {
      $(window).off("pageshow", this.onOtpTokenPageShow);
    }
    this.onOtpTokenPageShow = (e) => {
      if (e.originalEvent && e.originalEvent.persisted) {
        this.clearOtpVerificationToken();
      }
    };
    $(window).on("pageshow", this.onOtpTokenPageShow);
    this.validateFormRealtime();
    this.initFollowUpAppointments();
  }

  getInputValue(fieldName) {
    const $field = this.$form
      .find(`input[name="${fieldName}"], textarea[name="${fieldName}"]`)
      .first();
    if (!$field.length) {
      return "";
    }

    const rawValue = $field.val();
    const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;

    if (value === undefined || value === null) {
      return "";
    }

    return String(value).trim();
  }

  getLocationPlaceholder(key, fallback) {
    if (
      typeof HM_Variables !== "undefined" &&
      HM_Variables.frontend_lang &&
      HM_Variables.frontend_lang.select_placeholders &&
      HM_Variables.frontend_lang.select_placeholders[key]
    ) {
      return HM_Variables.frontend_lang.select_placeholders[key];
    }
    return fallback;
  }

  setWardSelectDisabled(disabled) {
    const $wardSelect = this.$form
      .find("#ward-select-list")
      .closest(".custom-select");
    const $wardCurrent = $wardSelect.find(".current-value");

    if (!$wardSelect.length) {
      return;
    }

    if (disabled) {
      $wardSelect.css({
        "pointer-events": "none",
        cursor: "not-allowed",
        opacity: "0.65",
      });
      $wardCurrent
        .html(this.getLocationPlaceholder("choose_ward", "Choose Ward"))
        .addClass("default-value");
      $wardSelect.find(".arrow-ico").hide();
    } else {
      $wardSelect.css({
        "pointer-events": "",
        cursor: "",
        opacity: "",
      });
      $wardSelect.find(".arrow-ico").show();
    }
  }

  initLocationSelectors() {
    const $provinceList = this.$form.find("#province-select-list");
    const $wardList = this.$form.find("#ward-select-list");
    const $provinceInput = this.$form.find("input[name='id_tinh_thanh']");
    const $wardInput = this.$form.find("input[name='id_phuong_xa']");
    const $wardSelect = $wardList.closest(".custom-select");
    const $wardCurrent = $wardSelect.find(".current-value");

    if (
      !$provinceList.length ||
      !$wardList.length ||
      !$provinceInput.length ||
      !$wardInput.length
    ) {
      return;
    }

    const provinces =
      typeof HM_Variables !== "undefined" &&
      HM_Variables.location_units &&
      Array.isArray(HM_Variables.location_units.provinces)
        ? HM_Variables.location_units.provinces
        : [];

    const buildWardOptions = (wards) => {
      if (!Array.isArray(wards) || !wards.length) {
        return '<li class="label">No ward available</li>';
      }

      return wards
        .map(
          (ward) =>
            `<li class="option" data-value="${ward.id}">${ward.name}</li>`,
        )
        .join("");
    };

    const updateWardsByProvince = (provinceId) => {
      $wardCurrent
        .html(this.getLocationPlaceholder("choose_ward", "Choose Ward"))
        .addClass("default-value");
      $wardList.find(".option").removeClass("active");

      if (!provinceId) {
        $wardList.html(
          `<li class="label">${this.getLocationPlaceholder(
            "select_city_first",
            "Please select city first",
          )}</li>`,
        );
        $wardInput.val("00").trigger("change");
        this.setWardSelectDisabled(true);
        return;
      }

      const selectedProvince = provinces.find(
        (province) => String(province.id) === String(provinceId),
      );
      const wardOptions = selectedProvince ? selectedProvince.wards : [];
      $wardList.html(buildWardOptions(wardOptions));
      $wardInput.val("00").trigger("change");
      this.setWardSelectDisabled(false);
    };

    $provinceList.html(
      provinces
        .map(
          (province) =>
            `<li class="option" data-value="${province.id}">${province.name}</li>`,
        )
        .join(""),
    );

    updateWardsByProvince($provinceInput.val());

    $provinceInput.off("change.location");
    $provinceInput.on("change.location", (e) => {
      updateWardsByProvince($(e.target).val());
    });
  }

  setupDateTimeField() {
    this.$form
      .off("click.bookingTimeOpen", ".booking-time-input-group")
      .on("click.bookingTimeOpen", ".booking-time-input-group", (event) => {
        if (
          $(event.target).closest(".booking-time-trigger-ignore").length > 0
        ) {
          return;
        }

        const $group = $(event.currentTarget);
        const $input = $group.find(".booking-time-input").first();

        if ($group.hasClass("is-date-locked")) {
          event.preventDefault();
          return;
        }

        if (!$input.length) {
          return;
        }

        // Every CRM form with a type field: the server requires the type, and on the
        // integrated form it decides single slot vs follow-up cards.
        const $appointmentType = this.$form.find(
          'input[name="booking-appointment-type"]',
        );
        const missingAppointmentType =
          $appointmentType.length > 0 &&
          !($appointmentType.val() || "").trim();

        if (!this.currentHospitalKey) {
          event.preventDefault();
          if (missingAppointmentType) {
            this.showError(
              this.bookingTimeSelector,
              this.hospitalAndTypeRequiredMessage,
            );
          } else {
            this.showHospitalSelectionError();
          }
          return;
        }

        if (missingAppointmentType) {
          event.preventDefault();
          this.showError(
            this.bookingTimeSelector,
            this.appointmentTypeRequiredMessage,
          );
          return;
        }

        if (
          this.usesBookingTimeRelationLoading &&
          (this.bookingTimeLoading || $group.hasClass("is-loading"))
        ) {
          event.preventDefault();
          this.showError(".booking-time-input", this.bookingTimeLoadingMessage);
          return;
        }

        const manager = this.activeManager || this.dateTimeManager;
        if (!manager || typeof manager.open !== "function") {
          return;
        }

        manager.open($input);
      });

    // Drop the "select appointment type first" hint once a type is picked.
    this.$form
      .off("change.bookingTimeAppointmentType", 'input[name="booking-appointment-type"]')
      .on("change.bookingTimeAppointmentType", 'input[name="booking-appointment-type"]', (event) => {
        if (
          ($(event.currentTarget).val() || "").trim() &&
          !this.$form.find(this.bookingTimeSelector).val()
        ) {
          this.clearBookingTimeError();
        }
      });
  }

  initAppointmentTypes() {
    if (!this.isActive) {
      return;
    }

    if (
      typeof HM_Variables === "undefined" ||
      !Array.isArray(HM_Variables.appointment_types)
    ) {
      return;
    }

    const $list = this.$form.find("#appointment-type-select-list");
    if (!$list.length) {
      return;
    }

    const types = HM_Variables.appointment_types;
    const lang = (HM_Variables.language || "vi").toLowerCase();
    const labelKey = lang === "en" ? "label_en" : "label_vi";

    const optionsHtml = types
      .map((type) => {
        const label = type[labelKey] || type.label_vi || type.label_en || "";
        return `<li class="option" data-value="${type.value}">${label}</li>`;
      })
      .join("");

    $list.html(optionsHtml);
  }

  selectManagerForHospital(hospitalData) {
    const noCrm = !!(hospitalData && hospitalData.no_crm_integrated);
    const slot = this.managers && this.managers.slot;
    const crm = this.managers && this.managers.crm;

    let next = null;
    if (noCrm) {
      next = slot || crm;
    } else {
      next = crm || slot;
    }

    const inactive = next === slot ? crm : slot;
    if (
      inactive &&
      inactive !== next &&
      typeof inactive.setHospital === "function"
    ) {
      inactive.setHospital(null);
    }

    this.activeManager = next;
    this.dateTimeManager = next;

    if (this.usesBookingTimeRelationLoading && next !== crm && next === slot) {
      this.bookingTimeLoadingMessage = this.bookingTimeLoadingMessageLegacy;
    } else {
      this.bookingTimeLoadingMessage = this.bookingTimeLoadingMessageCrm;
    }

    if (this.usesBookingTimeRelationLoading && next !== crm && next !== slot) {
      this.setBookingTimeLoading(false);
    }
  }

  bindCommonEvents() {
    this.$form.off("submit.bookingForm");
    this.$form.on("submit.bookingForm", async (e) => {
      e.preventDefault();
      await this.ValidateForm();
    });

    this.$form
      .off("change.bookingAgreement", 'input[name="agreement"]')
      .on("change.bookingAgreement", 'input[name="agreement"]', (e) => {
        if ($(e.target).is(":checked")) {
          this.hideAgreementError();
        }
        this.validateFormRealtime();
      });

    this.$form
      .off(
        "input.bookingRealtime change.bookingRealtime",
        'input[name="name"], input[name="email"], input[name="phone"], input[name="dia_chi_khach_hang"], input[name="birthday"], input[name="booking-hospital"], input[name="booking-specialty"], input[name="booking-package"], input[name="booking-appointment-type"], input[name="booking-time"], input[name="id_tinh_thanh"], input[name="id_phuong_xa"], textarea[name="additional-information"]',
      )
      .on(
        "input.bookingRealtime change.bookingRealtime",
        'input[name="name"], input[name="email"], input[name="phone"], input[name="dia_chi_khach_hang"], input[name="birthday"], input[name="booking-hospital"], input[name="booking-specialty"], input[name="booking-package"], input[name="booking-appointment-type"], input[name="booking-time"], input[name="id_tinh_thanh"], input[name="id_phuong_xa"], textarea[name="additional-information"]',
        () => {
          this.validateFormRealtime();
        },
      );

    this.$form
      .off(
        "blur.bookingField",
        'input[name="name"], input[name="email"], input[name="phone"], input[name="dia_chi_khach_hang"], textarea[name="additional-information"]',
      )
      .on(
        "blur.bookingField",
        'input[name="name"], input[name="email"], input[name="phone"], input[name="dia_chi_khach_hang"], textarea[name="additional-information"], input[name="cccd"]',
        (e) => {
          this.validateFieldRealtime(e.target);
        },
      );
  }

  async ValidateForm() {
    let isValid = true;

    this.$form.find(".input-group").removeClass("err");
    this.$form.find(".err-msg").text("");
    this.hideFormError();

    const formData = {
      name: this.getInputValue("name"),
      email: this.getInputValue("email"),
      phone: this.getInputValue("phone"),
      dia_chi_khach_hang: this.getInputValue("dia_chi_khach_hang"),
      id_tinh_thanh: this.getInputValue("id_tinh_thanh"),
      id_phuong_xa: this.getInputValue("id_phuong_xa"),
      birthday: this.getInputValue("birthday"),
      cccd: this.getInputValue("cccd"),
      hospital: this.getInputValue("booking-hospital"),
      specialty: this.getInputValue("booking-specialty"),
      package: this.getInputValue("booking-package"),
      booking_time: this.getInputValue("booking-time"),
      additional_info: this.getInputValue("additional-information"),
      agreement: this.$form.find('input[name="agreement"]').is(":checked"),
    };

    if (!formData.name) {
      this.showError(
        'input[name="name"]',
        HM_Variables.frontend_lang.validation.name_required,
      );
      isValid = false;
    } else if (formData.name.length > 64) {
      this.showError(
        'input[name="name"]',
        HM_Variables.frontend_lang.validation.name_too_long,
      );
      isValid = false;
    } else if (!validationRegex.nameRegex.test(formData.name)) {
      this.showError(
        'input[name="name"]',
        HM_Variables.frontend_lang.validation.name_invalid,
      );
      isValid = false;
    }

    if (!formData.email) {
      this.showError(
        'input[name="email"]',
        HM_Variables.frontend_lang.validation.email_required,
      );
      isValid = false;
    } else if (formData.email.length > 100) {
      this.showError(
        'input[name="email"]',
        HM_Variables.frontend_lang.validation.email_too_long,
      );
      isValid = false;
    } else if (!validationRegex.emailRegex.test(formData.email)) {
      this.showError(
        'input[name="email"]',
        HM_Variables.frontend_lang.validation.email_invalid,
      );
      isValid = false;
    }

    if (!formData.phone) {
      this.showError(
        'input[name="phone"]',
        HM_Variables.frontend_lang.validation.phone_required,
      );
      isValid = false;
    } else if (!validationRegex.phoneRegex.test(formData.phone)) {
      this.showError(
        'input[name="phone"]',
        HM_Variables.frontend_lang.validation.phone_invalid,
      );
      isValid = false;
    }

    if (formData.dia_chi_khach_hang.length > 200) {
      this.showError(
        'input[name="dia_chi_khach_hang"]',
        HM_Variables.frontend_lang.validation.address_too_long,
      );
      isValid = false;
    } else if (
      formData.dia_chi_khach_hang &&
      !validationRegex.addressRegex.test(formData.dia_chi_khach_hang)
    ) {
      this.showError(
        'input[name="dia_chi_khach_hang"]',
        HM_Variables.frontend_lang.validation.address_invalid,
      );
      isValid = false;
    }

    if (formData.additional_info && formData.additional_info.length > 200) {
      this.showError(
        '[name="additional-information"]',
        HM_Variables.frontend_lang.validation.additional_info_too_long,
      );
      isValid = false;
    }

    if (formData.cccd && !validationRegex.cccdRegex.test(formData.cccd)) {
      this.showError(
        'input[name="cccd"]',
        HM_Variables.frontend_lang.validation.cccd_invalid,
      );
      isValid = false;
    }

    if (!formData.birthday) {
      this.showError(
        'input[name="birthday"]',
        HM_Variables.frontend_lang.validation.birthday_required,
      );
      isValid = false;
    }

    if (!formData.hospital) {
      this.showError(
        'input[name="booking-hospital"]',
        HM_Variables.frontend_lang.validation.hospital_required,
      );
      isValid = false;
    }

    // Specialty and package are optional for all forms

    // package optional

    if (!formData.booking_time) {
      this.showError(
        'input[name="booking-time"]',
        HM_Variables.frontend_lang.validation.booking_time_required,
      );
      isValid = false;
    }

    if (!formData.agreement) {
      this.showAgreementError(
        HM_Variables.frontend_lang.validation.agreement_required,
      );
      isValid = false;
    }

    if (isValid) {
      if (
        this.requiresOtpForSelectedHospital() &&
        $("#otp-verify-modal").length &&
        window.HM_OTP_FLOW
      ) {
        await this.startOtpVerification();
      } else {
        await this.submitForm();
      }
    }

    return isValid;
  }

  /**
   * OTP is required only for CRM-integrated hospitals.
   * no_crm_integrated hospitals submit directly (HubSpot).
   * Danh Y QR session skips OTP — PHP verifies phone/email server-side.
   */
  requiresOtpForSelectedHospital() {
    if (this.$form?.attr("data-danhy-logged-in") === "1") {
      return false;
    }

    const hospitalRef = this.getInputValue("booking-hospital");
    let hospital = this.resolveHospitalData(hospitalRef);

    if (!hospital && typeof packageBookingFormVars !== "undefined") {
      hospital = packageBookingFormVars.hospital || null;
    }
    if (!hospital && typeof hospitalBookingFormVars !== "undefined") {
      hospital = hospitalBookingFormVars.hospital || null;
    }

    if (!hospital) {
      return false;
    }

    return !hospital.no_crm_integrated;
  }

  getOtpVerificationToken() {
    return this.$form.find('[name="otp_verification_token"]').val() || "";
  }

  setOtpVerificationToken(token) {
    if (window.HM_OTP_FLOW) {
      window.HM_OTP_FLOW.setTokenOnForm(this.$form, token);
      return;
    }

    let $input = this.$form.find('[name="otp_verification_token"]');
    if (!$input.length) {
      this.$form.append(
        '<input type="hidden" name="otp_verification_token" id="otp-verification-token" value="">',
      );
      $input = this.$form.find('[name="otp_verification_token"]');
    }
    $input.val(token || "");
  }

  clearOtpVerificationToken() {
    if (window.HM_OTP_FLOW) {
      window.HM_OTP_FLOW.clearTokenOnForm(this.$form);
      return;
    }
    this.$form.find('[name="otp_verification_token"]').val("");
  }

  hasValidOtpVerification(phone) {
    const token = this.getOtpVerificationToken();
    if (!token) {
      return false;
    }
    return OtpVerifyFlow.isTokenValidForPhone(token, phone);
  }

  async startOtpVerification() {
    const phone = this.getInputValue("phone");
    if (!phone) return;

    if (this.hasValidOtpVerification(phone)) {
      this.submitForm();
      return;
    }

    this.clearOtpVerificationToken();

    const otpPrecheck = await window.HM_OTP_FLOW?.checkDailySendBlocked(phone);
    if (otpPrecheck?.blocked) {
      this.showFormError(otpPrecheck.message);
      return;
    }

    window.HM_OTP_FLOW?.open({
      phone,
      $bookingForm: this.$form,
      cachedOtpStatus: otpPrecheck?.status || null,
      onVerified: async (otpVerificationToken) => {
        this.setOtpVerificationToken(otpVerificationToken);
        await this.submitForm({ fromOtpFlow: true });
      },
    });
  }

  validateFormRealtime() {
    const $submitBtn = this.$form.find('button[type="submit"]');

    const formData = {
      name: this.getInputValue("name"),
      email: this.getInputValue("email"),
      phone: this.getInputValue("phone"),
      dia_chi_khach_hang: this.getInputValue("dia_chi_khach_hang"),
      birthday: this.getInputValue("birthday"),
      hospital: this.getInputValue("booking-hospital"),
      specialty: this.getInputValue("booking-specialty"),
      package: this.getInputValue("booking-package"),
      booking_time: this.getInputValue("booking-time"),
      agreement: this.$form.find('input[name="agreement"]').is(":checked"),
    };

    const isFormValid =
      formData.name &&
      formData.email &&
      formData.phone &&
      formData.birthday &&
      formData.hospital &&
      formData.booking_time &&
      formData.agreement;

    if (isFormValid) {
      $submitBtn.prop("disabled", false).removeClass("disabled");
    } else {
      $submitBtn.prop("disabled", true).addClass("disabled");
    }
  }

  validateFieldRealtime(inputElement) {
    const $input = $(inputElement);
    const fieldName = $input.attr("name");
    const rawValue = $input.val();
    const fieldValue = typeof rawValue === "string" ? rawValue.trim() : "";

    const $inputGroup = $input.closest(".input-group");
    $inputGroup.removeClass("err");
    $inputGroup.find(".err-msg").text("");

    switch (fieldName) {
      case "name":
        if (fieldValue && fieldValue.length > 64) {
          this.showError(
            'input[name="name"]',
            HM_Variables.frontend_lang.validation.name_too_long,
          );
        } else if (fieldValue && !validationRegex.nameRegex.test(fieldValue)) {
          this.showError(
            'input[name="name"]',
            HM_Variables.frontend_lang.validation.name_invalid,
          );
        }
        break;

      case "email":
        if (fieldValue && fieldValue.length > 100) {
          this.showError(
            'input[name="email"]',
            HM_Variables.frontend_lang.validation.email_too_long,
          );
        } else if (fieldValue && !validationRegex.emailRegex.test(fieldValue)) {
          this.showError(
            'input[name="email"]',
            HM_Variables.frontend_lang.validation.email_invalid,
          );
        }
        break;

      case "phone":
        if (fieldValue && !validationRegex.phoneRegex.test(fieldValue)) {
          this.showError(
            'input[name="phone"]',
            HM_Variables.frontend_lang.validation.phone_invalid,
          );
        }
        break;

      case "dia_chi_khach_hang":
        if (fieldValue && fieldValue.length > 200) {
          this.showError(
            'input[name="dia_chi_khach_hang"]',
            HM_Variables.frontend_lang.validation.address_too_long,
          );
        } else if (
          fieldValue &&
          !validationRegex.addressRegex.test(fieldValue)
        ) {
          this.showError(
            'input[name="dia_chi_khach_hang"]',
            HM_Variables.frontend_lang.validation.address_invalid,
          );
        }
        break;

      case "additional-information":
        if (fieldValue && fieldValue.length > 200) {
          this.showError(
            '[name="additional-information"]',
            HM_Variables.frontend_lang.validation.additional_info_too_long,
          );
        }
        break;

      case "cccd":
        if (fieldValue && !validationRegex.cccdRegex.test(fieldValue)) {
          this.showError(
            'input[name="cccd"]',
            HM_Variables.frontend_lang.validation.cccd_invalid,
          );
        }
        break;

      default:
        break;
    }
  }

  showError(inputSelector, message) {
    const $input = this.$form.find(inputSelector);
    const $inputGroup = $input.closest(".input-group");

    $inputGroup.addClass("err");
    $inputGroup.find(".err-msg").text(message);
  }

  clearFieldError(inputSelector) {
    const $input = this.$form.find(inputSelector);
    if (!$input.length) {
      return;
    }

    const $inputGroup = $input.closest(".input-group");
    $inputGroup.removeClass("err");
    $inputGroup.find(".err-msg").text("");
  }

  clearBookingTimeError() {
    this.clearFieldError(this.bookingTimeSelector);
  }

  showHospitalSelectionError() {
    this.showError(this.bookingTimeSelector, this.hospitalRequiredMessage);
  }

  setBookingTimeLoading(isLoading) {
    if (!this.usesBookingTimeRelationLoading) {
      return;
    }

    const loading = Boolean(isLoading);
    this.bookingTimeLoading = loading;

    const $group = this.$form.find(".booking-time-input-group");
    if (!$group.length) {
      return;
    }

    const $visibleInput = $group.find(".booking-time-input").first();

    $group.toggleClass("is-loading", loading);

    if (!loading) {
      this.clearFieldError(".booking-time-input");
      this.clearFieldError("#booking-time-input");
    }

    if ($visibleInput.length) {
      $visibleInput.prop("readonly", true);
    }
  }

  resetBookingTimeValue() {
    const fields = [
      this.bookingTimeSelector,
      'input[name="booking-time-render"]',
    ];
    fields.forEach((selector) => {
      const $input = this.$form.find(selector);
      if ($input.length && $input.val()) {
        $input.val("");
        $input.trigger("change");
      }
    });
  }

  handleHospitalSelection(hospital) {
    const resolvedHospital = this.resolveHospitalData(hospital);
    let nextKey = null;

    if (resolvedHospital && resolvedHospital.hospital_shortname) {
      nextKey = resolvedHospital.hospital_shortname;
    } else if (typeof hospital === "string") {
      nextKey = hospital;
    }

    if (typeof nextKey === "string") {
      nextKey = nextKey.trim().toLowerCase();
      if (!nextKey.length) {
        nextKey = null;
      }
    } else {
      nextKey = null;
    }

    const previousKey = this.currentHospitalKey;
    const hospitalChanged = previousKey !== nextKey;

    if (hospitalChanged) {
      this.resetBookingTimeValue();
      this.resetCrmHiddenFields();
    }

    this.currentHospitalKey = nextKey;

    this.selectManagerForHospital(resolvedHospital);

    if (
      this.activeManager &&
      typeof this.activeManager.setHospital === "function"
    ) {
      this.activeManager.setHospital(nextKey, resolvedHospital);
    }

    if (!nextKey && this.usesBookingTimeRelationLoading) {
      this.setBookingTimeLoading(false);
    }

    if (nextKey) {
      this.clearBookingTimeError();
    }

    this.updateDisclaimerHotline(resolvedHospital);
  }

  bindModalRelationLoadingListeners() {
    if (!this.usesBookingTimeRelationLoading || !this.isActive) {
      return;
    }

    const crmMgr = this.managers && this.managers.crm;
    if (crmMgr && typeof crmMgr.setSpecialtiesLoadingListener === "function") {
      crmMgr.setSpecialtiesLoadingListener((isLoading) => {
        this.bookingTimeLoadingMessage = this.bookingTimeLoadingMessageCrm;
        this.setBookingTimeLoading(isLoading);
      });
    }

    const slotMgr = this.managers && this.managers.slot;
    if (slotMgr && typeof slotMgr.setRelationLoadingListener === "function") {
      slotMgr.setRelationLoadingListener((isLoading) => {
        this.bookingTimeLoadingMessage = this.bookingTimeLoadingMessageLegacy;
        this.setBookingTimeLoading(isLoading);
      });
    }
  }

  resetCrmHiddenFields() {
    if (!this.isActive) {
      return;
    }
    this.clearCrmSlotFields();
    this.clearFollowUpListState();
  }

  supportsFollowUpMulti() {
    // Any CRM form that renders the follow-up block opts in; the package form never does.
    return (
      this.isActive &&
      this.formType !== "package" &&
      this.$form.find(".followup-appointments").length > 0
    );
  }

  clearCrmSlotFields() {
    [
      "booking-specialty",
      "booking-specialty-id",
      "booking-specialty-name",
      "booking-package",
      "booking-timeslot-id",
      "booking-doctor-id",
      "booking-doctor-name",
    ].forEach((name) => {
      this.$form.find(`input[name="${name}"]`).val("");
    });
  }

  initFollowUpAppointments() {
    if (!this.supportsFollowUpMulti()) {
      return;
    }

    this.followUpAppointments = [];
    this.followUpMalichhenBase = "";
    this.followUpLastAppointmentType = "";
    this.recoveryActive = false;
    this.recoverySubmitting = false;
    this.recoveryAppointments = [];
    this.recoveryIssueSlots = {};
    this.recoveryIssueReasons = {};
    this.recoveryBlockedSlots = {};

    const crm = this.managers && this.managers.crm;
    if (crm && typeof crm.setFollowUpBridge === "function") {
      crm.setFollowUpBridge({
        isFollowUpForm: ($form) =>
          $form.is(this.$form) &&
          String($form.find('input[name="booking-appointment-type"]').val() || "") ===
            "11129",
        getAppointments: () =>
          this.recoveryActive && Array.isArray(this.recoveryAppointments)
            ? this.recoveryAppointments
            : this.followUpAppointments,
        commit: (selection, context) =>
          this.commitFollowUpAppointment(selection, context),
      });
    }

    this.$form
      .off("change.followUpType", 'input[name="booking-appointment-type"]')
      .on("change.followUpType", 'input[name="booking-appointment-type"]', () => {
        this.handleFollowUpAppointmentTypeChange();
      });

    this.$form
      .off("click.followUpAdd", ".followup-appointments__add")
      .on("click.followUpAdd", ".followup-appointments__add", (event) => {
        event.preventDefault();
        this.openFollowUpModal("add");
      });

    this.$form
      .off("click.followUpEdit", ".followup-appointment-card__open")
      .on("click.followUpEdit", ".followup-appointment-card__open", (event) => {
        event.preventDefault();
        const index = Number(
          $(event.currentTarget)
            .closest(".followup-appointment-card")
            .attr("data-index"),
        );
        this.openFollowUpModal("edit", index);
      });

    this.$form
      .off("click.followUpRemove", ".followup-appointment-card__remove")
      .on(
        "click.followUpRemove",
        ".followup-appointment-card__remove",
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          const index = Number(
            $(event.currentTarget)
              .closest(".followup-appointment-card")
              .attr("data-index"),
          );
          this.removeFollowUpAppointment(index);
        },
      );
  }

  handleFollowUpAppointmentTypeChange() {
    if (!this.supportsFollowUpMulti()) {
      return;
    }
    const next = String(
      this.$form.find('input[name="booking-appointment-type"]').val() || "",
    );
    const prev = this.followUpLastAppointmentType || "";
    this.followUpLastAppointmentType = next;
    // First pick: nothing to clear yet (the time can't be chosen before a type),
    // and clearing here would drop a specialty prefilled from the URL.
    if (prev === next || !prev) {
      return;
    }
    // Recovery owns the appointments until it is submitted or canceled.
    if (this.recoveryActive) {
      return;
    }
    this.resetBookingTimeValue();
    this.resetCrmHiddenFields();
    const crm = this.managers && this.managers.crm;
    if (crm && typeof crm.reset === "function") {
      crm.reset();
    }
    this.validateFormRealtime();
  }

  clearFollowUpListState() {
    if (!this.supportsFollowUpMulti()) {
      return;
    }
    this.followUpAppointments = [];
    this.followUpMalichhenBase = "";
    this.syncFollowUpHiddenJson();
    this.renderFollowUpAppointments();
  }

  createFollowUpMalichhenBase() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const serial = String(now.getTime() % 1000).padStart(3, "0");
    return `WEB-${year}${month}${day}-${serial}`;
  }

  nextFollowUpMalichhenSuffix() {
    const base = this.followUpMalichhenBase;
    const used = new Set(
      this.followUpAppointments.map((item) => {
        const code = item && item.malichhen ? item.malichhen : "";
        if (code === base) {
          return "";
        }
        if (code === `${base}-B`) {
          return "-B";
        }
        if (code === `${base}-C`) {
          return "-C";
        }
        return null;
      }),
    );
    return ["", "-B", "-C"].find((suffix) => !used.has(suffix)) || "";
  }

  formatFollowUpVisitDate(iso) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${date.getFullYear()}`;
  }

  formatFollowUpClockTime(iso) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return date.toLocaleTimeString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  followUpText(key, fallback) {
    return HM_Variables?.frontend_lang?.[key] || fallback;
  }

  escapeFollowUpText(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  syncFollowUpHiddenJson() {
    const $field = this.$form.find('input[name="booking-appointments"]');
    if (!$field.length) {
      return;
    }
    // Names/time are display-only (patient email); CRM keeps the first three keys.
    const payload = (this.followUpAppointments || []).map((item) => ({
      timeslotid: Number(item.timeslotid),
      bookingstatus: item.bookingstatus || "waiting-confirm",
      malichhen: item.malichhen,
      specialtyName: item.specialtyName || "",
      doctorName: item.doctorName || "",
      startTime: item.startTime || "",
    }));
    $field.val(payload.length ? JSON.stringify(payload) : "");
  }

  syncFirstFollowUpHiddenFields() {
    const first = (this.followUpAppointments || [])[0];
    if (!first) {
      this.clearCrmSlotFields();
      this.resetBookingTimeValue();
      return;
    }
    this.$form
      .find('input[name="booking-time"]')
      .val(first.startTime || "")
      .trigger("change");
    this.$form.find('input[name="booking-timeslot-id"]').val(first.timeslotid || "");
    this.$form.find('input[name="booking-doctor-name"]').val(first.doctorName || "");
    this.$form.find('input[name="booking-doctor-id"]').val(first.doctorId || "");
    this.$form.find('input[name="booking-specialty"]').val(first.specialtyId || "");
    this.$form
      .find('input[name="booking-specialty-id"]')
      .val(first.specialtyId || "");
    this.$form
      .find('input[name="booking-specialty-name"]')
      .val(first.specialtyName || "");
    this.$form
      .find('input[name="booking-time-render"]')
      .val(this.formatFollowUpVisitDate(first.startTime));
  }

  sortAppointmentsByTime(list) {
    if (!Array.isArray(list)) {
      return;
    }
    list.sort((a, b) => {
      const left = new Date(a && a.startTime).getTime();
      const right = new Date(b && b.startTime).getTime();
      if (Number.isNaN(left) || Number.isNaN(right)) {
        return 0;
      }
      return left - right;
    });
  }

  sortFollowUpAppointments() {
    this.sortAppointmentsByTime(this.followUpAppointments);
  }

  commitFollowUpAppointment(selection, context = {}) {
    if (!this.supportsFollowUpMulti()) {
      return false;
    }
    const mode = context.mode || "create";
    const specialtyId = selection.specialtyId ? String(selection.specialtyId) : "";
    const timeslotid = parseInt(selection.timeslotid, 10);
    if (!specialtyId || !Number.isFinite(timeslotid)) {
      return false;
    }

    const details = {
      timeslotid,
      bookingstatus: "waiting-confirm",
      specialtyId,
      specialtyName: selection.specialtyName || "",
      doctorId: selection.doctorId ? String(selection.doctorId) : "",
      doctorName: selection.doctorName || "",
      startTime: selection.startTime || "",
    };

    if (mode === "edit") {
      const index = Number(context.editIndex);
      const current = this.followUpAppointments[index];
      if (!current) {
        return false;
      }
      this.followUpAppointments[index] = {
        ...current,
        ...details,
        malichhen: current.malichhen,
      };
    } else if (
      mode === "add" ||
      (mode === "create" && this.followUpAppointments.length === 0)
    ) {
      if (this.followUpAppointments.length >= 3) {
        return false;
      }
      if (!this.followUpMalichhenBase) {
        this.followUpMalichhenBase = this.createFollowUpMalichhenBase();
      }
      this.followUpAppointments.push({
        ...details,
        malichhen: `${this.followUpMalichhenBase}${this.nextFollowUpMalichhenSuffix()}`,
      });
    } else if (mode === "recover") {
      return this.commitRecoveryAppointment(selection, context);
    } else {
      return false;
    }

    this.sortFollowUpAppointments();
    this.syncFollowUpHiddenJson();
    this.syncFirstFollowUpHiddenFields();
    this.renderFollowUpAppointments();
    return true;
  }

  removeFollowUpAppointment(index) {
    if (
      !Number.isInteger(index) ||
      index < 0 ||
      !this.followUpAppointments[index]
    ) {
      return;
    }
    this.followUpAppointments.splice(index, 1);
    if (!this.followUpAppointments.length) {
      this.followUpMalichhenBase = "";
      // Last card gone: the CRM modal must not reopen with the old date / specialty / doctor.
      const crm = this.managers && this.managers.crm;
      if (crm && typeof crm.reset === "function") {
        crm.reset();
      }
    }
    this.syncFollowUpHiddenJson();
    this.syncFirstFollowUpHiddenFields();
    this.renderFollowUpAppointments();
  }

  openFollowUpModal(mode, editIndex = null, extra = {}) {
    const manager = this.managers && this.managers.crm;
    const $input = this.$form.find(".booking-time-input").first();
    if (!manager || typeof manager.open !== "function" || !$input.length) {
      return;
    }
    manager.open($input, { mode, editIndex, ...extra });
  }

  recoveryText(key, fallback) {
    return this.followUpText(key, fallback);
  }

  // Every slot the CRM reported full for an appointment, across recovery rounds.
  addRecoveryBlockedSlot(code, timeslotid, card = null) {
    if (!code || timeslotid === undefined || timeslotid === null || timeslotid === "") {
      return;
    }
    if (!this.recoveryBlockedSlots) {
      this.recoveryBlockedSlots = {};
    }
    if (!this.recoveryBlockedSlots[code]) {
      this.recoveryBlockedSlots[code] = new Map();
    }
    const id = String(timeslotid);
    const known = this.recoveryBlockedSlots[code].get(id);
    const hasDetails =
      card &&
      String(card.timeslotid) === id &&
      card.startTime &&
      card.specialtyId &&
      card.doctorId;
    if (known && known.thoigian_batdau && !hasDetails) {
      return;
    }
    this.recoveryBlockedSlots[code].set(
      id,
      hasDetails
        ? {
            id,
            thoigian_batdau: card.startTime,
            chuyenkhoa_id: String(card.specialtyId),
            manhanvien: String(card.doctorId),
            tennhanvien: card.doctorName || "",
          }
        : known || { id },
    );
  }

  getRecoveryBlockedSlots(code) {
    const map = this.recoveryBlockedSlots && this.recoveryBlockedSlots[code];
    return map ? Array.from(map.keys()) : [];
  }

  getRecoveryBlockedSlotDetails(code) {
    const map = this.recoveryBlockedSlots && this.recoveryBlockedSlots[code];
    return map ? Array.from(map.values()).filter((slot) => slot.thoigian_batdau) : [];
  }

  recoveryItemNeedsAction(item) {
    if (!item || item.pendingCancel) {
      return false;
    }
    const original = this.recoveryIssueSlots[item.malichhen];
    if (!original) {
      return false;
    }
    return String(item.timeslotid) === String(original);
  }

  recoveryItemWasUpdated(item) {
    if (!item || item.pendingCancel || !item.malichhen) {
      return false;
    }
    const original = this.recoveryIssueSlots[item.malichhen];
    if (original === undefined || original === null || original === "") {
      return false;
    }
    return String(item.timeslotid) !== String(original);
  }

  isRecoveryConfirmProcessing() {
    return $("#booking-recovery-confirm-modal").hasClass("is-processing");
  }

  setRecoveryConfirmError(message = "") {
    const $error = $("#booking-recovery-confirm-error");
    if (!$error.length) {
      return;
    }
    const text = String(message || "").trim();
    // Expired holds cancel without the confirm modal, so report on the recovery modal.
    if (!$("#booking-recovery-confirm-modal").hasClass("show-modal")) {
      const $recoveryError = $("#booking-recovery-error");
      if (text) {
        $recoveryError.text(text).removeAttr("hidden");
        if ($recoveryError[0]) {
          $recoveryError[0].scrollIntoView({ block: "center" });
        }
      } else {
        $recoveryError.text("").attr("hidden", "hidden");
      }
      return;
    }
    if (text) {
      $error.text(text).removeAttr("hidden");
    } else {
      $error.text("").attr("hidden", "hidden");
    }
  }

  setRecoveryConfirmProcessing(isProcessing) {
    const $modal = $("#booking-recovery-confirm-modal");
    const $yes = $("#booking-recovery-confirm-yes");
    const $no = $("#booking-recovery-confirm-no");
    const $close = $("#booking-recovery-confirm-close");
    const $cancelAll = $("#booking-recovery-cancel-all");
    // The button doing the cancel: confirm "yes", or cancel-all when the hold expired.
    const $busy = $modal.hasClass("show-modal") ? $yes : $cancelAll;
    if (!$modal.length) {
      return;
    }

    if (isProcessing) {
      if (!$busy.data("recoveryOriginalLabel")) {
        $busy.data("recoveryOriginalLabel", $busy.text());
      }
      $modal.addClass("is-processing").attr("aria-busy", "true");
      $busy.text(
        this.recoveryText("recovery_cancel_processing", "Đang huỷ..."),
      );
      $yes.add($no).add($close).add($cancelAll).prop("disabled", true);
      return;
    }

    $modal.removeClass("is-processing").attr("aria-busy", "false");
    $yes.add($no).add($close).add($cancelAll).prop("disabled", false);
    $yes.add($cancelAll).each((_index, button) => {
      const $button = $(button);
      const original = $button.data("recoveryOriginalLabel");
      if (original) {
        $button.text(original).removeData("recoveryOriginalLabel");
      }
    });
  }

  openRecoveryConfirmModal() {
    const $modal = $("#booking-recovery-confirm-modal");
    if (!$modal.length) {
      return;
    }
    if (this.isRecoveryConfirmProcessing()) {
      return;
    }
    if (this.recoveryExpired) {
      // Nothing left to undo once the hold expired: cancel straight away.
      this.setRecoveryConfirmError("");
      this.setRecoveryConfirmProcessing(true);
      this.cancelAllFollowUpRecovery();
      return;
    }
    this.setRecoveryConfirmProcessing(false);
    this.setRecoveryConfirmError("");
    $modal.addClass("show-modal").attr("aria-hidden", "false");
    $modal.find(".modal-dialog").addClass("active");
  }

  closeRecoveryConfirmModal() {
    const $modal = $("#booking-recovery-confirm-modal");
    this.setRecoveryConfirmProcessing(false);
    this.setRecoveryConfirmError("");
    $modal.removeClass("show-modal").attr("aria-hidden", "true");
    $modal.find(".modal-dialog").removeClass("active");
  }

  recoveryCanSubmit() {
    if (!this.recoveryActive || this.recoveryExpired) {
      return false;
    }
    return (this.recoveryAppointments || []).every(
      (item) => !this.recoveryItemNeedsAction(item),
    );
  }

  sortRecoveryAppointments() {
    this.sortAppointmentsByTime(this.recoveryAppointments);
  }

  commitRecoveryAppointment(selection, context = {}) {
    const index = Number(context.editIndex);
    const current = this.recoveryAppointments[index];
    const timeslotid = parseInt(selection.timeslotid, 10);
    if (!current || !Number.isFinite(timeslotid)) {
      return false;
    }
    if (
      String(timeslotid) === String(this.recoveryIssueSlots[current.malichhen] || "") ||
      this.getRecoveryBlockedSlots(current.malichhen).includes(String(timeslotid))
    ) {
      return false;
    }
    this.recoveryAppointments[index] = {
      ...current,
      timeslotid,
      bookingstatus: "waiting-confirm",
      pendingCancel: false,
      specialtyId: selection.specialtyId ? String(selection.specialtyId) : "",
      specialtyName: selection.specialtyName || "",
      doctorId: selection.doctorId ? String(selection.doctorId) : "",
      doctorName: selection.doctorName || "",
      startTime: selection.startTime || "",
      malichhen: current.malichhen,
    };
    this.sortRecoveryAppointments();
    this.renderRecoveryAppointments();
    return true;
  }

  bindRecoveryEvents() {
    if (this.recoveryEventsBound) {
      return;
    }
    this.recoveryEventsBound = true;
    $(document)
      .on("click.followUpRecovery", "#booking-recovery-submit", (event) => {
        event.preventDefault();
        this.submitFollowUpRecovery();
      })
      .on("click.followUpRecovery", "#booking-recovery-cancel-all", (event) => {
        event.preventDefault();
        if (this.recoverySubmitting) {
          return;
        }
        this.openRecoveryConfirmModal();
      })
      .on(
        "click.followUpRecovery",
        "#booking-recovery-confirm-no, #booking-recovery-confirm-close",
        (event) => {
          event.preventDefault();
          if (this.isRecoveryConfirmProcessing()) {
            return;
          }
          this.closeRecoveryConfirmModal();
        },
      )
      .on("click.followUpRecovery", "#booking-recovery-confirm-yes", (event) => {
        event.preventDefault();
        if (this.isRecoveryConfirmProcessing()) {
          return;
        }
        this.setRecoveryConfirmError("");
        this.setRecoveryConfirmProcessing(true);
        this.cancelAllFollowUpRecovery();
      })
      .on(
        "click.followUpRecovery",
        "#booking-recovery-confirm-modal .modal-overlay",
        (event) => {
          event.preventDefault();
          if (this.isRecoveryConfirmProcessing()) {
            return;
          }
          this.closeRecoveryConfirmModal();
        },
      )
      .on("click.followUpRecovery", ".booking-recovery-card__update", (event) => {
        event.preventDefault();
        if (this.recoveryExpired || this.recoverySubmitting) {
          return;
        }
        const index = Number($(event.currentTarget).closest("[data-index]").attr("data-index"));
        const item = this.recoveryAppointments[index];
        if (!item || item.cancelLocked) {
          return;
        }
        const blocked = this.getRecoveryBlockedSlots(item.malichhen);
        this.openFollowUpModal("recover", index, {
          overbookedSlotIds: blocked.length
            ? blocked
            : [String(this.recoveryIssueSlots[item.malichhen] || item.timeslotid)],
          overbookedSlots: this.getRecoveryBlockedSlotDetails(item.malichhen),
          overbookedReason:
            this.recoveryIssueReasons[item.malichhen] ||
            this.recoveryText(
              "recovery_overbooked",
              "Lịch đã đầy hoặc bị khóa",
            ),
        });
      })
      .on("click.followUpRecovery", ".booking-recovery-card__cancel", (event) => {
        event.preventDefault();
        if (this.recoveryExpired || this.recoverySubmitting) {
          return;
        }
        const index = Number($(event.currentTarget).closest("[data-index]").attr("data-index"));
        const item = this.recoveryAppointments[index];
        if (!item || item.cancelLocked) {
          return;
        }
        item.pendingCancel = true;
        item.bookingstatus = "canceled";
        this.renderRecoveryAppointments();
      })
      .on("click.followUpRecovery", ".booking-recovery-card__undo", (event) => {
        event.preventDefault();
        if (this.recoveryExpired || this.recoverySubmitting) {
          return;
        }
        const index = Number($(event.currentTarget).closest("[data-index]").attr("data-index"));
        const item = this.recoveryAppointments[index];
        // Canceled in an earlier round: the CRM already has it canceled.
        if (!item || item.cancelLocked) {
          return;
        }
        item.pendingCancel = false;
        item.bookingstatus = "waiting-confirm";
        this.renderRecoveryAppointments();
      });
  }

  openFollowUpRecovery(data) {
    if (!$("#booking-recovery-modal").length || !data) {
      const fallback = data && data.message ? data.message : "";
      if (fallback && typeof this.showFormError === "function") {
        this.showFormError(fallback);
      }
      return;
    }
    if (window.HM_OTP_FLOW) {
      window.HM_OTP_FLOW.closeModal({ clearResendTimer: true });
    }
    this.bindRecoveryEvents();
    // A 409 on a recovery submit (same booking) continues the session: cards are
    // rebuilt from what was sent, and slots already found full stay blocked.
    const continuing =
      this.recoveryActive &&
      String(this.recoveryPostId || "") === String(data.booking_post_id || "");
    const previousCards = continuing
      ? this.recoveryAppointments || []
      : this.followUpAppointments || [];
    if (!continuing) {
      this.recoveryBlockedSlots = {};
    }
    this.recoveryActive = true;
    this.recoveryExpired = false;
    this.recoveryLeaveSent = false;
    this.recoveryPostId = data.booking_post_id;
    this.recoveryToken = data.recovery_token || "";
    this.recoveryExpiresAt = Number(data.expires_at) || 0;
    this.recoveryIssueSlots = {};
    this.recoveryIssueReasons = {};
    (data.issues || []).forEach((issue) => {
      if (!issue || !issue.malichhen) {
        return;
      }
      this.recoveryIssueSlots[issue.malichhen] = String(issue.timeslotid);
      this.recoveryIssueReasons[issue.malichhen] = issue.reason || "";
    });

    const sentList = Array.isArray(data.appointments) ? data.appointments : [];
    let source;
    if (sentList.length) {
      // The server stores this list as the recovery baseline; mirror it exactly.
      source = sentList.map((sent) => {
        const details =
          previousCards.find((item) => item.malichhen === sent.malichhen) ||
          previousCards.find(
            (item) => String(item.timeslotid) === String(sent.timeslotid),
          ) ||
          {};
        const isCanceled = sent.bookingstatus === "canceled";
        return {
          ...details,
          timeslotid: Number(sent.timeslotid),
          malichhen: sent.malichhen,
          bookingstatus: isCanceled ? "canceled" : "waiting-confirm",
          pendingCancel: isCanceled,
          cancelLocked: isCanceled,
        };
      });
    } else {
      source = (this.followUpAppointments || []).map((item) => ({
        ...item,
        pendingCancel: false,
        cancelLocked: false,
        bookingstatus: "waiting-confirm",
      }));
    }
    this.recoveryAppointments = source;
    (data.issues || []).forEach((issue) => {
      if (!issue || !issue.malichhen) {
        return;
      }
      const card = source.find((item) => item.malichhen === issue.malichhen) || null;
      this.addRecoveryBlockedSlot(issue.malichhen, issue.timeslotid, card);
    });
    this.sortRecoveryAppointments();

    $("#booking-recovery-patient").text(this.$form.find('input[name="name"]').val() || "");
    const hospitalLabel = this.$form
      .find("#hospitals-select-list .option.active")
      .first()
      .text()
      .trim();
    $("#booking-recovery-hospital").text(hospitalLabel);
    const first = this.recoveryAppointments[0];
    $("#booking-recovery-date").text(
      first ? this.formatFollowUpVisitDate(first.startTime) : "",
    );
    const condition = String(
      this.$form.find('[name="additional-information"]').val() || "",
    ).trim();
    const $conditionRow = $("#booking-recovery-condition-row");
    if (condition) {
      $("#booking-recovery-condition").text(condition);
      $conditionRow.removeAttr("hidden");
    } else {
      $("#booking-recovery-condition").text("");
      $conditionRow.attr("hidden", "hidden");
    }
    this.fillRecoveryContactInfo();
    this.closeRecoveryConfirmModal();
    $("#booking-recovery-expired").attr("hidden", "hidden");
    $("#booking-recovery-error").attr("hidden", "hidden").text("");
    $("#booking-recovery-hold").removeAttr("hidden");
    this.renderRecoveryAppointments();
    this.startRecoveryTimer();
    this.bindRecoveryLeaveGuard();

    const $modal = $("#booking-recovery-modal");
    $modal.addClass("show-modal").attr("aria-hidden", "false");
    $modal.find(".modal-dialog").addClass("active");
    $("body").addClass("show-modal");
  }

  fillRecoveryContactInfo() {
    const hospitalId = this.$form
      .find('input[name="booking-hospital"]')
      .val();
    const hospital = this.resolveHospitalData(hospitalId);
    const $emailBlock = $("#booking-recovery-email-block");
    const $hotlineBlock = $("#booking-recovery-hotline-block");
    const $emailLink = $("#booking-recovery-email");
    const $hotlineLink = $("#booking-recovery-hotline");

    $emailBlock.attr("hidden", "hidden");
    $hotlineBlock.attr("hidden", "hidden");
    $emailLink.attr("href", "").text("");
    $hotlineLink.attr("href", "").text("");

    if (hospital && hospital.email) {
      const email = String(hospital.email).trim();
      if (email) {
        $emailLink.attr("href", `mailto:${email}`).text(email);
        $emailBlock.removeAttr("hidden");
      }
    }

    if (hospital && hospital.hotline) {
      const hotline = String(hospital.hotline).trim();
      if (hotline) {
        $hotlineLink
          .attr("href", `tel:${formatPhoneTel(hotline)}`)
          .text(formatPhoneDisplay(hotline));
        $hotlineBlock.removeAttr("hidden");
      }
    }
  }

  renderRecoveryAppointments() {
    const labelTemplate = this.recoveryText("followup_appointment_label", "Lịch hẹn %d");
    const updateLabel = this.recoveryText("recovery_update_slot", "Chỉnh sửa");
    const cancelLabel = this.recoveryText("recovery_cancel_slot", "Huỷ");
    const undoLabel = this.recoveryText("recovery_undo", "Hoàn tác");
    const pendingLabel = this.recoveryText(
      "recovery_pending_cancel",
      "Yêu cầu huỷ",
    );
    const conflictLabel = this.recoveryText("recovery_status_conflict", "Bị trùng");
    const heldLabel = this.recoveryText("recovery_status_held", "Đang giữ");
    const updatedLabel = this.recoveryText("recovery_status_updated", "Đã chỉnh sửa");
    const expiredLabel = this.recoveryText("recovery_status_expired", "Đã hết hạn");
    const overbookedMsg = this.recoveryText(
      "recovery_overbooked",
      "Khung giờ này hiện không còn trống. Vui lòng chọn giờ khác hoặc hủy và đặt tiếp các lịch đang được giữ chỗ.",
    );
    const pendingMsg = this.recoveryText(
      "recovery_pending_cancel_msg",
      "Lịch hẹn này đã bị huỷ, có thể hoàn tác trong thời hạn chỉnh sửa lịch hẹn.",
    );
    const updatedMsg = this.recoveryText(
      "recovery_updated_msg",
      "Lịch hẹn này đã được cập nhật mới, tiếp tục tiến hành hoàn tất đặt lịch khám.",
    );
    const assetsUrl =
      (typeof HM_Variables !== "undefined" && HM_Variables.assets_url) ||
      "";
    const infoIcon = `${assetsUrl}img/icons/recovery-info.svg`;
    const editIcon = `${assetsUrl}img/icons/recovery-edit.svg`;
    const undoIcon = `${assetsUrl}img/icons/rotate-cw-ico.svg`;
    const editActions = `<button type="button" class="booking-recovery-card__cancel">${this.escapeFollowUpText(cancelLabel)}</button><button type="button" class="booking-recovery-card__update"><span>${this.escapeFollowUpText(updateLabel)}</span><img src="${this.escapeFollowUpText(editIcon)}" alt="" width="24" height="24"></button>`;
    const undoAction = `<button type="button" class="booking-recovery-card__undo"><span>${this.escapeFollowUpText(undoLabel)}</span><img src="${this.escapeFollowUpText(undoIcon)}" alt="" width="24" height="24"></button>`;
    const buildFooter = (message, actions) =>
      `<div class="booking-recovery-card__footer"><div class="booking-recovery-card__message"><img src="${this.escapeFollowUpText(infoIcon)}" alt="" width="20" height="20"><p>${this.escapeFollowUpText(message)}</p></div><div class="booking-recovery-card__actions">${actions}</div></div>`;
    const html = (this.recoveryAppointments || [])
      .map((item, index) => {
        const needsAction = this.recoveryItemNeedsAction(item);
        const wasUpdated = this.recoveryItemWasUpdated(item);
        const info = [item.specialtyName, item.doctorName, this.formatFollowUpClockTime(item.startTime)]
          .filter(Boolean)
          .join(" - ");
        const label = this.escapeFollowUpText(
          String(labelTemplate).replace("%d", String(index + 1)),
        );

        let statusLabel = heldLabel;
        if (this.recoveryExpired) {
          statusLabel = expiredLabel;
        } else if (item.pendingCancel) {
          statusLabel = pendingLabel;
        } else if (needsAction) {
          statusLabel = conflictLabel;
        } else if (wasUpdated) {
          statusLabel = updatedLabel;
        }

        let footer = "";
        if (!this.recoveryExpired) {
          if (item.pendingCancel) {
            // Canceled in an earlier round: already sent to the CRM, no undo.
            footer = item.cancelLocked ? "" : buildFooter(pendingMsg, undoAction);
          } else if (needsAction) {
            footer = buildFooter(overbookedMsg, editActions);
          } else if (wasUpdated) {
            footer = buildFooter(updatedMsg, editActions);
          }
        }

        const cardClass = [
          "booking-recovery-card",
          this.recoveryExpired ? "is-readonly" : "",
          !this.recoveryExpired && needsAction ? "is-overbooked" : "",
          !this.recoveryExpired && item.pendingCancel ? "is-pending-cancel" : "",
          !this.recoveryExpired && wasUpdated ? "is-updated" : "",
        ]
          .filter(Boolean)
          .join(" ");
        return `<article class="${cardClass}" data-index="${index}"><div class="booking-recovery-card__heading"><span class="booking-recovery-card__status">${this.escapeFollowUpText(statusLabel)}</span><div class="booking-recovery-card__label">${label}</div><p class="booking-recovery-card__info">${this.escapeFollowUpText(info)}</p></div>${footer}</article>`;
      })
      .join("");
    $("#booking-recovery-list").html(html);
    const canSubmit = this.recoveryCanSubmit();
    $("#booking-recovery-submit")
      .prop("disabled", !canSubmit || this.recoverySubmitting)
      .prop("hidden", !!this.recoveryExpired);
    $("#booking-recovery-cancel-all").toggleClass(
      "is-primary",
      !!this.recoveryExpired,
    );
  }

  markRecoveryExpired() {
    this.recoveryExpired = true;
    // The server can report expiry before the local countdown ends; a later tick
    // would otherwise bring the timer back over the expired line.
    this.stopRecoveryTimer();
    $("#booking-recovery-timer").text("");
    $("#booking-recovery-hold").attr("hidden", "hidden");
    $("#booking-recovery-expired").removeAttr("hidden");
    // A cancel request in flight keeps its lock; it resolves on its own.
    if (!this.isRecoveryConfirmProcessing()) {
      this.closeRecoveryConfirmModal();
    }
    this.renderRecoveryAppointments();
  }

  startRecoveryTimer() {
    this.stopRecoveryTimer();
    const tick = () => {
      const remain = this.recoveryExpiresAt * 1000 - Date.now();
      if (remain <= 0) {
        this.markRecoveryExpired();
        this.stopRecoveryTimer();
        return;
      }
      const total = Math.ceil(remain / 1000);
      const minutes = String(Math.floor(total / 60)).padStart(2, "0");
      const seconds = String(total % 60).padStart(2, "0");
      $("#booking-recovery-hold").removeAttr("hidden");
      $("#booking-recovery-expired").attr("hidden", "hidden");
      $("#booking-recovery-timer").text(`${minutes}:${seconds}`);
    };
    tick();
    this.recoveryTimerId = window.setInterval(tick, 1000);
  }

  stopRecoveryTimer() {
    if (this.recoveryTimerId) {
      window.clearInterval(this.recoveryTimerId);
      this.recoveryTimerId = null;
    }
  }

  bindRecoveryLeaveGuard() {
    this.clearRecoveryLeaveGuard();
    this.onRecoveryBeforeUnload = (event) => {
      if (!this.recoveryActive) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
    };
    this.onRecoveryPageHide = () => {
      if (!this.recoveryActive || this.recoveryLeaveSent) {
        return;
      }
      this.recoveryLeaveSent = true;
      const body = new FormData();
      body.append("action", "hmc_cancel_followup_recovery");
      body.append("booking_post_id", String(this.recoveryPostId || ""));
      body.append("recovery_token", this.recoveryToken || "");
      if (navigator.sendBeacon) {
        navigator.sendBeacon(HM_Variables.ajax_url, body);
        return;
      }
      fetch(HM_Variables.ajax_url, { method: "POST", body, keepalive: true });
    };
    window.addEventListener("beforeunload", this.onRecoveryBeforeUnload);
    window.addEventListener("pagehide", this.onRecoveryPageHide);
  }

  clearRecoveryLeaveGuard() {
    if (this.onRecoveryBeforeUnload) {
      window.removeEventListener("beforeunload", this.onRecoveryBeforeUnload);
    }
    if (this.onRecoveryPageHide) {
      window.removeEventListener("pagehide", this.onRecoveryPageHide);
    }
    this.onRecoveryBeforeUnload = null;
    this.onRecoveryPageHide = null;
  }

  clearRecoverySession() {
    this.recoveryActive = false;
    this.recoveryExpired = false;
    this.recoveryBlockedSlots = {};
    this.stopRecoveryTimer();
    this.clearRecoveryLeaveGuard();
    this.closeRecoveryConfirmModal();
    $("#booking-recovery-modal").removeClass("show-modal").attr("aria-hidden", "true");
    $("#booking-recovery-modal .modal-dialog").removeClass("active");
  }

  fillRecoveryCanceledNoticeHotline() {
    const $row = $("#booking-recovery-notice-hotline-row");
    const $link = $("#booking-recovery-notice-hotline");
    if (!$row.length) {
      return;
    }

    const hospitalId = this.$form
      .find('input[name="booking-hospital"]')
      .val();
    const hospital = this.resolveHospitalData(hospitalId);
    const rawHotline = hospital && hospital.hotline
      ? String(hospital.hotline).trim()
      : "";

    if (!rawHotline) {
      $link.attr("href", "").text("");
      $row.attr("hidden", "hidden");
      return;
    }

    $link
      .attr("href", `tel:${formatPhoneTel(rawHotline)}`)
      .text(formatPhoneDisplay(rawHotline));
    $row.removeAttr("hidden");
  }

  showRecoveryCanceledNotice() {
    this.fillRecoveryCanceledNoticeHotline();
    this.clearRecoverySession();
    const $notice = $("#booking-recovery-notice");
    $notice.addClass("show-modal").attr("aria-hidden", "false");
    $notice.find(".modal-dialog").addClass("active");
    $("body").addClass("show-modal");
  }

  ensureRecoveryFields() {
    let $token = this.$form.find('input[name="crm_recovery_token"]');
    if (!$token.length) {
      $token = $('<input type="hidden" name="crm_recovery_token">');
      this.$form.append($token);
    }
    let $postId = this.$form.find('input[name="crm_recovery_booking_id"]');
    if (!$postId.length) {
      $postId = $('<input type="hidden" name="crm_recovery_booking_id">');
      this.$form.append($postId);
    }
    $token.val(this.recoveryToken || "");
    $postId.val(this.recoveryPostId || "");
  }

  setRecoverySubmitProcessing(isProcessing) {
    this.recoverySubmitting = !!isProcessing;
    const $submit = $("#booking-recovery-submit");
    const $label = $submit.find("span").first();
    $("#booking-recovery-modal").attr("aria-busy", isProcessing ? "true" : "false");
    $("#booking-recovery-cancel-all").prop("disabled", !!isProcessing);

    if (isProcessing) {
      if (!$submit.data("recoveryOriginalLabel")) {
        $submit.data("recoveryOriginalLabel", $label.text());
      }
      $label.text(this.recoveryText("recovery_submit_processing", "Đang gửi..."));
      $submit.addClass("is-loading").prop("disabled", true);
      return;
    }

    const original = $submit.data("recoveryOriginalLabel");
    if (original) {
      $label.text(original);
      $submit.removeData("recoveryOriginalLabel");
    }
    $submit
      .removeClass("is-loading")
      .prop("disabled", !this.recoveryCanSubmit());
  }

  submitFollowUpRecovery() {
    if (this.recoverySubmitting || !this.recoveryCanSubmit()) {
      return;
    }
    const payload = (this.recoveryAppointments || []).map((item) => ({
      timeslotid: Number(item.timeslotid),
      bookingstatus: item.pendingCancel ? "canceled" : "waiting-confirm",
      malichhen: item.malichhen,
      specialtyName: item.specialtyName || "",
      doctorName: item.doctorName || "",
      startTime: item.startTime || "",
    }));
    this.$form.find('input[name="booking-appointments"]').val(JSON.stringify(payload));
    this.ensureRecoveryFields();
    this.setRecoverySubmitProcessing(true);
    Promise.resolve(this.submitForm({ fromRecovery: true }))
      .catch(() => {})
      .finally(() => this.setRecoverySubmitProcessing(false));
  }

  cancelAllFollowUpRecovery() {
    const failedMessage = this.recoveryText(
      "recovery_cancel_failed",
      "Không huỷ được lịch hẹn. Vui lòng thử lại.",
    );
    const timeoutMessage = this.recoveryText(
      "recovery_cancel_timeout",
      "Yêu cầu huỷ quá thời gian chờ. Vui lòng thử lại.",
    );

    if (this.recoveryCancelXhr && typeof this.recoveryCancelXhr.abort === "function") {
      this.recoveryCancelXhr.abort();
    }

    const body = new FormData();
    body.append("action", "hmc_cancel_followup_recovery");
    body.append("booking_post_id", String(this.recoveryPostId || ""));
    body.append("recovery_token", this.recoveryToken || "");

    this.recoveryCancelXhr = $.ajax({
      url: HM_Variables.ajax_url,
      type: "POST",
      data: body,
      processData: false,
      contentType: false,
      timeout: 15000,
      success: (response) => {
        this.recoveryCancelXhr = null;
        if (response && response.success) {
          this.setRecoveryConfirmProcessing(false);
          this.showRecoveryCanceledNotice();
          return;
        }
        this.setRecoveryConfirmProcessing(false);
        this.setRecoveryConfirmError(failedMessage);
      },
      error: (_xhr, textStatus) => {
        this.recoveryCancelXhr = null;
        if (textStatus === "abort") {
          return;
        }
        this.setRecoveryConfirmProcessing(false);
        this.setRecoveryConfirmError(
          textStatus === "timeout" ? timeoutMessage : failedMessage,
        );
      },
    });
  }

  renderFollowUpAppointments() {
    const $wrap = this.$form.find(".followup-appointments");
    const $list = $wrap.find(".followup-appointments__list");
    const $group = this.$form.find(".booking-time-input-group");
    if (!$wrap.length || !$list.length) {
      return;
    }

    const appointments = this.followUpAppointments || [];
    if (!appointments.length) {
      $list.empty();
      $wrap.attr("hidden", "hidden");
      $group.removeClass("is-date-locked");
      return;
    }

    const labelTemplate = this.followUpText(
      "followup_appointment_label",
      "Appointment %d",
    );
    const removeLabel = this.followUpText(
      "followup_remove_appointment",
      "Remove appointment",
    );
    const cards = appointments
      .map((item, index) => {
        const info = [
          item.specialtyName,
          item.doctorName,
          this.formatFollowUpClockTime(item.startTime),
        ]
          .filter(Boolean)
          .join(" - ");
        const label = this.escapeFollowUpText(
          String(labelTemplate).replace("%d", String(index + 1)),
        );
        return `<article class="followup-appointment-card" data-index="${index}">
          <button type="button" class="followup-appointment-card__open">
            <span class="followup-appointment-card__label">${label}</span>
            <span class="followup-appointment-card__info">${this.escapeFollowUpText(info)}</span>
          </button>
          <button type="button" class="followup-appointment-card__remove" aria-label="${this.escapeFollowUpText(removeLabel)}">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </article>`;
      })
      .join("");

    $list.html(cards);
    $wrap.removeAttr("hidden");
    $wrap
      .find(".followup-appointments__add")
      .prop("hidden", appointments.length >= 3);
    $group.addClass("is-date-locked");
  }

  applyUrlBookingPreselection() {
    if (!this.isActive) {
      return;
    }

    const vars =
      this.formType === "package" &&
      typeof packageBookingFormVars !== "undefined"
        ? packageBookingFormVars
        : typeof bookingFormVars !== "undefined"
          ? bookingFormVars
          : null;

    if (!vars) {
      return;
    }

    if (vars.book_specialty) {
      this.$form
        .find('input[name="booking-specialty"]')
        .val(vars.book_specialty);
    }

    if (vars.book_package) {
      this.$form
        .find('input[name="booking-package"]')
        .val(vars.book_package);
    }
  }

  populateHospitalRelationData(hospital) {
    if (!hospital || typeof hospital !== "object") {
      return hospital;
    }

    let enriched = { ...hospital };

    if (
      typeof HM_Variables !== "undefined" &&
      Array.isArray(HM_Variables.hospitals)
    ) {
      const refShort = (hospital.hospital_shortname || "").toLowerCase();
      const match = HM_Variables.hospitals.find((h) => {
        const shortName = (h.hospital_shortname || "").toLowerCase();
        return (
          String(h.id) === String(hospital.id) ||
          String(h.hospital_id) === String(hospital.hospital_id || "") ||
          (refShort && shortName === refShort)
        );
      });
      if (match) {
        enriched = { ...match, ...hospital };
      }
    }

    const needsSpecialties =
      !Array.isArray(enriched.specialties) || !enriched.specialties.length;
    const needsPackages =
      !Array.isArray(enriched.packages) || !enriched.packages.length;

    if (
      (needsSpecialties || needsPackages) &&
      typeof hospitalBookingFormVars !== "undefined" &&
      hospitalBookingFormVars
    ) {
      if (needsSpecialties && Array.isArray(hospitalBookingFormVars.specialties)) {
        enriched.specialties = hospitalBookingFormVars.specialties.map(
          (specialty) => ({
            ...specialty,
            is_his: specialty.is_his === 0 ? 0 : 1,
          }),
        );
      }
      if (needsPackages && Array.isArray(hospitalBookingFormVars.packages)) {
        enriched.packages = hospitalBookingFormVars.packages;
      }
    }

    return enriched;
  }

  resolveHospitalData(hospitalRef) {
    if (!hospitalRef) {
      return null;
    }

    if (typeof hospitalRef === "object") {
      return this.populateHospitalRelationData(hospitalRef);
    }

    if (
      typeof HM_Variables === "undefined" ||
      !Array.isArray(HM_Variables.hospitals)
    ) {
      return null;
    }

    const reference = String(hospitalRef).trim();
    if (!reference.length) {
      return null;
    }

    const normalized = reference.toLowerCase();

    const matched =
      HM_Variables.hospitals.find((hospital) => {
        const shortName = (hospital.hospital_shortname || "").toLowerCase();
        const hisId = String(hospital.hospital_id || "");
        return (
          shortName === normalized ||
          String(hospital.id) === reference ||
          hisId === reference
        );
      }) || null;

    return matched ? this.populateHospitalRelationData(matched) : null;
  }

  updateDisclaimerHotline(hospital) {
    const $disclaimerRow = this.$form.find(".disclaimer-row");
    if (!$disclaimerRow.length) {
      return;
    }

    const template = $disclaimerRow.data("hotlineTemplate");
    if (!template) {
      return;
    }

    const defaultHotline = $disclaimerRow.data("defaultHotline") || "";

    let hotline = null;
    if (hospital && typeof hospital === "object") {
      if (hospital.hotline) {
        hotline = hospital.hotline;
      } else if (
        hospital.hospital_contact_info &&
        hospital.hospital_contact_info.hotline
      ) {
        hotline = hospital.hospital_contact_info.hotline;
      }
    }

    if (!hotline) {
      hotline = defaultHotline;
    }

    if (!hotline) {
      return;
    }

    const sanitizedHotline = String(hotline)
      .replace(/[^0-9+().\s|-]/g, "")
      .trim();

    if (!sanitizedHotline.length) {
      return;
    }

    const hotlineForDisplay = formatPhoneDisplay(sanitizedHotline);
    const hotlineForUrl = formatPhoneTel(sanitizedHotline);

    // Display text uses formatted hotline; tel: uses compact first number
    let updatedHtml = template.replace(/__HOTLINE__/g, hotlineForDisplay);

    updatedHtml = updatedHtml.replace(
      /href="tel:([^"]*)"/g,
      () => `href="tel:${hotlineForUrl}"`,
    );

    $disclaimerRow.html(updatedHtml);
  }

  showFormError(message) {
    const $errorMsg = this.$form.find("#form-primary-error-msg");
    $errorMsg.text(message).show();
  }

  showFormSuccess(message) {
    const $errorMsg = this.$form.find("#form-primary-error-msg");
    $errorMsg.text(message).show();
  }

  showAgreementError(message) {
    const $agreementErrorMsg = this.$form.find("#agreement-error-msg");
    $agreementErrorMsg.text(message).show();
  }

  hideFormError() {
    this.$form.find("#form-primary-error-msg").hide();
  }

  hideAgreementError() {
    this.$form.find("#agreement-error-msg").hide();
  }

  getRecaptchaToken() {
    return new Promise((resolve, reject) => {
      // Check if reCAPTCHA is available
      if (typeof grecaptcha === "undefined" || !grecaptcha.ready) {
        // If reCAPTCHA script is not loaded, resolve with empty string
        // (form will submit without token, server will handle validation)
        resolve("");
        return;
      }

      // Get site key from script tag
      const recaptchaScript = document.querySelector(
        'script[src*="recaptcha/api.js"]',
      );
      if (!recaptchaScript) {
        resolve("");
        return;
      }

      const recaptchaSiteKey = recaptchaScript.src.match(/render=([^&]+)/)?.[1];
      if (!recaptchaSiteKey) {
        resolve("");
        return;
      }

      // Get fresh token
      grecaptcha.ready(function () {
        grecaptcha
          .execute(recaptchaSiteKey, { action: "booking_form" })
          .then(function (token) {
            resolve(token);
          })
          .catch(function (error) {
            console.error("reCAPTCHA error:", error);
            reject(error);
          });
      });
    });
  }

  getBookingConnectionError() {
    return HM_Variables.language === "vi"
      ? "Lỗi kết nối. Vui lòng thử lại."
      : "Connection error. Please try again.";
  }

  /**
   * `data` of a failed admin-ajax booking response. jQuery leaves `responseJSON`
   * empty when the body is not clean JSON (e.g. a PHP notice printed before it,
   * or a non-JSON Content-Type), so fall back to parsing `responseText`.
   */
  getAjaxErrorData(xhr) {
    if (xhr && xhr.responseJSON && typeof xhr.responseJSON === "object") {
      return xhr.responseJSON.data;
    }
    const text = String((xhr && xhr.responseText) || "");
    const start = text.indexOf('{"success"');
    if (start === -1) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(text.slice(start));
      return parsed && typeof parsed === "object" ? parsed.data : undefined;
    } catch (e) {
      return undefined;
    }
  }

  bookingErrorMessage(data, fallback = "") {
    if (typeof data === "string" && data) {
      return data;
    }
    if (data && typeof data === "object" && data.message) {
      return String(data.message);
    }
    return fallback || this.getBookingConnectionError();
  }

  /**
   * Shared CRM submit failure handling for base + integrated CRM forms.
   * Returns true when the response was fully handled (recovery, etc.).
   */
  handleBookingSubmitFailure(responseData, options = {}) {
    const { fromRecovery = false } = options;
    const connectionError = this.getBookingConnectionError();

    if (
      responseData &&
      typeof responseData === "object" &&
      responseData.code === "partial_lock"
    ) {
      if (typeof this.openFollowUpRecovery === "function") {
        this.openFollowUpRecovery(responseData);
      }
      return true;
    }

    if (
      responseData &&
      typeof responseData === "object" &&
      responseData.code === "recovery_expired"
    ) {
      this.markRecoveryExpired();
      return true;
    }

    if (fromRecovery) {
      const recoveryMessage = this.bookingErrorMessage(
        responseData,
        connectionError,
      );
      $("#booking-recovery-error").text(recoveryMessage).removeAttr("hidden");
      if (typeof this.renderRecoveryAppointments === "function") {
        this.renderRecoveryAppointments();
      }
      return true;
    }

    return false;
  }

  handleBookingSubmitSuccess(response, options = {}) {
    const { fromOtpFlow = false, fromRecovery = false } = options;
    const payload =
      response.data && typeof response.data === "object" ? response.data : {};

    if (payload.code === "canceled_all") {
      if (typeof this.showRecoveryCanceledNotice === "function") {
        this.showRecoveryCanceledNotice();
      }
      return true;
    }

    this.clearOtpVerificationToken();

    if (fromOtpFlow && window.HM_OTP_FLOW) {
      window.HM_OTP_FLOW.closeModal({ clearResendTimer: true });
    }

    // Canceled recovery items are not booked, so they stay out of the thank-you list.
    const displayAppointments = (
      (fromRecovery ? this.recoveryAppointments : this.followUpAppointments) || []
    ).filter((item) => item && !item.pendingCancel && item.bookingstatus !== "canceled");
    if (typeof this.clearRecoverySession === "function") {
      this.clearRecoverySession();
    }
    // After recovery the form's hidden fields still hold the first original slot,
    // which may be canceled or moved, so even one remaining item uses the list.
    const minListLength = fromRecovery ? 1 : 2;
    this.showThankYouModal({
      appointments:
        displayAppointments.length >= minListLength ? displayAppointments : null,
    });
    this.$form[0].reset();
    this.resetCustomSelects();
    if (typeof this.clearFollowUpListState === "function") {
      this.clearFollowUpListState();
    }
    // Hidden inputs keep .val() through form.reset(); a stale token would route the
    // next booking on this page into the (already finished) recovery branch.
    this.$form
      .find('input[name="crm_recovery_token"], input[name="crm_recovery_booking_id"]')
      .val("");
    this.validateFormRealtime();
    return true;
  }

  showBookingSubmitError(errorMessage, options = {}) {
    // asModal: a 409 (slot full, or every follow-up slot full) uses the error modal
    // even without OTP (Danh Y session). Partial locks never get here; they open recovery.
    const { fromOtpFlow = false, asModal = false } = options;
    if ((fromOtpFlow && window.HM_OTP_FLOW) || asModal) {
      if (window.HM_OTP_FLOW) {
        window.HM_OTP_FLOW.closeModal();
      }
      this.showThankYouModal({
        isError: true,
        message: errorMessage,
      });
      return;
    }
    this.showFormError("❌ " + errorMessage);
  }

  async submitForm(options = {}) {
    const { fromOtpFlow = false, fromRecovery = false } = options;
    const $submitBtn = this.$form.find('button[type="submit"]');
    const originalText = $submitBtn.text();

    const processingText =
      HM_Variables.language === "vi" ? "Đang xử lý..." : "Processing...";
    if (!fromOtpFlow) {
      $submitBtn.prop("disabled", true).text(processingText);
    }

    this.hideFormError();

    const connectionError = this.getBookingConnectionError();

    try {
      const token = await this.getRecaptchaToken().catch((error) => {
        console.warn(
          "reCAPTCHA token generation failed, submitting without token:",
          error,
        );
        return "";
      });

      const formData = new FormData(this.$form[0]);
      const $recaptchaInput = this.$form.find("#g-recaptcha-response");
      if ($recaptchaInput.length) {
        formData.set("g-recaptcha-response", token);
      } else {
        formData.append("g-recaptcha-response", token);
      }

      return await new Promise((resolve) => {
        $.ajax({
          url: HM_Variables.ajax_url,
          type: "POST",
          data: formData,
          processData: false,
          contentType: false,
          success: (response) => {
            if (!fromOtpFlow) {
              $submitBtn
                .prop("disabled", false)
                .text(originalText)
                .removeClass("disabled");
            }

            if (response.success) {
              this.handleBookingSubmitSuccess(response, options);
              resolve(response);
              return;
            }

            if (this.handleBookingSubmitFailure(response.data, options)) {
              resolve(response);
              return;
            }

            this.showBookingSubmitError(
              this.bookingErrorMessage(response.data, connectionError),
              options,
            );
            resolve(response);
          },
          error: (xhr) => {
            if (!fromOtpFlow) {
              $submitBtn
                .prop("disabled", false)
                .text(originalText)
                .removeClass("disabled");
            }

            console.error("AJAX Error:", {
              status: xhr.status,
              statusText: xhr.statusText,
              responseText: xhr.responseText,
              responseJSON: xhr.responseJSON,
            });

            const responseData = this.getAjaxErrorData(xhr);
            if (this.handleBookingSubmitFailure(responseData, options)) {
              resolve({ success: false, status: xhr.status, data: responseData });
              return;
            }

            let errorMessage = connectionError;
            if (responseData !== undefined) {
              errorMessage = this.bookingErrorMessage(
                responseData,
                connectionError,
              );
            } else if (xhr.status === 0) {
              errorMessage =
                "Không thể kết nối đến server. Vui lòng kiểm tra kết nối internet.";
            } else if (xhr.status >= 500) {
              errorMessage = "Lỗi server. Vui lòng thử lại sau.";
            }

            this.showBookingSubmitError(errorMessage, {
              ...options,
              asModal: xhr.status === 409,
            });
            resolve({ success: false, status: xhr.status, data: responseData });
          },
        });
      });
    } catch (error) {
      if (!fromOtpFlow) {
        $submitBtn
          .prop("disabled", false)
          .text(originalText)
          .removeClass("disabled");
      }

      this.showBookingSubmitError(connectionError, options);
      throw error;
    }
  }

  resetCustomSelects() {
    if (this.formType === "general") {
      const $hospitalsCustomSelect = this.$form
        .find("#hospitals-select-list")
        .closest(".custom-select");
      const $hospitalsCurrentValue =
        $hospitalsCustomSelect.find(".current-value");
      $hospitalsCurrentValue
        .html(HM_Variables.frontend_lang.select_placeholders.choose_hospital)
        .addClass("default-value");
      this.$form.find("input[name='booking-hospital']").val("");
    }

    const $specialtiesCustomSelect = this.$form
      .find("#specialties-select-list")
      .closest(".custom-select");
    const $specialtiesCurrentValue =
      $specialtiesCustomSelect.find(".current-value");
    $specialtiesCurrentValue
      .html(HM_Variables.frontend_lang.select_placeholders.choose_specialty)
      .addClass("default-value");
    this.$form.find("input[name='booking-specialty']").val("");

    const $packagesCustomSelect = this.$form
      .find("#packages-select-list")
      .closest(".custom-select");
    const $packagesCurrentValue = $packagesCustomSelect.find(".current-value");
    $packagesCurrentValue
      .html(HM_Variables.frontend_lang.select_placeholders.choose_package)
      .addClass("default-value");
    this.$form.find("input[name='booking-package']").val("");

    const $provinceCustomSelect = this.$form
      .find("#province-select-list")
      .closest(".custom-select");
    const $provinceCurrentValue = $provinceCustomSelect.find(".current-value");
    $provinceCurrentValue
      .html(this.getLocationPlaceholder("choose_city", "Choose City"))
      .addClass("default-value");
    this.$form.find("input[name='id_tinh_thanh']").val("");

    const $wardCustomSelect = this.$form
      .find("#ward-select-list")
      .closest(".custom-select");
    const $wardCurrentValue = $wardCustomSelect.find(".current-value");
    $wardCurrentValue
      .html(this.getLocationPlaceholder("choose_ward", "Choose Ward"))
      .addClass("default-value");
    this.$form.find("input[name='id_phuong_xa']").val("00");

    const $wardList = this.$form.find("#ward-select-list");
    if ($wardList.length) {
      $wardList.html(
        `<li class="label">${this.getLocationPlaceholder(
          "select_city_first",
          "Please select city first",
        )}</li>`,
      );
    }
    this.setWardSelectDisabled(true);

    if (this.formType === "general") {
      this.$form
        .find("#specialties-select-list")
        .html(
          '<li class="label">' +
            HM_Variables.frontend_lang.select_hospital_first +
            "</li>",
        );
      this.$form
        .find("#packages-select-list")
        .html(
          '<li class="label">' +
            HM_Variables.frontend_lang.select_hospital_first +
            "</li>",
        );
    } else if (this.formType === "hospital") {
      if (hospitalBookingFormVars && hospitalBookingFormVars.specialties) {
        // Get specialty label (Tùy chọn or Optional)
        const getAllSpecialtyLabel = () => {
          if (typeof HM_Variables !== "undefined") {
            const lang = (HM_Variables.language || "").toLowerCase();
            if (lang === "en") {
              return "Optional";
            }
          }
          return "Tùy chọn";
        };

        // Build specialty options with "Tùy chọn" option first
        // For hospital form, specialties are already prepared, no need to filter H.I.S
        const specialtyOptions = [
          `<li class="option" data-value="">${getAllSpecialtyLabel()}</li>`,
          ...hospitalBookingFormVars.specialties.map(
            (specialty) =>
              `<li class="option" data-value="${specialty.id}">${specialty.title}</li>`,
          ),
        ];
        this.$form
          .find("#specialties-select-list")
          .html(specialtyOptions.join(""));
      }
      if (hospitalBookingFormVars && hospitalBookingFormVars.packages) {
        const packageOptions = [
          `<li class="option" data-value="-2">${this.getExamineBySpecialtyLabel()}</li>`,
          ...hospitalBookingFormVars.packages.map(
            (packageItem) =>
              `<li class="option" data-value="${packageItem.id}">${packageItem.title}</li>`,
          ),
        ];
        this.$form.find("#packages-select-list").html(packageOptions.join(""));
      }
    }

    this.$form.find("#hospitals-select-list .option").removeClass("active");
    this.$form.find("#specialties-select-list .option").removeClass("active");
    this.$form.find("#packages-select-list .option").removeClass("active");
    this.$form.find("#province-select-list .option").removeClass("active");
    this.$form.find("#ward-select-list .option").removeClass("active");
    this.$form
      .find("#appointment-type-select-list .option")
      .removeClass("active");

    const $appointmentTypeSelect = this.$form
      .find("#appointment-type-select-list")
      .closest(".custom-select");
    if ($appointmentTypeSelect.length) {
      const placeholderLabel =
        (typeof HM_Variables !== "undefined" &&
          HM_Variables.frontend_lang &&
          HM_Variables.frontend_lang.choose_appointment_type) ||
        (typeof HM_Variables !== "undefined" &&
        HM_Variables.language === "en"
          ? "Select Appointment Type*"
          : "Chọn loại lịch hẹn*");
      $appointmentTypeSelect
        .find(".current-value")
        .html(placeholderLabel)
        .addClass("default-value");
      this.$form.find("input[name='booking-appointment-type']").val("");
    }
    this.$form
      .find("input[name='cothebhyt'][value='true']")
      .prop("checked", true);

    if (this.formType === "general") {
      this.handleHospitalSelection(null);
    } else if (
      this.formType === "hospital" &&
      typeof hospitalBookingFormVars !== "undefined" &&
      hospitalBookingFormVars &&
      hospitalBookingFormVars.hospital
    ) {
      this.handleHospitalSelection(hospitalBookingFormVars.hospital);
    }
  }

  refreshRecaptchaToken() {
    if (typeof grecaptcha !== "undefined" && grecaptcha.ready) {
      grecaptcha.ready(function () {
        const recaptchaSiteKey = document
          .querySelector('script[src*="recaptcha/api.js"]')
          ?.src.match(/render=([^&]+)/)?.[1];
        if (recaptchaSiteKey) {
          grecaptcha
            .execute(recaptchaSiteKey, { action: "booking_form" })
            .then(function (token) {
              document.getElementById("g-recaptcha-response").value = token;
            });
        }
      });
    }
  }

  showThankYouModal(options = {}) {
    const { isError = false, message = "", appointments = null } = options;

    const selectedHospitalId = this.$form
      .find("input[name='booking-hospital']")
      .val();

    let hospital = null;

    if (selectedHospitalId && HM_Variables?.hospitals) {
      hospital = HM_Variables.hospitals.find(
        (h) =>
          String(h.hospital_id) === String(selectedHospitalId) ||
          String(h.id) === String(selectedHospitalId),
      );
    } else if (
      typeof hospitalBookingFormVars !== "undefined" &&
      hospitalBookingFormVars &&
      hospitalBookingFormVars.hospital
    ) {
      hospital = hospitalBookingFormVars.hospital;
    }

    const isNoCrmHospital = !!(hospital && hospital.no_crm_integrated);
    const shouldUseCrmThankYouModal = () => {
      if (this.formType === "integrated-crm") {
        return !isNoCrmHospital;
      }

      if (this.formType === "hospital") {
        return !isNoCrmHospital;
      }

      // Package form should keep legacy thank-you modal.
      if (this.formType === "package") {
        return false;
      }

      return false;
    };

    const usesCrmThankYouModal = shouldUseCrmThankYouModal();

    const modalSelector = usesCrmThankYouModal
      ? "#thankyou-modal-crm"
      : "#thankyou-modal";
    const $modal = $(modalSelector);
    const $modalDialog = $modal.find(".modal-dialog");
    const $title = $modal.find(".thankyou-dialog-tittle");
    const $desc = $modal.find(".desc");
    const $statusIcon = $modal.find(".checked-ico img");

    // Hide all contact info blocks initially
    const contactPrefix = usesCrmThankYouModal ? "thankyou-crm" : "thankyou";
    const $emailBlock = $(`#${contactPrefix}-email-block`);
    const $hotlineBlock = $(`#${contactPrefix}-hotline-block`);
    const $emailLink = $(`#${contactPrefix}-email`);
    const $hotlineLink = $(`#${contactPrefix}-hotline`);

    // The CRM modal renders its hotline/working hours from theme options.
    const $crmContact = usesCrmThankYouModal
      ? $("#thankyou-crm-contact")
      : $();

    if (!$crmContact.length) {
      $emailBlock.hide();
      $hotlineBlock.hide();
      $emailLink.attr("href", "").text("");
      $hotlineLink.attr("href", "").text("");
    }

    const resetCrmAppointmentInfo = () => {
      if (!usesCrmThankYouModal) {
        return;
      }

      const fields = [
        "patient",
        "hospital",
        "specialty",
        "package",
        "date",
        "time",
        "doctor",
        "condition",
      ];

      fields.forEach((field) => {
        $(`#thankyou-crm-${field}`).text("");
        $(`#thankyou-crm-${field}-row`).removeClass("is-visible");
      });
      $("#thankyou-crm-appointment-info").removeClass("is-visible");
      $("#thankyou-crm-appointment-list").empty().attr("hidden", "hidden");
    };

    const stripFollowUpSelectedSuffix = (label) => {
      const lang = (HM_Variables?.language || "vi").toLowerCase();
      const suffix =
        HM_Variables?.frontend_lang?.followup_specialty_selected ||
        (lang === "en" ? "(selected)" : "(đã chọn)");
      const pattern = new RegExp(
        `\\s*${String(suffix).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*$`,
        "i",
      );
      return String(label || "").replace(pattern, "").trim();
    };

    const resolveSelectedOptionLabel = (listSelector, hiddenInputName) => {
      const hiddenVal = this.$form.find(`input[name="${hiddenInputName}"]`).val();
      if (!hiddenVal) {
        return "";
      }

      const $option = this.$form
        .find(`${listSelector} .option[data-value="${hiddenVal}"]`)
        .first();
      if ($option.length) {
        return $option.text().trim();
      }
      return "";
    };

    const parseBookingTimeRender = (rawValue) => {
      const normalized = String(rawValue || "").trim();
      if (!normalized) {
        return { specialty: "", date: "", time: "", trailing: "" };
      }

      const parts = normalized
        .split(/\s*-\s*/)
        .map((part) => part.trim())
        .filter(Boolean);
      const dateIndex = parts.findIndex((part) =>
        /\d{1,2}\/\d{1,2}\/\d{4}/.test(part),
      );

      if (dateIndex < 0) {
        return { specialty: "", date: "", time: "", trailing: "" };
      }

      const date = parts[dateIndex].match(/\d{1,2}\/\d{1,2}\/\d{4}/)[0];
      const specialty =
        dateIndex > 0 ? parts.slice(0, dateIndex).join(" - ") : "";
      const afterDate = parts.slice(dateIndex + 1);
      const timeMatch = afterDate[0]?.match(/^(\d{1,2}:\d{2})/);
      const time = timeMatch ? timeMatch[1] : "";
      const trailing = time ? afterDate.slice(1).join(" - ").trim() : "";

      return { specialty, date, time, trailing };
    };

    const parseAppointmentDateTime = (rawValue) => {
      const normalized = String(rawValue || "").trim();
      if (!normalized) {
        return { date: "", time: "" };
      }

      const dateMatch = normalized.match(/\b\d{1,2}\/\d{1,2}\/\d{4}\b/);
      // Not preceded by a digit or colon: in an ISO value ("…T07:30:00") a
      // \b match would land on "30:00".
      const timeMatch = normalized.match(
        /(?:^|[^\d:])(\d{1,2}:\d{2}(?:\s*\([^)]+\))?)/i,
      );
      if (dateMatch || timeMatch) {
        return {
          date: dateMatch ? dateMatch[0] : "",
          time: timeMatch ? timeMatch[1] : "",
        };
      }

      const parsedDate = new Date(normalized);
      if (!Number.isNaN(parsedDate.getTime())) {
        const date = parsedDate.toLocaleDateString("en-GB");
        const time = parsedDate.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        return { date, time };
      }

      return { date: "", time: normalized };
    };

    const getAppointmentShiftSuffix = (dateTimeValue, timeStr = "") => {
      let hours = null;
      const timeSource = timeStr || dateTimeValue || "";
      const timeMatch = String(timeSource).match(/(?:^|\s)(\d{1,2}):(\d{2})/);

      if (timeMatch) {
        hours = parseInt(timeMatch[1], 10);
      } else if (dateTimeValue) {
        const parsed = new Date(dateTimeValue);
        if (!Number.isNaN(parsed.getTime())) {
          hours = parsed.getHours();
        }
      }

      if (hours === null || Number.isNaN(hours)) {
        return "";
      }

      const lang = (HM_Variables?.language || "vi").toLowerCase();
      const messages = HM_Variables?.frontend_lang?.booking_messages || {};
      const isMorning = hours <= 12;
      const label = isMorning ? messages.morning : messages.afternoon;

      if (label) {
        return `(${lang === "en" ? label.toLowerCase() : label.toLowerCase()})`;
      }

      if (lang === "en") {
        return isMorning ? "(morning)" : "(afternoon)";
      }

      return isMorning ? "(sáng)" : "(chiều)";
    };

    const formatAppointmentTimeWithShift = (timeStr, dateTimeValue) => {
      const baseTime = String(timeStr || "")
        .replace(/\s*\([^)]+\)\s*$/i, "")
        .trim();
      if (!baseTime) {
        return "";
      }

      const suffix = getAppointmentShiftSuffix(dateTimeValue, baseTime);
      return suffix ? `${baseTime} ${suffix}` : baseTime;
    };

    if (!$title.data("default-text")) {
      $title.data("default-text", $title.text());
    }
    if (!$desc.data("default-text")) {
      $desc.data("default-text", $desc.text());
    }

    const fallbackErrorMessage =
      "The system has encountered an issue, please try again later.";

    if (isError) {
      $modal.addClass("is-error");
      if ($statusIcon.length) {
        $statusIcon.attr(
          "src",
          $statusIcon.data("error-icon") || $statusIcon.attr("src")
        );
      }
      $title.text(
        HM_Variables?.language === "en"
          ? "Booking failed"
          : "Đặt lịch chưa thành công"
      );
      $desc.text(message || fallbackErrorMessage);
    } else {
      $modal.removeClass("is-error");
      if ($statusIcon.length) {
        $statusIcon.attr(
          "src",
          $statusIcon.data("success-icon") || $statusIcon.attr("src")
        );
      }
      $title.text($title.data("default-text"));
      $desc.text($desc.data("default-text"));
    }

    if ($crmContact.length) {
      const $workingHour = $("#thankyou-crm-working-hour");
      if (!$crmContact.data("default-hotline")) {
        $crmContact.data("default-hotline", $hotlineLink.text().trim());
        $crmContact.data("default-working-hour", $workingHour.text().trim());
      }

      // Theme option on by default; when off, prefer the hospital's own values.
      const useHospital = String($crmContact.data("override-hospital")) === "0";
      const hotline =
        (useHospital && String(hospital?.hotline || "").trim()) ||
        $crmContact.data("default-hotline");
      const workingHour =
        (useHospital && String(hospital?.work_time || "").trim()) ||
        $crmContact.data("default-working-hour");

      $hotlineLink
        .attr("href", `tel:${formatPhoneTel(hotline)}`)
        .text(formatPhoneDisplay(hotline));
      $workingHour.text(workingHour);
    }

    // Update email if available
    if (!$crmContact.length && hospital && hospital.email) {
      const email = String(hospital.email).trim();
      if (email.length > 0) {
        $emailLink.attr("href", `mailto:${email}`);
        $emailLink.text(email);
        $emailBlock.show();
      }
    }

    // Update hotline if available
    if (!$crmContact.length && hospital && hospital.hotline) {
      const hotline = String(hospital.hotline).trim();
      if (hotline.length > 0) {
        $hotlineLink.attr("href", `tel:${formatPhoneTel(hotline)}`);
        $hotlineLink.text(formatPhoneDisplay(hotline));
        $hotlineBlock.show();
      }
    }

    resetCrmAppointmentInfo();
    if (usesCrmThankYouModal && !isError) {
      const patient = this.$form.find('input[name="name"]').val().trim();
      const hospitalTitle =
        resolveSelectedOptionLabel("#hospitals-select-list", "booking-hospital") ||
        (hospital && hospital.title ? String(hospital.title).trim() : "");
      const rawBookingTime =
        this.$form.find('input[name="booking-time-render"]').val().trim() ||
        this.$form.find('input[name="booking-time"]').val().trim();
      const rawBookingTimeValue = this.$form
        .find('input[name="booking-time"]')
        .val()
        .trim();
      const renderedParts = parseBookingTimeRender(rawBookingTime);
      const parsedDateTime = parseAppointmentDateTime(rawBookingTime);
      const date = renderedParts.date || parsedDateTime.date;
      // A single follow-up card leaves only the date in the visible field; the
      // time then comes from the ISO value in booking-time.
      const timeBase =
        renderedParts.time ||
        parsedDateTime.time ||
        parseAppointmentDateTime(rawBookingTimeValue).time;
      const timeWithShift = formatAppointmentTimeWithShift(
        timeBase,
        rawBookingTimeValue || rawBookingTime,
      );

      const resolveThankYouSpecialtyLabel = () => {
        if (renderedParts.specialty) {
          return renderedParts.specialty;
        }

        const specialtyId = this.$form
          .find('input[name="booking-specialty"]')
          .val()
          .trim();
        if (!specialtyId) {
          return "";
        }

        if (isNoCrmHospital) {
          if (hospital && Array.isArray(hospital.specialties)) {
            const found = hospital.specialties.find(
              (item) => String(item.id) === String(specialtyId),
            );
            if (found?.title) {
              return String(found.title).trim();
            }
          }

          const $slotOption = $(
            `#slot-specialty-select-list .option[data-value="${specialtyId}"]`,
          ).first();
          if ($slotOption.length) {
            return (
              $slotOption.attr("data-label") || $slotOption.text() || ""
            ).trim();
          }

          return "";
        }

        const lookupIds = [
          specialtyId,
          this.$form.find('input[name="booking-specialty-id"]').val().trim(),
        ].filter(Boolean);

        for (const id of lookupIds) {
          const $crmOption = $(
            `#crm-specialty-select-list .option[data-value="${id}"]`,
          ).first();
          if ($crmOption.length) {
            return (
              $crmOption.attr("data-label") || $crmOption.text() || ""
            ).trim();
          }
        }

        return "";
      };

      const resolveThankYouPackageLabel = () => {
        if (!isNoCrmHospital) {
          return "";
        }

        const packageId = this.$form
          .find('input[name="booking-package"]')
          .val()
          .trim();

        if (packageId === "-2") {
          return (
            HM_Variables?.frontend_lang?.select_placeholders
              ?.examine_by_specialty ||
            (HM_Variables?.language === "en"
              ? "Examination by specialty"
              : "Khám theo chuyên khoa")
          );
        }

        if (packageId && hospital && Array.isArray(hospital.packages)) {
          const found = hospital.packages.find(
            (item) => String(item.id) === String(packageId),
          );
          if (found?.title) {
            return String(found.title).trim();
          }
        }

        if (packageId) {
          const $slotOption = $(
            `#slot-packages-select-list .option[data-value="${packageId}"]`,
          ).first();
          if ($slotOption.length) {
            return $slotOption.text().trim();
          }
        }

        return renderedParts.trailing;
      };

      const specialty = stripFollowUpSelectedSuffix(
        resolveThankYouSpecialtyLabel(),
      );
      const packageLabel = resolveThankYouPackageLabel();
      const doctor = isNoCrmHospital
        ? ""
        : this.$form.find('input[name="booking-doctor-name"]').val().trim();
      const condition = this.$form
        .find('[name="additional-information"]')
        .val()
        .trim();

      const appointmentMap = {
        patient,
        hospital: hospitalTitle,
        specialty,
        package: packageLabel,
        date,
        time: timeWithShift,
        doctor,
        condition,
      };

      Object.entries(appointmentMap).forEach(([key, value]) => {
        if (!value) {
          return;
        }
        $(`#thankyou-crm-${key}`).text(value);
        $(`#thankyou-crm-${key}-row`).addClass("is-visible");
      });

      const hasAnyAppointmentData = Object.values(appointmentMap).some(Boolean);
      if (hasAnyAppointmentData) {
        $("#thankyou-crm-appointment-info").addClass("is-visible");
      }

      if (Array.isArray(appointments) && appointments.length > 0) {
        ["specialty", "package", "time", "doctor"].forEach((field) => {
          $(`#thankyou-crm-${field}-row`).removeClass("is-visible");
        });
        const visitDate = this.formatFollowUpVisitDate(appointments[0].startTime);
        if (visitDate) {
          $("#thankyou-crm-date").text(visitDate);
          $("#thankyou-crm-date-row").addClass("is-visible");
        }
        const labelTemplate = this.followUpText(
          "thankyou_label_appointment",
          this.followUpText("followup_appointment_label", "Appointment %d"),
        );
        const cards = appointments
          .map((item, index) => {
            const info = [
              stripFollowUpSelectedSuffix(item.specialtyName),
              item.doctorName,
              this.formatFollowUpClockTime(item.startTime),
            ]
              .filter(Boolean)
              .join(" - ");
            // One left after a cancel: "Appointment:" without the index.
            const rawLabel = String(labelTemplate)
              .replace("%d", appointments.length > 1 ? String(index + 1) : "")
              .replace(/\s+:/, ":")
              .trim();
            const label = this.escapeFollowUpText(
              rawLabel.endsWith(":") ? rawLabel : `${rawLabel}:`,
            );
            return `<div class="info-row is-visible thankyou-appointment-row"><div class="label">${label}</div><div class="value">${this.escapeFollowUpText(info)}</div></div>`;
          })
          .join("");
        $("#thankyou-crm-appointment-list").html(cards).removeAttr("hidden");
        $("#thankyou-crm-appointment-info").addClass("is-visible");
      }
    }

    $modal.addClass("show-modal");
    $modalDialog.addClass("active");
  }
}

/**
 * Shared Danh Y platform detection.
 * @returns {"ios"|"android"|"other"}
 */
function getDanhyPlatform() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "other";
  }

  const ua = String(navigator.userAgent || "");
  const platform = String(navigator.platform || "");

  // Classic iOS UA, or iPadOS 13+ desktop UA (Macintosh + multi-touch).
  if (/iPad|iPhone|iPod/i.test(ua)) {
    return "ios";
  }
  if (
    (platform === "MacIntel" || /Macintosh/i.test(ua)) &&
    Number(navigator.maxTouchPoints || 0) > 1 &&
    !/Windows NT|CrOS|Linux x86_64|X11/i.test(ua)
  ) {
    return "ios";
  }

  if (/Android/i.test(ua)) {
    return "android";
  }

  return "other";
}

/**
 * Detect phone/tablet for Danh Y app deeplink (desktop/laptop always use QR).
 * Never treat Windows/macOS/Linux/ChromeOS computers as mobile — even with touch.
 */
function isDanhyMobileDevice() {
  const platform = getDanhyPlatform();
  if (platform === "ios" || platform === "android") {
    return true;
  }

  if (typeof navigator === "undefined") {
    return false;
  }

  // Other legacy mobile OS tokens → still treat as mobile.
  return /Windows Phone|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
    String(navigator.userAgent || ""),
  );
}

export {
  pageListener,
  validationRegex,
  getCurrentTimeSlotDescriptor,
  buildHisSpecialtyOptions,
  buildPackageOptions,
  isDateTimeInPast,
  applyUtmData,
  createBookingDateTimeManager,
  createBookingDateTimeManagerCRM,
  bookingApi,
  BOOKING_ACTIONS,
  BookingFormBase,
  getDanhyPlatform,
  isDanhyMobileDevice,
};
